import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell } from "recharts";
import { DEFAULT_CFG, calcDaily, calcItem, subscriptionMonthsForPeriod, computeSubscriptionForPeriod, calcBreakEvenFromRows } from "./lib/calc";
import { effNet } from "./lib/coFund";
import { supabase, __DETECTED_RECOVERY } from "./lib/supabaseClient";
import { loadWorkspacesFromDB, createWorkspaceInDB, deleteWorkspaceFromDB, saveChannelTier, deleteChannelTier, saveFixedCostsToDB, saveWorkspaceRatesToDB, updateChannelRates, setVatOnFees, archiveWorkspaces, getAccountByOwner } from "./data/db";
import { insertDailyLog, deleteDailyLog, updateDailyLog } from "./data/dailyLogs";
import { upsertMenuItem, deleteMenuItem } from "./data/menuItems";
import { getCachedAnalysis, saveAnalysisToCache } from "./data/aiCache";
import { insertContactSubmission } from "./data/contact";
import { getCurrentSession, getCurrentUser } from "./data/auth";
import ConnectPlatforms from "./pages/ConnectPlatforms";
import { track } from "./lib/track";
import { OurStory } from "./pages/OurStory";
import { LegalPage } from "./pages/Legal";
import { Landing } from "./pages/Landing";
import { PRICING, PRICE, getFirstSiteRate, getAdditionalRate, getCurrencySymbol, calcMonthlyTotal, calcAnnualTotal, WS_PRICE } from "./lib/pricing";
import { C } from "./lib/theme";
import { setGlobalCurrency, curSym, fmt, fmtK, pct, pctR, posc, posbg } from "./lib/format";
import { useT, setLang, dir as langDir } from "./lib/i18n";
import { ConfirmDialog, Btn, Inp, Card, StatCard, EmptyState, SectionHdr, ChartTip } from "./components/ui";
import { AdminPage, AdminPageHeader, AdminSectionLabel, AdminCard } from "./components/adminUi";
import { API_BASE, callAnthropic } from "./lib/api";
import { apiErrorText, API_ERROR_UNKNOWN_KEY, API_ERROR_NETWORK_KEY } from "./lib/apiError";
import { openDatePicker, openPickerProps } from "./lib/datePicker";
import { RANGES, filterHistory } from "./lib/ranges";
import { RangeFilter } from "./components/RangeFilter";
import { useTableSortFilter, SortableTh } from "./lib/tableSort";
import { computeAccountStatus, isOverrideActive, isOverrideExpired, isOverrideCapped } from "./lib/accountAccess";
import { membershipContext, membershipChanged } from "./lib/teamContext";
import { MarkdownText } from "./components/MarkdownText";
import { ChannelSettings } from "./components/ChannelSettings";
import { CsvImporter } from "./components/CsvImporter";
import { AdminAnalysis } from "./pages/AdminAnalysis";
import { AdminOverview } from "./pages/AdminOverview";
import { AdminPerformance } from "./pages/AdminPerformance";
import { AdminPriceTool } from "./pages/AdminPriceTool";
import { AdminDailyLog } from "./pages/AdminDailyLog";
import { AdminConfig } from "./pages/AdminConfig";
import { AdminSubscriptions } from "./pages/AdminSubscriptions";
import { AdminFeed, useFeedAdminAccess, feedAdminVisible, visibleSuperAdminNav, feedSetupHandler, leaveFeedScreen, FEED_ICON_PATHS } from "./pages/AdminFeed";
import { useNavigate, useLocation } from "react-router-dom";

// ── Admin tab <-> URL route map (3c) ────────────────────────────────
// Each admin sidebar tab is a real, bookmarkable route (/overview, /insights…).
// "overview" is intentionally omitted — Overview is hidden for now, so /overview
// is no longer a resolvable route (a stale bookmark falls through to the default
// tab instead of flashing the hidden page). The AdminOverview component + its
// render branch are kept; restore the route by re-adding overview:"/overview".
const NAV_TO_PATH = { analysis:"/insights", pricetool:"/price-tool", dailylog:"/daily-log", config:"/configuration", team:"/team", subscriptions:"/subscriptions",
  admin_overview:"/admin/overview", admin_accounts:"/admin/accounts", admin_audit:"/admin/audit", admin_super_admins:"/admin/super-admins",
  admin_feed:"/admin/feed" };
const PATH_TO_NAV = Object.fromEntries(Object.entries(NAV_TO_PATH).map(([k, v]) => [v, k]));

// ── Top-level public/auth screen <-> URL route map (3c) ──────────────
// The authenticated admin section uses NAV_TO_PATH (handled in AdminView);
// these are the logged-out screens. "loading" and "admin" are intentionally
// absent (transient / owned by AdminView).
const VIEW_TO_PATH = { landing:"/", story:"/story", auth:"/login", recover:"/reset-password", privacy:"/privacy", appPrivacy:"/app-privacy", terms:"/terms" };
const PATH_TO_VIEW = Object.fromEntries(Object.entries(VIEW_TO_PATH).map(([k, v]) => [v, k]));

// ── AI cache helpers ────────────────────────────────────────────────
// Deterministic fingerprint of the data being analyzed. If this string is
// the same on two generations, the inputs are identical -> reuse cached AI output.
// IMPORTANT: includes channel-level rates so changing commission/card fee
// invalidates the cache even if no rows were edited.


// Supabase data-access helpers extracted to ./data/db.js (loadWorkspacesFromDB, createWorkspaceInDB, deleteWorkspaceFromDB, saveChannelTier, deleteChannelTier, saveFixedCostsToDB, saveWorkspaceRatesToDB)

// P&L formula core (DEFAULT_CFG, calcDaily, calcItem, subscription + break-even helpers) extracted to ./lib/calc.js

// ═══════════════════════════════════════════════════════════════════════
// HISTORY GENERATOR — unique per workspace (different seed)
// ═══════════════════════════════════════════════════════════════════════
function genHistory(seed = 0) {
  const MO = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const rows = []; let id = seed * 1000;
  const baseVolume = 7000 + seed * 2500; // Sheffield (1) earns less than Rotherham (0)
  for (let y = 2021; y <= 2026; y++) {
    const mMax = y === 2026 ? 4 : 12;
    for (let m = 0; m < mMax; m++) {
      const age    = (y - 2021) * 12 + m;
      const growth = 1 + age * (0.012 + seed * 0.003);
      const season = 1 + 0.18 * Math.sin((m / 12) * 2 * Math.PI - 1);
      const base   = baseVolume * growth * season;
      const rnd    = (lo, hi) => lo + (hi - lo) * (Math.sin((id + seed * 99) * 17 + m * 7 + y * 3) * 0.5 + 0.5);
      const gross  = Math.round(base * rnd(0.82, 1.2));
      const net    = Math.round(gross * rnd(0.60, 0.75));
      const cpc    = Math.round(gross * rnd(0.004, 0.012));
      rows.push({ id: id++, gross, net, cpc, dateObj: new Date(y, m, 1),
        label: `${MO[m]} ${y}`, shortLabel: `${MO[m].slice(0,3)} '${String(y).slice(2)}` });
    }
  }
  return rows;
}


// ═══════════════════════════════════════════════════════════════════════
// WORKSPACE DATA — each workspace is fully isolated
// Own menu items, own log history, own aggregator (Deliveroo / Uber etc)
// ═══════════════════════════════════════════════════════════════════════
const WORKSPACE_SEED = {
  ws1: {
    id: "ws1", name: "Burger Bite — Rotherham", platform: "Deliveroo",
    color: "#00CCBC", status: "active", since: "Jan 2025", nextBill: "14/05/2026",
    fixedCosts: { rent:2000, wages:3500, utilities:300, other:200 },
    history: genHistory(0),
    menu: [
      { id:101, name:"Chicken Wrap",   cost:5,     price:50,     disc:0.50, fee:5  },
      { id:102, name:"Chicken Burger", cost:15,    price:70,     disc:0.50, fee:10 },
      { id:103, name:"Burger",         cost:9,     price:15,     disc:0.20, fee:5  },
      { id:104, name:"French Fries",   cost:2,     price:10,     disc:0.20, fee:0  },
      { id:105, name:"Chick a Boo",    cost:8,     price:35,     disc:0.30, fee:5  },
      { id:106, name:"Chicken",        cost:5,     price:9,      disc:0.50, fee:0  },
      { id:107, name:"Strawberry",     cost:29000, price:100000, disc:0.30, fee:0  },
    ],
  },
  ws2: {
    id: "ws2", name: "Burger Bite — Sheffield", platform: "Uber Eats",
    color: "#06C167", status: "active", since: "Mar 2025", nextBill: "14/05/2026",
    fixedCosts: { rent:1800, wages:3000, utilities:250, other:150 },
    history: genHistory(1),
    menu: [
      { id:201, name:"Classic Burger", cost:8,  price:14,  disc:0.20, fee:5  },
      { id:202, name:"Loaded Fries",   cost:2,  price:8,   disc:0.30, fee:0  },
      { id:203, name:"BBQ Wings",      cost:6,  price:12,  disc:0.30, fee:5  },
      { id:204, name:"Milkshake",      cost:3,  price:7,   disc:0.20, fee:0  },
      { id:205, name:"Onion Rings",    cost:1,  price:5,   disc:0.50, fee:0  },
    ],
  },
};

const DEFAULT_TIERS = [
  { id:"t1", label:"50% — High Visibility", discount:0.50, fee:0  },
  { id:"t2", label:"40% — Boosted",         discount:0.40, fee:5  },
  { id:"t3", label:"30% — Standard",        discount:0.30, fee:10 },
  { id:"t4", label:"20% — Lite Boost",      discount:0.20, fee:15 },
  { id:"t5", label:"No Promotion",          discount:0,    fee:0  },
];



// Bucket already-calc'd rows (with dateObj, gross, trueProfit, netReceivable, vat)
// into DAY buckets when the span is under ~32 days, otherwise MONTH buckets.
// Produces chart-ready data with a readable date label on each point, so the
// profit bar charts show "true profit per day" for short ranges and "per month"
// for longer ones, with proper readable dates on the x-axis.


// ═══════════════════════════════════════════════════════════════════════
// WORKSPACE SELECTOR BADGE — used in mobile header
// ═══════════════════════════════════════════════════════════════════════
function WsBadge({ws, onClick}) {
  return (
    <button onClick={onClick} style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",borderRadius:6,padding:"4px 10px",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>
      <div style={{width:6,height:6,borderRadius:3,background:ws.color}}/>
      <span style={{color:"#fff",fontSize:11,fontWeight:500}}>{ws.platform}</span>
      <span style={{color:"rgba(255,255,255,.6)",fontSize:10}}>▾</span>
    </button>
  );
}

// Workspace picker modal (mobile)
function WsPicker({workspaces, current, onSelect}) {
  const { t } = useT();
  return (
    <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.6)",zIndex:50,display:"flex",alignItems:"flex-end"}}>
      <div style={{background:"#fff",borderRadius:"16px 16px 0 0",width:"100%",padding:"16px"}}>
        <div style={{width:40,height:4,background:C.border,borderRadius:2,margin:"0 auto 16px"}}/>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:14}}>{t("app.mob.switchWorkspace")}</div>
        {Object.values(workspaces).map(ws => (
          <button key={ws.id} onClick={() => onSelect(ws.id)}
            style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"12px 14px",borderRadius:8,border:`1.5px solid ${current===ws.id?C.blue:C.border}`,background:current===ws.id?C.blue10:"#fff",cursor:"pointer",marginBottom:8,fontFamily:"inherit",boxShadow:C.s1}}>
            <div style={{width:10,height:10,borderRadius:5,background:ws.color,flexShrink:0}}/>
            <div style={{textAlign:"start",flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:current===ws.id?C.blue:C.text}}>{ws.name}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>{ws.platform} · {ws.menu.length} {t("app.mob.itemsSuffix")}</div>
            </div>
            {current===ws.id && <span style={{color:C.blue,fontSize:14}}>✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MOBILE: DASHBOARD
// ═══════════════════════════════════════════════════════════════════════
function MobDash({ws, cfg}) {
  const { t: tt } = useT();
  const [range, setRange] = useState("ALL");
  const raw  = useMemo(() => filterHistory(ws.history, range), [ws, range]);
  const rows = useMemo(() => raw.map(r => ({...r,...calcDaily(r.gross,effNet(r),r.cpc,{aggCommission:r.commission ?? cfg.aggCommission,cardFee:r.cardFeePct ?? cfg.cardFee,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true})})), [raw,cfg]);
  const t    = useMemo(() => rows.reduce((a,r) => ({gross:a.gross+r.gross,net:a.net+r.netReceivable,profit:a.profit+r.trueProfit,vat:a.vat+r.vat}),{gross:0,net:0,profit:0,vat:0}), [rows]);
  const avgP = t.profit/(rows.length||1);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:12,fontWeight:500,color:C.muted}}>{tt("app.mob.performance")} · {ws.platform}</span>
        <div style={{display:"flex",background:"#fff",border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden",boxShadow:C.s1}}>
          {RANGES.map(r => <button key={r.id} onClick={()=>setRange(r.id)} style={{padding:"4px 7px",border:"none",background:range===r.id?C.blue:"transparent",color:range===r.id?"#fff":C.muted,fontSize:9,fontWeight:500,cursor:"pointer",fontFamily:"inherit",borderInlineEnd:`1px solid ${C.border2}`}}>{r.l}</button>)}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <StatCard label={tt("app.mob.grossRevenue")}  value={fmtK(t.gross)}  accent={C.blue} color={C.text}/>
        <StatCard label={tt("app.mob.netReceivable")} value={fmtK(t.net)}    accent={C.blue} color={C.blue}/>
        <StatCard label={tt("app.mob.vatPaid")}       value={fmtK(t.vat)}    accent={C.red}  color={C.red}/>
        <StatCard label={tt("app.mob.trueProfit")}    value={fmtK(t.profit)} accent={posc(t.profit)} color={posc(t.profit)}/>
      </div>
      <div style={{background:posbg(avgP),border:`1px solid ${posc(avgP)}33`,borderRadius:8,padding:"9px 13px",display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:C.s1}}>
        <span style={{fontSize:11,color:C.muted,fontWeight:500}}>{tt("app.mob.avgMonthlyProfit")}</span>
        <span style={{fontSize:14,fontWeight:700,color:posc(avgP)}}>{fmt(avgP)}</span>
      </div>
      <Card sx={{padding:"13px 8px 4px"}}>
        <div style={{fontSize:10,fontWeight:500,color:C.muted,marginBottom:8,paddingInlineStart:8}}>{tt("app.mob.trueProfitPerMonth")}</div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={rows.map(r=>({label:r.shortLabel,trueProfit:r.trueProfit}))} margin={{top:4,right:4,left:-12,bottom:0}} barSize={rows.length>24?4:7}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border2} vertical={false}/>
            <XAxis dataKey="label" tick={{fontSize:7,fill:C.muted2}} tickLine={false} axisLine={{stroke:C.border}} interval={rows.length>24?5:2}/>
            <YAxis tick={{fontSize:8,fill:C.muted2}} tickLine={false} axisLine={false} tickFormatter={v=>v>=1000?`£${(v/1000).toFixed(0)}k`:`£${v}`}/>
            <Tooltip content={<ChartTip/>}/>
            <ReferenceLine y={0} stroke={C.border}/>
            <Bar dataKey="trueProfit" name={tt("app.mob.trueProfit")} fill={C.blue} radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <div style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>{tt("app.mob.recentMonths")}</div>
        {rows.slice(0,5).map((r,i) => (
          <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:i<4?`1px solid ${C.border2}`:"none"}}>
            <div>
              <div style={{fontSize:12,fontWeight:500,color:C.text}}>{r.label}</div>
              <div style={{fontSize:10,color:C.muted,marginTop:1}}>{fmt(r.gross)} {tt("app.mob.grossSuffix")}</div>
            </div>
            <span style={{fontSize:12,fontWeight:600,color:posc(r.trueProfit),background:posbg(r.trueProfit),padding:"2px 7px",borderRadius:4}}>{fmt(r.trueProfit)}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MOBILE: ITEMS
// ═══════════════════════════════════════════════════════════════════════
function MobItems({ws, setWs, tiers, cfg}) {
  const { t: tt } = useT();
  const menu = ws.menu;
  const setMenu = fn => setWs(p => ({...p, menu: fn(p.menu)}));
  const [sel, setSel]     = useState(menu[0]?.id);
  const [adding,setAdding]= useState(false);
  const [form, setForm]   = useState({name:"",cost:0,price:0,disc:0.50,fee:0});
  const item = menu.find(m => m.id===sel);
  const res  = item ? calcItem(item.cost,item.price,item.disc,item.fee,{...cfg,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true}, ws?.cardFeePct ?? 0) : null;
  const upd  = (f,v) => setMenu(p => p.map(m => m.id===sel ? {...m,[f]:v} : m));
  function pickDisc(d) { const t=tiers.find(t=>t.discount===d); upd("disc",d); if(t)upd("fee",t.fee); }
  function addItem() { if(!form.name)return; setMenu(p=>[...p,{...form,id:Date.now()}]); setAdding(false); setForm({name:"",cost:0,price:0,disc:0.50,fee:0}); }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Platform badge */}
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",background:C.bg2,borderRadius:8,border:`1px solid ${C.border}`,boxShadow:C.s1}}>
        <div style={{width:8,height:8,borderRadius:4,background:ws.color}}/>
        <span style={{fontSize:12,fontWeight:500,color:C.text}}>{ws.name}</span>
        <span style={{fontSize:11,color:C.muted,marginInlineStart:"auto"}}>{ws.platform}</span>
      </div>

      {/* Pills */}
      <div style={{display:"flex",gap:5,overflowX:"auto",paddingBottom:4}}>
        {menu.map(m => {
          const r=calcItem(m.cost,m.price,m.disc,m.fee,{...cfg,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true}, ws?.cardFeePct ?? 0);
          return (
            <button key={m.id} onClick={()=>setSel(m.id)}
              style={{flexShrink:0,padding:"6px 12px",borderRadius:20,border:`1.5px solid ${sel===m.id?C.blue:C.border}`,background:sel===m.id?C.blue10:"#fff",cursor:"pointer",fontSize:11,fontWeight:500,color:sel===m.id?C.blue:C.sub,boxShadow:C.s1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
              <span>{m.name.split(" ")[0]}</span>
              <div style={{width:5,height:5,borderRadius:3,background:posc(r.profit)}}/>
            </button>
          );
        })}
        <button onClick={()=>setAdding(true)} style={{flexShrink:0,padding:"6px 12px",borderRadius:20,border:`1px dashed ${C.border}`,background:"transparent",cursor:"pointer",fontSize:11,color:C.muted}}>{tt("app.mob.add")}</button>
      </div>

      {adding && (
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{tt("app.mob.newItem")} — {ws.name}</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <Inp label={tt("app.mob.itemName")} value={form.name} onChange={v=>setForm(p=>({...p,name:v}))} placeholder={tt("app.mob.itemNamePlaceholder")}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp label={tt("app.mob.yourCost")} value={form.cost} onChange={v=>setForm(p=>({...p,cost:v}))} type="number" prefix="£"/>
              <Inp label={tt("app.mob.listingPrice")} value={form.price} onChange={v=>setForm(p=>({...p,price:v}))} type="number" prefix="£"/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:10,fontWeight:500,color:C.muted,textTransform:"uppercase",letterSpacing:.8}}>{tt("app.mob.discountTier")}</label>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {tiers.map(t => <button key={t.id} onClick={()=>setForm(p=>({...p,disc:t.discount,fee:t.fee}))} style={{padding:"5px 10px",borderRadius:6,border:`1.5px solid ${form.disc===t.discount?C.blue:C.border}`,background:form.disc===t.discount?C.blue10:"#fff",cursor:"pointer",fontSize:11,fontWeight:500,color:form.disc===t.discount?C.blue:C.muted,fontFamily:"inherit"}}>{t.discount===0?tt("app.mob.none"):`${t.discount*100}%`}</button>)}
              </div>
            </div>
            <Inp label={tt("app.mob.campaignFee")} value={form.fee} onChange={v=>setForm(p=>({...p,fee:v}))} type="number" prefix="£" hint={tt("app.mob.campaignFeeHint")}/>
            <div style={{display:"flex",gap:8,marginTop:4}}><Btn full onClick={addItem}>{tt("btn.saveItem")}</Btn><Btn v="secondary" full onClick={()=>setAdding(false)}>{tt("btn.cancel")}</Btn></div>
          </div>
        </Card>
      )}

      {item && res && (
        <>
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{item.name}</div>
              <button onClick={()=>setMenu(p=>p.filter(m=>m.id!==item.id))} style={{fontSize:10,color:C.red,background:C.red10,border:"none",borderRadius:4,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit"}}>{tt("app.mob.remove")}</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <Inp label={tt("app.mob.cost")} value={item.cost} onChange={v=>upd("cost",v)} type="number" prefix="£"/>
              <Inp label={tt("app.mob.listingPrice")} value={item.price} onChange={v=>upd("price",v)} type="number" prefix="£"/>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
              {tiers.map(t => <button key={t.id} onClick={()=>pickDisc(t.discount)} style={{padding:"5px 10px",borderRadius:6,border:`1.5px solid ${item.disc===t.discount?C.blue:C.border}`,background:item.disc===t.discount?C.blue10:"#fff",cursor:"pointer",fontSize:11,fontWeight:500,color:item.disc===t.discount?C.blue:C.muted,fontFamily:"inherit"}}>{t.discount===0?tt("app.mob.none"):`${t.discount*100}%`}</button>)}
            </div>
            <Inp label={tt("app.mob.campaignFee")} value={item.fee} onChange={v=>upd("fee",v)} type="number" prefix="£"/>
          </Card>

          <Card sx={{background:C.bg2,padding:0,overflow:"hidden"}}>
            <div style={{padding:"10px 15px",borderBottom:`1px solid ${C.border2}`,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:12,fontWeight:600,color:C.text}}>{tt("app.mob.fullBreakdown")}</span>
              <span style={{fontSize:10,color:C.muted}}>{tt("app.mob.allFormulasVerified")}</span>
            </div>
            {[
              {l:tt("app.mob.sellingPrice"),           v:fmt(item.price),                                    c:C.sub},
              {l:`${tt("app.mob.discount")} (${(item.disc*100).toFixed(0)}%)`,v:`−${fmt(item.price*item.disc)}`, c:C.muted},
              {l:tt("app.mob.campaignDeliveryFee"), v:`−${fmt(item.fee)}`,                               c:C.muted},
              {l:tt("app.mob.netTicketTotal"),        v:fmt(res.netTicket),                                 c:C.blue, bold:true},
              {l:`${tt("app.mob.aggregatorAt")} ${(cfg.aggCommission*100).toFixed(0)}%`, v:`−${fmt(res.commission)}`, c:C.red},
              {l:tt("app.mob.netReceivableFromAgg"), v:fmt(res.netReceivable),                      c:C.blue, bold:true},
              {l:`${tt("app.mob.vatAt")} ${(cfg.vatRate*100).toFixed(0)}%`, v:`−${fmt(res.vat)}`,                 c:C.red},
            ].map(({l,v,c,bold},i) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 15px",borderBottom:`1px solid ${C.border2}`,background:bold?"#fff":C.bg2}}>
                <span style={{fontSize:11,color:C.muted,fontWeight:bold?500:400}}>{l}</span>
                <span style={{fontSize:bold?14:12,fontWeight:bold?700:500,color:c,fontVariantNumeric:"tabular-nums"}}>{v}</span>
              </div>
            ))}
            <div style={{padding:"12px 15px",background:posbg(res.profit),borderTop:`2px solid ${posc(res.profit)}33`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:posc(res.profit)}}>{res.profit>=0?tt("app.mob.profitPerSale"):tt("app.mob.lossPerSale")}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:1}}>{tt("app.mob.afterCostPrefix")}{item.cost}{tt("app.mob.afterCostSuffix")}</div>
                </div>
                <div style={{fontSize:22,fontWeight:800,color:posc(res.profit),fontVariantNumeric:"tabular-nums"}}>{fmt(res.profit)}</div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Compact summary table — no overflow */}
      <Card sx={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"10px 14px",borderBottom:`1px solid ${C.border2}`}}>
          <span style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.8}}>{tt("app.mob.allItems")}</span>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,tableLayout:"auto"}}>
            <thead>
              <tr style={{background:C.bg3}}>
                {[tt("app.mob.colItem"),tt("app.mob.colCost"),tt("app.mob.colPrice"),tt("app.mob.colDisc"),tt("app.mob.colFee"),tt("app.mob.colRecv"),tt("app.mob.colPL")].map(h => (
                  <th key={h} style={{padding:"6px 10px",textAlign:"start",color:C.muted,fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:.5,borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {menu.map((m,i) => {
                const r=calcItem(m.cost,m.price,m.disc,m.fee,{...cfg,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true}, ws?.cardFeePct ?? 0);
                return (
                  <tr key={m.id} onClick={()=>setSel(m.id)} style={{background:sel===m.id?C.blue10:i%2===0?"#fff":C.bg2,cursor:"pointer"}}>
                    <td style={{padding:"6px 10px",color:sel===m.id?C.blue:C.text,fontWeight:sel===m.id?600:500,whiteSpace:"nowrap",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis"}}>{m.name}</td>
                    <td style={{padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontSize:11,whiteSpace:"nowrap"}}>{fmt(m.cost)}</td>
                    <td style={{padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontSize:11,whiteSpace:"nowrap"}}>{fmt(m.price)}</td>
                    <td style={{padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontSize:11,color:C.blue,fontWeight:500}}>{(m.disc*100).toFixed(0)}%</td>
                    <td style={{padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontSize:11}}>{fmt(m.fee)}</td>
                    <td style={{padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontSize:11,color:C.blue,fontWeight:500,whiteSpace:"nowrap"}}>{fmt(r.netReceivable)}</td>
                    <td style={{padding:"6px 10px",whiteSpace:"nowrap"}}>
                      <span style={{fontWeight:600,color:posc(r.profit),background:posbg(r.profit),padding:"2px 5px",borderRadius:3,fontSize:11,fontVariantNumeric:"tabular-nums"}}>{fmt(r.profit)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MOBILE: LOG
// ═══════════════════════════════════════════════════════════════════════
function MobLog({ws, cfg}) {
  const { t } = useT();
  const [exp,setExp]      = useState(null);
  const [adding,setAdding]= useState(false);
  const [extra,setExtra]  = useState([]);
  const [form,setForm]    = useState({date:"14/04/2026",gross:0,net:0,cpc:0});
  const recent = useMemo(() => [...extra,...ws.history.slice(-8)].slice(0,10), [extra,ws]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",background:C.bg2,borderRadius:8,border:`1px solid ${C.border}`,boxShadow:C.s1}}>
        <div style={{width:8,height:8,borderRadius:4,background:ws.color}}/>
        <span style={{fontSize:12,fontWeight:500,color:C.text}}>{ws.platform} {t("app.mob.logSuffix")}</span>
        <span style={{fontSize:11,color:C.muted,marginInlineStart:"auto"}}>{t("app.mob.seeAdminFullHistory")}</span>
      </div>
      <Btn full onClick={()=>setAdding(true)}>{t("app.mob.logTodaysSales")}</Btn>
      {adding && (
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14,letterSpacing:"-.2px"}}>{t("app.mob.newEntry")}</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <label style={{fontSize:10.5,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:6}}>{t("app.mob.date")}</label>
              <input value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} onClick={openDatePicker} onFocus={openDatePicker} type="date" max={new Date().toISOString().split("T")[0]}
                style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13.5,fontFamily:"inherit",boxSizing:"border-box",background:"#fff",fontWeight:500,color:C.text}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp label={t("app.mob.gross")} value={form.gross} onChange={v=>setForm(p=>({...p,gross:v}))} type="number" prefix="£"/>
              <Inp label={t("app.mob.netOnTicket")} value={form.net} onChange={v=>setForm(p=>({...p,net:v}))} type="number" prefix="£"/>
            </div>
            <Inp label={t("app.mob.cpcSpend")} value={form.cpc} onChange={v=>setForm(p=>({...p,cpc:v}))} type="number" prefix="£"/>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <Btn full onClick={()=>{setExtra(p=>[{...form,id:Date.now(),dateObj:new Date(),label:form.date,shortLabel:form.date},...p]);setAdding(false);}}>{t("btn.save")}</Btn>
              <Btn v="secondary" full onClick={()=>setAdding(false)}>{t("btn.cancel")}</Btn>
            </div>
          </div>
        </Card>
      )}
      {recent.map((r,i) => {
        const c=calcDaily(r.gross,effNet(r),r.cpc,{aggCommission:r.commission ?? cfg.aggCommission,cardFee:r.cardFeePct ?? cfg.cardFee,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true});
        return (
          <Card key={r.id} sx={{padding:0,overflow:"hidden"}}>
            <div onClick={()=>setExp(exp===r.id?null:r.id)} style={{padding:"11px 15px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
              <div>
                <div style={{fontSize:12,fontWeight:500,color:C.text}}>{r.label}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:1}}>{t("app.mob.gross")} {fmt(r.gross)} · CPC {fmt(r.cpc)}</div>
              </div>
              <div style={{textAlign:"end",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                <span style={{fontSize:12,fontWeight:600,color:posc(c.trueProfit),background:posbg(c.trueProfit),padding:"2px 7px",borderRadius:4}}>{fmt(c.trueProfit)}</span>
                <span style={{fontSize:9,color:C.muted}}>{exp===r.id?"▲":"▼"}</span>
              </div>
            </div>
            {exp===r.id && (
              <div style={{padding:"10px 15px 13px",background:C.bg2,borderTop:`1px solid ${C.border2}`}}>
                {[
                  [t("app.mob.netAfterDiscComm"), fmt(c.netAfterDiscComm)],
                  [t("app.mob.cardFees"),             fmt(c.cardFees)],
                  [t("app.mob.netReceivable"),        fmt(c.netReceivable)],
                  [`${t("app.mob.vat")} (${(cfg.vatRate*100).toFixed(0)}%)`, fmt(c.vat)],
                  [t("app.mob.earningAfterVat"),     fmt(c.earningAfterVat)],
                  [`${t("app.mob.cogs")} (${(cfg.cogsPct*100).toFixed(0)}% ${t("app.mob.cogsGrossSuffix")})`, fmt(c.cogs)],
                  [t("app.mob.pctEarningOnGross"),    pct(c.pctEarningOnGross)],
                  [t("app.mob.trueProfit"),           fmt(c.trueProfit)],
                  [t("app.mob.netProfitPct"),          pct(c.netProfitPct)],
                  [t("app.mob.cogsPctOnNet"),         pct(c.cogsPctOnNet)],
                ].map(([l,v],i,a) => (
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:i<a.length-1?`1px solid ${C.border2}`:"none"}}>
                    <span style={{fontSize:11,color:C.muted}}>{l}</span>
                    <span style={{fontSize:11,fontWeight:500,color:C.text,fontVariantNumeric:"tabular-nums"}}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MOBILE: ACCOUNT
// ═══════════════════════════════════════════════════════════════════════
function MobAccount({workspaces, setWorkspaces}) {
  const { t } = useT();
  const [adding,setAdding]=useState(false);
  const [newName,setNewName]=useState("");
  const [platform,setPlatform]=useState("Deliveroo");
  const [step,setStep]=useState(1);
  const active = Object.values(workspaces).filter(w=>w.status==="active");
  const total  = active.length * WS_PRICE;

  function confirm() {
    const id = "ws"+Date.now();
    setWorkspaces(p=>({...p,[id]:{id,name:newName,platform,color:"#1a73e8",status:"active",since:"Apr 2026",nextBill:"14/05/2026",history:genHistory(Object.keys(p).length),menu:[]}}));
    setAdding(false); setStep(1); setNewName(""); setPlatform("Deliveroo");
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{background:C.blue,borderRadius:10,padding:"15px 17px",boxShadow:C.s3}}>
        <div style={{fontSize:9,color:"rgba(255,255,255,.7)",fontWeight:500,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{t("app.mob.totalMonthlyBilling")}</div>
        <div style={{fontSize:28,fontWeight:700,color:"#fff",letterSpacing:"-.5px"}}>{fmt(total)}<span style={{fontSize:13,fontWeight:400,opacity:.7}}>{t("app.mob.perMonth")}</span></div>
        <div style={{fontSize:11,color:"rgba(255,255,255,.7)",marginTop:3}}>{active.length} workspace{active.length!==1?"s":""} × £{WS_PRICE}/mo each</div>
      </div>

      {Object.values(workspaces).map((ws,i) => (
        <Card key={ws.id} sx={{padding:0,overflow:"hidden"}}>
          <div style={{padding:"13px 15px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:10,height:10,borderRadius:5,background:ws.color,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.text}}>{ws.name}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:1}}>{ws.platform} · {t("app.mob.since")} {ws.since}</div>
                </div>
              </div>
              <span style={{padding:"3px 8px",borderRadius:4,fontSize:10,fontWeight:500,background:ws.status==="active"?C.green10:C.red10,color:ws.status==="active"?C.green:C.red}}>{ws.status}</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div style={{background:C.bg2,borderRadius:6,padding:"7px 10px"}}><div style={{fontSize:9,color:C.muted2,textTransform:"uppercase",letterSpacing:.8,marginBottom:2}}>{t("app.mob.monthlyCharge")}</div><div style={{fontSize:13,fontWeight:700,color:C.text}}>{fmt(WS_PRICE)}</div></div>
              <div style={{background:C.bg2,borderRadius:6,padding:"7px 10px"}}><div style={{fontSize:9,color:C.muted2,textTransform:"uppercase",letterSpacing:.8,marginBottom:2}}>{t("app.mob.nextBilling")}</div><div style={{fontSize:12,fontWeight:500,color:C.text}}>{ws.nextBill}</div></div>
            </div>
          </div>
          <div style={{borderTop:`1px solid ${C.border2}`,padding:"7px 15px",display:"flex",gap:8,background:C.bg2}}>
            <Btn v="ghost" sx={{fontSize:11,padding:"4px 10px"}}>{t("app.mob.manageBilling")}</Btn>
            {i>0&&<Btn v="danger" sx={{fontSize:11,padding:"4px 10px"}} onClick={()=>setWorkspaces(p=>{const n={...p};delete n[ws.id];return n;})}>{t("btn.cancel")}</Btn>}
          </div>
        </Card>
      ))}

      {!adding ? (
        <div style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"15px",textAlign:"center",background:C.bg2}}>
          <div style={{fontSize:13,fontWeight:500,color:C.text,marginBottom:4}}>{t("app.mob.addAnotherLocation")}</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:12}}>£{WS_PRICE}{t("app.mob.ownMenuAgg")}</div>
          <Btn onClick={()=>setAdding(true)}>{t("app.mob.addWorkspace")}</Btn>
        </div>
      ):(
        <Card sx={{border:`2px solid ${C.blue}`,boxShadow:C.s3}}>
          {step===1&&(
            <>
              <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>{t("app.mob.newWorkspace")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Inp label={t("app.mob.locationName")} value={newName} onChange={setNewName} placeholder={t("app.mob.locationNamePlaceholder")}/>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:10,fontWeight:500,color:C.muted,textTransform:"uppercase",letterSpacing:.8}}>{t("app.mob.aggregatorPlatform")}</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["Deliveroo","Uber Eats","JustEat","Multiple"].map(p=>(
                      <button key={p} onClick={()=>setPlatform(p)} style={{padding:"5px 12px",borderRadius:6,border:`1.5px solid ${platform===p?C.blue:C.border}`,background:platform===p?C.blue10:"#fff",cursor:"pointer",fontSize:11,fontWeight:500,color:platform===p?C.blue:C.muted,fontFamily:"inherit"}}>{p}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:14}}>
                <Btn full onClick={()=>newName&&setStep(2)}>{t("btn.continue")}</Btn>
                <Btn v="secondary" full onClick={()=>{setAdding(false);setStep(1);}}>{t("btn.cancel")}</Btn>
              </div>
            </>
          )}
          {step===2&&(
            <>
              <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>{t("app.mob.confirmSubscription")}</div>
              <div style={{background:C.bg2,borderRadius:8,padding:"13px",marginBottom:12,border:`1px solid ${C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{fontSize:12,color:C.muted}}>{t("app.mob.newWorkspace")}</span><span style={{fontSize:12,fontWeight:500,color:C.text}}>{newName}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{fontSize:12,color:C.muted}}>{t("app.mob.platform")}</span><span style={{fontSize:12,fontWeight:500}}>{platform}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}><span style={{fontSize:12,color:C.muted}}>{t("app.mob.addedCharge")}</span><span style={{fontSize:13,fontWeight:700,color:C.blue}}>+£{WS_PRICE}{t("app.mob.perMonth")}</span></div>
                <div style={{borderTop:`1px solid ${C.border}`,paddingTop:8,display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,fontWeight:600}}>{t("app.mob.newTotal")}</span><span style={{fontSize:14,fontWeight:700,color:C.text}}>{fmt(total+WS_PRICE)}{t("app.mob.perMonth")}</span></div>
              </div>
              <div style={{background:C.blue10,borderRadius:6,padding:"9px 12px",marginBottom:12,fontSize:11,color:C.blue}}>{t("app.mob.redirectingStripe")}</div>
              <div style={{display:"flex",gap:8}}>
                <Btn full onClick={confirm}>{t("btn.confirmPay")}</Btn>
                <Btn v="secondary" full onClick={()=>setStep(1)}>{t("app.mob.back")}</Btn>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ADMIN PORTAL
// ═══════════════════════════════════════════════════════════════════════
// AUTH + PLAN CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

// Super admin status is determined by the `super_admins` table in Supabase,
// resolved via the /api/admin/me endpoint after every login. The hardcoded
// fallback below is ONLY used as a tiebreaker for the very first super
// admin (chicken-and-egg: someone needs to be able to access the panel to
// add other super admins via UI). For everyone else, add them through the
// "Super admins" tab — no code redeploy needed.
const SUPER_ADMIN_BOOTSTRAP_EMAILS = ["yaseen.patel2005@hotmail.com"];

// Calls /api/admin/me to find out whether the signed-in user is in the
// super_admins table. Returns true/false. Never throws — returns false on
// any error (offline, server down, etc.) so a flaky backend can't lock
// someone out of their normal user account.
async function checkSuperAdminFromDB(token) {
  if (!token) return false;
  try {
    const res = await fetch(`${API_BASE}/api/admin/me`, {
      headers: { "Authorization": `Bearer ${token}` },
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data?.super_admin === true;
  } catch (_) {
    return false;
  }
}


// WhatsApp dial codes per signup region — prefixed onto the number the user types
const DIAL_CODES = { UK: "+44", UAE: "+971", US: "+1", IN: "+91" };

async function supabaseAuth(mode, email, password, opts = {}) {
  if (mode === "signup") {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          // Stored in auth.users.raw_user_meta_data — read by the trigger
          full_name: opts.name || null,
          region: opts.region || "UK",
          business_type: opts.businessType || "restaurant",
          phone: opts.phone || null,
        },
      },
    });
    if (error) return { error: error.message };
    return data;
  } else if (mode === "reset") {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { error: error.message };
    return data || { msg: "Reset email sent" };
  } else {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return data;
  }
}

const PLANS = {
  free:  { label:"Free",    price:0,     wsLimit:1, features:["1 workspace","Price tool","14-day log","Read-only config"] },
  pro:   { label:"Pro",     price:49.99, wsLimit:99,features:["Unlimited workspaces","Full daily log","CSV import","Break-even dashboard","5yr history","Mobile app"] },
};

// ── Auth screens ─────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// AUTH VISUAL PANEL — branded right panel for sign-in/up screen
// (Not a product slideshow — that's on the landing page now)
// ─────────────────────────────────────────────────────────────────────
function AuthVisualPanel() {
  const { t } = useT();
  return (
    <div style={{position:"relative",width:"100%",height:"100%",background:"linear-gradient(135deg,#1e3a8a 0%,#0f172a 60%,#1e1b4b 100%)",borderRadius:18,overflow:"hidden",display:"flex",flexDirection:"column",padding:"48px 56px",color:"#fff",boxShadow:"0 24px 48px rgba(15,23,42,0.25)"}}>

      {/* Decorative gradients — subtle, single-direction */}
      <div style={{position:"absolute",top:"-10%",right:"-20%",width:500,height:500,background:"radial-gradient(circle,rgba(99,102,241,0.35) 0%,transparent 60%)",borderRadius:"50%",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:"-15%",left:"-15%",width:450,height:450,background:"radial-gradient(circle,rgba(59,130,246,0.30) 0%,transparent 60%)",borderRadius:"50%",pointerEvents:"none"}}/>

      {/* Animated grid pattern — subtle backdrop texture */}
      <svg style={{position:"absolute",inset:0,opacity:0.06,pointerEvents:"none"}} width="100%" height="100%">
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#fff" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)"/>
      </svg>

      {/* Middle — value prop */}
      <div style={{position:"relative",marginTop:"auto",marginBottom:"auto",maxWidth:480}}>
        <h2 style={{fontSize:44,fontWeight:700,lineHeight:1.1,letterSpacing:"-1.6px",margin:"0 0 24px",color:"#fff"}}>
          {t("app.auth.realProfitHeadline")}
        </h2>
        <p style={{fontSize:16,color:"#cbd5e1",lineHeight:1.65,fontWeight:400,margin:"0 0 36px",maxWidth:440}}>
          {t("app.auth.realProfitSub")}
        </p>

        {/* Single visual element — stylized metric chips */}
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[
            {label:t("app.auth.commissionDeducted"),value:"−29.2%",color:"#fca5a5"},
            {label:t("app.auth.vatCalculated"),value:"20.0%",color:"#fde68a"},
            {label:t("app.auth.trueProfitMargin"),value:"39.0%",color:"#86efac"},
          ].map((m)=>(
            <div key={m.label} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,backdropFilter:"blur(10px)",WebkitBackdropFilter:"blur(10px)"}}>
              <span style={{fontSize:13.5,color:"#cbd5e1",fontWeight:500}}>{m.label}</span>
              <span style={{fontSize:15,fontWeight:700,color:m.color,fontVariantNumeric:"tabular-nums",letterSpacing:"-.3px"}}>{m.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom — platform list */}
      <div style={{position:"relative",paddingTop:28,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{fontSize:11,fontWeight:600,color:"#64748b",letterSpacing:1.2,textTransform:"uppercase",marginBottom:12}}>{t("app.auth.supports")}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"6px 12px"}}>
          {["Deliveroo","Uber Eats","Just Eat","Talabat","Careem Food","Noon Food","Keeta","Zomato","Swiggy"].map(p=>(
            <span key={p} style={{fontSize:13,color:"#cbd5e1",fontWeight:500}}>{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}


function AuthScreen({onAuth, onBackToHome, initialMode="login"}) {
  const { t, lang } = useT();
  const [mode,setMode]=useState(initialMode==="signup"?"signup":"login");
  // Keep mode in sync with the intent the caller opened the screen with, so
  // reopening the auth screen from a different CTA (e.g. "Create account" after
  // having visited as "Sign in") shows the right form.
  useEffect(()=>{ setMode(initialMode==="signup"?"signup":"login"); /* eslint-disable-next-line */ },[initialMode]);
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [name,setName]=useState("");
  const [region,setRegion]=useState("UK"); // UK | UAE | US | IN — locks the account's billing currency
  const [businessType,setBusinessType]=useState("restaurant"); // restaurant | cloud_kitchen | pharmacy | supermarket | retail — retail switches the workspace wizard to marketplace platforms
  const [phone,setPhone]=useState(""); // local part only — DIAL_CODES[region] is prefixed at submit
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [keepSignedIn,setKeepSignedIn]=useState(true);

  async function submit(){
    // RESET PASSWORD — only needs email, no password required
    if(mode==="reset"){
      if(!email){setErr("Please enter your email address");return;}
      setLoading(true);setErr("");
      try{
        const d = await supabaseAuth("reset", email, "");
        if(d.error){
          const msg = (d.error||"").toString().toLowerCase();
          if(msg.includes("rate") || msg.includes("limit")){
            setErr("Too many attempts. Please wait a moment and try again.");
          } else if(msg.includes("not found") || msg.includes("invalid")){
            // Don't reveal whether email exists — pretend success
            setMode("reset_sent");
          } else {
            setErr(d.error);
          }
        } else {
          // Always show success regardless (anti-enumeration)
          setMode("reset_sent");
        }
      }catch{
        setErr("Connection error — please try again");
      }
      setLoading(false);
      return;
    }
    if(!email||!pass){setErr("Email and password required");return;}
    if(pass.length<8){setErr("Password must be at least 8 characters");return;}
    let fullPhone = null;
    if(mode==="signup"){
      const digits = phone.replace(/\D/g,"").replace(/^0+/,"");
      if(digits.length<7||digits.length>13){setErr("Please enter a valid phone number (without the country code)");return;}
      fullPhone = (DIAL_CODES[region]||"") + digits;
    }
    setLoading(true);setErr("");
    try{
      const d = await supabaseAuth(mode, email, pass, mode === "signup" ? { name, region, businessType, phone: fullPhone } : {});
      if(d.error||d.msg){
        const msg = (d.error||d.msg||"").toString().toLowerCase();
        if(msg.includes("already") || msg.includes("registered") || msg.includes("exist")){
          setErr("An account with this email already exists. Try signing in instead.");
        } else if(msg.includes("invalid login") || msg.includes("invalid credentials")){
          setErr("Incorrect email or password.");
        } else if(msg.includes("rate") || msg.includes("limit")){
          setErr("Too many attempts. Please wait a moment and try again.");
        } else if(msg.includes("email not confirmed") || msg.includes("not confirmed")){
          setErr("Please verify your email first. Check your inbox for the confirmation link.");
        } else {
          setErr(d.error||d.msg||"Authentication failed");
        }
        setLoading(false);return;
      }
      // SIGNUP: handle duplicate-email detection robustly
      // Supabase returns different shapes depending on version/config:
      //   - data.user.identities = [] → email exists (newer Supabase)
      //   - data.user.email_confirmed_at = "<timestamp>" → email already confirmed (re-signup attempt)
      //   - data.session = null + data.user with no error → could be verification pending OR duplicate
      // We use a probe: try signing in. If sign-in says "Invalid credentials" → user exists with different password (duplicate).
      // If sign-in says "Email not confirmed" → genuinely new and waiting for verification.
      if(mode==="signup"){
        console.log("[Netrev signup] Supabase response:", JSON.stringify({
          hasUser: !!d.user,
          identitiesLength: d.user?.identities?.length,
          emailConfirmedAt: d.user?.email_confirmed_at,
          hasSession: !!d.session,
          confirmationSentAt: d.user?.confirmation_sent_at,
        }, null, 2));
        // Signal 1: empty identities array = email exists
        if(d.user && Array.isArray(d.user.identities) && d.user.identities.length === 0){
          setErr("An account with this email already exists. Try signing in instead.");
          setLoading(false);return;
        }
        // Signal 2: user already has confirmed_at = pre-existing confirmed account
        if(d.user && d.user.email_confirmed_at){
          setErr("An account with this email already exists. Try signing in instead.");
          setLoading(false);return;
        }
        // Signal 3: no session returned — could be verification needed OR silent duplicate
        if(!d.session || !d.session.access_token){
          // Probe: try to sign in with the password we just used
          const probe = await supabaseAuth("login", email, pass);
          if(probe.access_token || probe.session?.access_token){
            // Sign-in worked → account already existed AND password matched (rare but possible)
            setErr("An account with this email already exists. We've signed you in.");
            const token = probe.session?.access_token || probe.access_token;
            const isSuperAdmin = SUPER_ADMIN_BOOTSTRAP_EMAILS.includes(email.toLowerCase());
            onAuth({email, name:name||email.split("@")[0], plan:isSuperAdmin?"superadmin":"free", token});
            return;
          }
          const probeErr = (probe.error||probe.msg||"").toString().toLowerCase();
          if(probeErr.includes("invalid login") || probeErr.includes("invalid credentials")){
            // Account exists but with different password
            setErr("An account with this email already exists. Try signing in instead, or use a different email.");
            setLoading(false);return;
          }
          // Otherwise it's genuinely a new account waiting for verification
          setErr("");
          setLoading(false);
          setMode("verify_pending");
          return;
        }
        // Session exists — email verification is OFF, log them in immediately
        const isSuperAdmin = SUPER_ADMIN_BOOTSTRAP_EMAILS.includes(email.toLowerCase());
        onAuth({email, name:name||email.split("@")[0], plan:isSuperAdmin?"superadmin":"free", token:d.session.access_token});
        return;
      }
      // LOGIN
      const token = d.session?.access_token || d.access_token;
      if(!token){
        setErr("Login failed — please verify your email first if you just signed up");
        setLoading(false);return;
      }
      const isSuperAdmin = SUPER_ADMIN_BOOTSTRAP_EMAILS.includes(email.toLowerCase());
      onAuth({email, name:name||email.split("@")[0], plan:isSuperAdmin?"superadmin":"free", token});
    }catch{
      setErr("Connection error — please try again");
    }
    setLoading(false);
  }

  return(
    <div className="auth-root" style={{minHeight:"100vh",background:"#fff",display:"flex",fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",overflowX:"hidden",maxWidth:"100vw"}}>

      {/* LEFT PANEL — Form (white, like GrowDash) */}
      <div className="auth-form-side" style={{flex:"1 1 50%",display:"flex",flexDirection:"column",background:"#fff",minWidth:0}}>
        {/* Top bar */}
        <div className="auth-top-bar" style={{padding:"24px 40px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:16}}>
          <div onClick={onBackToHome} style={{display:"flex",alignItems:"center",gap:10,cursor:onBackToHome?"pointer":"default"}} title={onBackToHome?"Back to home":""}>
            <img src="/logo.svg" alt="Netrev.ai" width={34} height={34} style={{borderRadius:9,display:"block",boxShadow:"0 4px 12px rgba(59,130,246,0.25)"}}/>
            <span style={{fontSize:17,fontWeight:700,color:"#0f172a",letterSpacing:"-.3px"}}>Netrev<span style={{color:"#3b82f6"}}>.ai</span></span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            {/* Language toggle for logged-out users (top nav is hidden on auth).
                Border/background/colour kept neutral to match the auth surface. */}
            <button onClick={()=>setLang(lang==="ar"?"en":"ar")}
              style={{padding:"6px 11px",borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600}}>
              {lang==="ar"?"EN":"عربي"}
            </button>
            <div style={{fontSize:13.5,color:"#64748b",fontWeight:500}}>
              {mode==="signup"?t("auth.alreadyHaveAccount"):mode==="login"?t("auth.newToNetrev"):""}
              {(mode==="signup"||mode==="login")&&(
                <span onClick={()=>{setMode(mode==="signup"?"login":"signup");setErr("");}} style={{color:"#1d4ed8",fontWeight:600,cursor:"pointer"}}>
                  {mode==="signup"?t("btn.signIn"):t("btn.createAccount")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Form area */}
        <div className="auth-form-area" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 40px 40px"}}>
          <div style={{width:"100%",maxWidth:400}}>
            {onBackToHome && (
              <span onClick={onBackToHome} style={{fontSize:13,color:"#64748b",fontWeight:500,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5,marginBottom:24}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                {t("auth.backToHome")}
              </span>
            )}
            {/* Heading */}
            <div style={{marginBottom:32}}>
              <h1 className="auth-headline" style={{fontSize:36,fontWeight:600,color:"#0f172a",letterSpacing:"-1.2px",margin:"0 0 10px",lineHeight:1.15}}>
                {mode==="signup"?t("auth.heading.signup"):mode==="verify_pending"?t("auth.heading.verifyPending"):mode==="reset"?t("auth.heading.reset"):mode==="reset_sent"?t("auth.heading.resetSent"):t("auth.heading.login")}
              </h1>
              <p style={{fontSize:15,color:"#64748b",fontWeight:400,lineHeight:1.55,margin:0}}>
                {mode==="signup"?t("auth.sub.signup"):mode==="verify_pending"?"":mode==="reset"?t("auth.sub.reset"):mode==="reset_sent"?"":t("auth.sub.login")}
              </p>
            </div>

            {/* Error */}
            {err && (
              <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:9,padding:"11px 14px",fontSize:13.5,color:"#991b1b",marginBottom:20,fontWeight:500,lineHeight:1.45}}>
                {err}
                {err.includes("already exists") && (
                  <span onClick={()=>{setMode("login");setErr("");setPass("");}} style={{display:"inline-block",marginInlineStart:6,color:"#0f172a",fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>{t("app.auth.signInArrow")}</span>
                )}
              </div>
            )}

            {/* VERIFY PENDING screen */}
            {mode==="verify_pending"?(
              <div>
                <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:12,padding:"24px",marginBottom:18}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:18}}>
                    <div style={{width:42,height:42,borderRadius:10,background:"linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 4px 12px rgba(59,130,246,0.25)"}}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"#0f172a",marginBottom:4}}>{t("app.auth.weSentConfirmation")}</div>
                      <div style={{fontSize:14,color:"#1d4ed8",fontWeight:600,wordBreak:"break-all"}}>{email}</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:"#64748b",lineHeight:1.65,paddingTop:16,borderTop:"1px solid #e2e8f0"}}>
                    {t("app.auth.activateLinkExpires")}
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>{setMode("signup");setErr("");}} style={{flex:1,padding:"12px 16px",border:"1px solid #e2e8f0",borderRadius:9,background:"#fff",color:"#0f172a",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {t("app.auth.useDifferentEmail")}
                  </button>
                  <button onClick={()=>{setMode("login");setErr("");setPass("");}} style={{flex:1,padding:"12px 16px",border:"none",borderRadius:9,background:"linear-gradient(135deg,#1e1b4b 0%,#0f172a 100%)",color:"#fff",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {t("app.auth.signIn")}
                  </button>
                </div>
              </div>
            ):mode==="reset_sent"?(
              <div>
                <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:12,padding:"24px",marginBottom:18}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:18}}>
                    <div style={{width:42,height:42,borderRadius:10,background:"linear-gradient(135deg,#10b981 0%,#059669 100%)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 4px 12px rgba(16,185,129,0.25)"}}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"#0f172a",marginBottom:4}}>{t("app.auth.ifAccountExistsFor")}</div>
                      <div style={{fontSize:14,color:"#1d4ed8",fontWeight:600,wordBreak:"break-all"}}>{email}</div>
                      <div style={{fontSize:13,color:"#64748b",marginTop:6,fontWeight:500}}>{t("app.auth.weSentResetLink")}</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:"#64748b",lineHeight:1.65,paddingTop:16,borderTop:"1px solid #e2e8f0"}}>
                    {t("app.auth.resetLinkExpires")}
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>{setMode("reset");setErr("");}} style={{flex:1,padding:"12px 16px",border:"1px solid #e2e8f0",borderRadius:9,background:"#fff",color:"#0f172a",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {t("app.auth.tryDifferentEmail")}
                  </button>
                  <button onClick={()=>{setMode("login");setErr("");setPass("");}} style={{flex:1,padding:"12px 16px",border:"none",borderRadius:9,background:"linear-gradient(135deg,#1e1b4b 0%,#0f172a 100%)",color:"#fff",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {t("app.auth.backToSignIn")}
                  </button>
                </div>
              </div>
            ):mode==="reset"?(
              <div>
                <div style={{marginBottom:18}}>
                  <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.email")}</label>
                  <input value={email} onChange={e=>setEmail(e.target.value)} placeholder={t("app.auth.emailPlaceholder")} type="email" autoComplete="email" autoFocus onKeyDown={e=>e.key==="Enter"&&submit()} style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
                </div>
                <button onClick={submit} disabled={loading} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:loading?"#94a3b8":"linear-gradient(135deg,#1e1b4b 0%,#0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",transition:"all 0.15s",letterSpacing:"-.1px",boxShadow:loading?"none":"0 4px 14px rgba(15,23,42,0.25)",marginBottom:14}}>
                  {loading?t("btn.sending"):t("btn.sendResetLink")}
                </button>
                <div style={{textAlign:"center"}}>
                  <span onClick={()=>{setMode("login");setErr("");}} style={{fontSize:13,color:"#64748b",fontWeight:500,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                    {t("auth.backToSignIn")}
                  </span>
                </div>
              </div>
            ):(
            <div>
              {mode==="signup"&&(
                <div style={{marginBottom:18}}>
                  <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.region")}</label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {[{key:"UK",flag:"🇬🇧",label:t("app.auth.unitedKingdom"),sub:t("app.auth.billedInGbp")},{key:"UAE",flag:"🇦🇪",label:t("app.auth.uae"),sub:t("app.auth.billedInAed")},{key:"US",flag:"🇺🇸",label:t("app.auth.unitedStates"),sub:t("app.auth.billedInUsd")},{key:"IN",flag:"🇮🇳",label:t("app.auth.india"),sub:t("app.auth.billedInInr")}].map(opt=>(
                      <button key={opt.key} type="button" onClick={()=>setRegion(opt.key)} style={{padding:"12px 14px",borderRadius:9,border:region===opt.key?"2px solid #1d4ed8":"2px solid transparent",background:region===opt.key?"#EBF2FF":"#f1f5f9",cursor:"pointer",textAlign:"start",fontFamily:"inherit",transition:"all 0.15s"}}>
                        <div style={{fontSize:18,marginBottom:2}}>{opt.flag}</div>
                        <div style={{fontSize:13,fontWeight:600,color:region===opt.key?"#1d4ed8":"#0f172a"}}>{opt.label}</div>
                        <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                  <p style={{fontSize:11,color:"#94a3b8",marginTop:8,lineHeight:1.5}}>{t("app.auth.regionLockNote")}</p>
                </div>
              )}
              {mode==="signup"&&(
                <div style={{marginBottom:18}}>
                  <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.name")}</label>
                  <input value={name} onChange={e=>setName(e.target.value)} placeholder={t("app.auth.namePlaceholder")} autoFocus style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
                </div>
              )}
              {mode==="signup"&&(
                <div style={{marginBottom:18}}>
                  <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.whatsapp")}</label>
                  <div style={{display:"flex",gap:8}}>
                    <span style={{padding:"13px 14px",borderRadius:9,background:"#e2e8f0",fontSize:14,fontWeight:600,color:"#334155",flexShrink:0,lineHeight:"20px"}}>{DIAL_CODES[region]}</span>
                    <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder={t("app.auth.phonePlaceholder")} type="tel" inputMode="tel" autoComplete="tel-national" onKeyDown={e=>e.key==="Enter"&&submit()} style={{flex:1,minWidth:0,padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
                  </div>
                </div>
              )}
              {mode==="signup"&&(
                <div style={{marginBottom:18}}>
                  <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.businessType")}</label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {[{key:"restaurant",label:t("app.auth.bizRestaurant")},{key:"cloud_kitchen",label:t("app.auth.bizCloudKitchen")},{key:"pharmacy",label:t("app.auth.bizPharmacy")},{key:"supermarket",label:t("app.auth.bizSupermarket")},{key:"retail",label:t("app.auth.bizRetail")}].map(opt=>(
                      <button key={opt.key} type="button" onClick={()=>setBusinessType(opt.key)} style={{padding:"12px 14px",borderRadius:9,border:businessType===opt.key?"2px solid #1d4ed8":"2px solid transparent",background:businessType===opt.key?"#EBF2FF":"#f1f5f9",cursor:"pointer",textAlign:"start",fontFamily:"inherit",transition:"all 0.15s"}}>
                        <div style={{fontSize:13,fontWeight:600,color:businessType===opt.key?"#1d4ed8":"#0f172a"}}>{opt.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{marginBottom:18}}>
                <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.email")}</label>
                <input value={email} onChange={e=>setEmail(e.target.value)} placeholder={t("app.auth.emailPlaceholder")} type="email" autoComplete="email" autoFocus={mode!=="signup"} style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
              </div>
              <div style={{marginBottom:18}}>
                <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("auth.field.password")}</label>
                <input value={pass} onChange={e=>setPass(e.target.value)} type="password" placeholder={mode==="signup"?t("app.auth.passwordPlaceholderSignup"):t("app.auth.passwordPlaceholderLogin")} autoComplete={mode==="signup"?"new-password":"current-password"} onKeyDown={e=>e.key==="Enter"&&submit()} style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:"#475569",fontWeight:500}}>
                  <input type="checkbox" checked={keepSignedIn} onChange={e=>setKeepSignedIn(e.target.checked)} style={{width:16,height:16,accentColor:"#1d4ed8",cursor:"pointer"}}/>
                  {t("auth.keepSignedIn")}
                </label>
                {mode==="login"&&<span onClick={()=>{setMode("reset");setErr("");setPass("");}} style={{fontSize:13,color:"#1d4ed8",fontWeight:600,cursor:"pointer"}}>{t("auth.resetPassword")}</span>}
              </div>
              <button onClick={submit} disabled={loading} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:loading?"#94a3b8":"linear-gradient(135deg,#1e1b4b 0%,#0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",transition:"all 0.15s",letterSpacing:"-.1px",boxShadow:loading?"none":"0 4px 14px rgba(15,23,42,0.25)"}}>
                {loading?t("btn.pleaseWait"):mode==="signup"?t("btn.createAccount"):t("btn.signIn")}
              </button>
              {mode==="signup"&&(
                <p style={{fontSize:12,color:"#94a3b8",textAlign:"center",marginTop:18,fontWeight:400,lineHeight:1.55}}>
                  {t("app.auth.byCreatingAgree")}
                  <span onClick={()=>window.open("/terms","_blank","noopener")} style={{color:"#1d4ed8",fontWeight:500,cursor:"pointer"}}>{t("app.auth.terms")}</span>
                  {t("app.auth.and")}
                  <span onClick={()=>window.open("/privacy","_blank","noopener")} style={{color:"#1d4ed8",fontWeight:500,cursor:"pointer"}}>{t("app.auth.privacyPolicy")}</span>.
                </p>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — Visual slideshow (hidden on mobile) */}
      <div className="auth-visual" style={{flex:"1 1 50%",padding:"20px 24px 20px 0",display:"flex"}}>
        <AuthVisualPanel/>
      </div>

      {/* Responsive — collapse to single column on mobile */}
      <style>{`
        html, body { overflow-x: hidden !important; max-width: 100vw !important; }
        .auth-root * { box-sizing: border-box; }
        @media (max-width: 980px) {
          .auth-visual { display: none !important; }
        }
        @media (max-width: 600px) {
          .auth-top-bar { padding: 18px 18px !important; }
          .auth-form-area { padding: 0 18px 32px !important; }
          .auth-headline { font-size: 28px !important; letter-spacing: -.8px !important; }
        }
        @media (max-width: 420px) {
          .auth-top-bar { padding: 14px 14px !important; flex-wrap: wrap !important; gap: 8px !important; }
          .auth-top-bar > div:first-child > span:last-child { font-size: 15px !important; }
          .auth-headline { font-size: 24px !important; letter-spacing: -.6px !important; }
        }
      `}</style>
    </div>
  );
}

// ── Upgrade prompt for free users ────────────────────────────────────
function UpgradePrompt({feature, onUpgrade}) {
  const { t } = useT();
  return(
    <div style={{padding:40,textAlign:"center",maxWidth:440,margin:"0 auto"}}>
      <div style={{fontSize:40,marginBottom:16}}>🔒</div>
      <h3 style={{fontSize:18,fontWeight:600,color:C.text,margin:"0 0 10px"}}>{t("app.upgrade.proFeature")}</h3>
      <p style={{fontSize:14,color:C.muted,lineHeight:1.6,marginBottom:20}}>{feature}{t("app.upgrade.featureAvailableSuffix")}</p>
      <div style={{background:C.bg2,borderRadius:10,padding:18,marginBottom:20,textAlign:"start"}}>
        {PLANS.pro.features.map(f=>(
          <div key={f} style={{display:"flex",gap:8,fontSize:13,color:C.sub,marginBottom:7}}><span style={{color:C.green,fontWeight:700}}>✓</span>{f}</div>
        ))}
      </div>
      <Btn onClick={onUpgrade} sx={{padding:"11px 28px",fontSize:14}}>{t("app.upgrade.upgradeProCta")}</Btn>
      <p style={{fontSize:11,color:C.muted2,marginTop:8}}>{t("app.upgrade.cancelAnytimeNote")}</p>
    </div>
  );
}

// ── Power Admin (superuser only) ─────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
// PowerAdmin — Netrev.ai Super Admin Panel
// ═══════════════════════════════════════════════════════════════════════
// DROP-IN REPLACEMENT for the existing function PowerAdmin({onClose}) in
// Netrev_v8.jsx (currently at ~line 1766–1830).
//
// What you need: nothing extra. This file uses the same module-scoped
// `supabase`, `API_BASE`, and `C` palette that the rest of Netrev_v8.jsx
// already defines, so just replace the existing function body with what's
// in this file.
//
// Server side: requires admin_routes.js to be mounted under /api/admin/*.
// All endpoints are gated by requireSuperAdmin on the server, so even if
// a non-admin loads this component (e.g. by editing localStorage), every
// request gets a 403.
//
// What it does:
//   - 4 tabs: Stats, Accounts, Audit log, Super admins
//   - Account search + filter (status, archived)
//   - "Inspect" → opens a side panel with a read-only snapshot of the
//     target account. NOT impersonation — you remain signed in as
//     yourself; the panel just renders their data via the server.
//     Every snapshot fetch is recorded in admin_audit_log.
//   - Free-override toggle (with reason input)
//   - Add/remove super admins
//   - Audit log viewer
//
// Read-only "view as" deliberately does NOT swap your session. It opens
// a panel showing the target user's account, workspaces, members, and
// subscription. Every snapshot fetch is logged in admin_audit_log.
// ═══════════════════════════════════════════════════════════════════════

// NOTE: The admin "tabs" (OverviewTab, AccountsTab, AuditTab, SuperAdminsTab)
// are rendered directly from AdminView's main content area via sidebar nav,
// not as a modal. SnapshotPanel is rendered at the AdminView level too.
// There is no PowerAdmin wrapper component any more.


// ─────────────────────────────────────────────────────────────────────
// API helper — every call sends the current Supabase JWT.
// ─────────────────────────────────────────────────────────────────────
async function adminFetch(path, opts = {}) {
  const session = await getCurrentSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}


// ─────────────────────────────────────────────────────────────────────
// OVERVIEW TAB — headline metrics, status pie, and recent churn list


// ─────────────────────────────────────────────────────────────────────
// ADMIN ICONS
// ─────────────────────────────────────────────────────────────────────
const ADMIN_PAGE_ICONS = {
  overview:     ["M3 12h4l3-9 4 18 3-9h4"],
  accounts:     ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"],
  audit:        ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"],
  super_admins: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z","M9 12l2 2 4-4"],
};


// ─────────────────────────────────────────────────────────────────────
// OVERVIEW TAB — KPIs, status pie chart, cancellation outreach list
// ─────────────────────────────────────────────────────────────────────
function OverviewTab() {
  const { t } = useT();
  const [stats, setStats]     = useState(null);
  const [churn, setChurn]     = useState(null);
  const [funnel, setFunnel]   = useState(null);   // signup funnel (GET /api/admin/funnel)
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [s, c, fn] = await Promise.all([
        adminFetch("/api/admin/stats"),
        adminFetch("/api/admin/churn?limit=100"),
        adminFetch("/api/admin/funnel?days=30").catch(e => ({ error: e.message })),
      ]);
      setStats(s); setChurn(c); setFunnel(fn); setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <AdminPage><Loading/></AdminPage>;
  if (error)   return <AdminPage><ErrorBlock msg={error}/></AdminPage>;
  if (!stats)  return <AdminPage/>;

  // Pie data with brand-aligned colours.
  const STATUS_COLOURS = {
    active:        "#16a34a",
    trialing:      "#2563eb",
    free_override: "#7c3aed",
    past_due:      "#dc2626",
    canceled:      "#64748b",
    incomplete:    "#ca8a04",
    paused:        "#94a3b8",
  };
  const pieData = Object.entries(stats.accounts_by_status || {})
    .map(([status, value]) => ({
      name: status.replace(/_/g, " "),
      status, value,
      color: STATUS_COLOURS[status] || "#94a3b8",
    }))
    .sort((a, b) => b.value - a.value);
  const pieTotal  = pieData.reduce((s, p) => s + p.value, 0);
  const cancelled = stats.accounts_by_status?.canceled || 0;
  const churnRate = pieTotal > 0 ? (cancelled / pieTotal) * 100 : 0;

  return (
    <AdminPage>
      <AdminPageHeader
        icon={ADMIN_PAGE_ICONS.overview}
        title={t("app.admin.overviewTitle")}
        description={t("app.admin.overviewDesc")}
        right={
          <button onClick={load} style={btnSecondary}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginInlineEnd:6, verticalAlign:-2}}>
              <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
            </svg>
            {t("app.admin.refresh")}
          </button>
        }
      />

      {/* KPI cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        <KpiCard
          accent="#1e40af"
          label={t("app.admin.totalAccounts")}
          value={stats.accounts_total}
          sub={`${stats.accounts_archived} ${t("app.admin.archivedSuffix")}`}
        />
        <KpiCard
          accent="#16a34a"
          label={t("app.admin.activeAccess")}
          value={stats.accounts_with_access}
          sub={t("app.admin.trialPaidComped")}
        />
        <KpiCard
          accent="#dc2626"
          label={t("app.admin.cancelled")}
          value={cancelled}
          sub={pieTotal > 0 ? `${churnRate.toFixed(1)}% ${t("app.admin.churnRateSuffix")}` : "—"}
        />
        <KpiCard
          accent="#7c3aed"
          label={t("app.admin.estMrr")}
          value={<>£{stats.estimated_mrr_gbp.toFixed(2)}</>}
          sub={stats.estimated_mrr_aed > 0
            ? `+ AED ${stats.estimated_mrr_aed.toFixed(2)} · approx`
            : t("app.admin.mrrApproxNote")}
        />
      </div>

      {/* Signup funnel — where the last 30 days of signups stopped */}
      <AdminSectionLabel>{t("app.admin.funnelTitle")}</AdminSectionLabel>
      <AdminCard style={{ marginBottom:24, padding:20 }}>
        {!funnel || funnel.error ? (
          <div style={{ padding:"20px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
            {funnel?.error ? `${t("app.admin.funnelUnavailable")} (${funnel.error})` : t("app.admin.funnelUnavailable")}
          </div>
        ) : (() => {
          const labels = {
            signed_up: t("app.admin.funnel.signedUp"), confirmed: t("app.admin.funnel.confirmed"), signed_in: t("app.admin.funnel.signedIn"),
            first_workspace: t("app.admin.funnel.firstWorkspace"), first_log: t("app.admin.funnel.firstLog"), seven_logs: t("app.admin.funnel.sevenLogs"),
            card_added: t("app.admin.funnel.cardAdded"), paid: t("app.admin.funnel.paid"),
          };
          const top = Math.max(1, ...(funnel.stages || []).map(x => x.count));
          const base = (funnel.stages || [])[0]?.count || 0;
          return (
            <>
              <div style={{ display:"grid", gap:8 }}>
                {(funnel.stages || []).map(st => (
                  <div key={st.key} style={{ display:"grid", gridTemplateColumns:"170px 1fr 90px", alignItems:"center", gap:12, fontSize:13 }}>
                    <div style={{ color:"#334155", fontWeight:600 }}>{labels[st.key] || st.key}</div>
                    <div style={{ background:"#f1f5f9", borderRadius:6, height:18, overflow:"hidden" }}>
                      <div style={{ width:`${Math.round((st.count / top) * 100)}%`, height:"100%", background: st.key === "paid" ? "#16a34a" : st.key === "card_added" ? "#7c3aed" : "#3b5bdb", transition:"width .3s" }}/>
                    </div>
                    <div className="num" style={{ textAlign:"right", color:"#0f172a", fontWeight:700 }}>{st.count}<span style={{ color:"#94a3b8", fontWeight:500, marginLeft:6 }}>{base ? Math.round((st.count / base) * 100) : 0}%</span></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:12, fontSize:12, color:"#64748b" }}>
                {t("app.admin.funnel.eventsPrefix")} {funnel.events?.checkout_opened ?? 0} {t("app.admin.funnel.openedCheckout")} · {funnel.events?.paywall_seen ?? 0} {t("app.admin.funnel.sawPaywall")} · {funnel.events?.statement_uploaded ?? 0} {t("app.admin.funnel.uploadedStatement")}
                {funnel.events_available === false ? ` · ${t("app.admin.funnel.eventsPending")}` : ""}
              </div>
              {(funnel.accounts || []).length > 0 && (
                <div style={{ marginTop:16, overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
                    <thead>
                      <tr>{[t("app.admin.funnel.col.email"), t("app.admin.funnel.col.region"), t("app.admin.funnel.col.signedUp"), t("app.admin.funnel.col.stage"), t("app.admin.funnel.col.lastSeen"), t("app.admin.funnel.col.logs")].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"6px 8px", borderBottom:"1px solid #e2e8f0", color:"#64748b", fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {funnel.accounts.slice(0, 25).map(a => (
                        <tr key={a.account_id}>
                          <td style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9", color:"#0f172a" }}>{a.email}</td>
                          <td style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9", color:"#64748b" }}>{a.region || "—"}</td>
                          <td style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9", color:"#64748b", whiteSpace:"nowrap" }}>{String(a.signed_up_at || "").slice(0, 10)}</td>
                          <td style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9" }}>
                            <span style={{ padding:"2px 8px", borderRadius:999, fontSize:11.5, fontWeight:600, background: a.stage === "paid" ? "#dcfce7" : a.stage === "card_added" ? "#ede9fe" : a.stage === "first_log" || a.stage === "seven_logs" ? "#dbeafe" : "#f1f5f9", color: a.stage === "paid" ? "#15803d" : a.stage === "card_added" ? "#6d28d9" : a.stage === "first_log" || a.stage === "seven_logs" ? "#1d4ed8" : "#475569" }}>{labels[a.stage] || a.stage}</span>
                            {a.checkout_opened && a.stage !== "paid" && a.stage !== "card_added" ? <span title={t("app.admin.funnel.openedCheckout")} style={{ marginLeft:6, fontSize:11, color:"#b45309" }}>💳</span> : null}
                          </td>
                          <td style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9", color:"#64748b", whiteSpace:"nowrap" }}>{a.last_sign_in_at ? String(a.last_sign_in_at).slice(0, 10) : "—"}</td>
                          <td className="num" style={{ padding:"6px 8px", borderBottom:"1px solid #f1f5f9", color:"#0f172a", textAlign:"right" }}>{a.log_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}
      </AdminCard>

      {/* Subscription mix — pie + legend in a card */}
      <AdminSectionLabel>{t("app.admin.subscriptionMix")}</AdminSectionLabel>
      <AdminCard style={{ marginBottom:24, padding:20 }}>
        {pieTotal === 0 ? (
          <div style={{ padding:"40px 20px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
            {t("app.admin.noAccountsYet")}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:32, alignItems:"center" }}>
            <div style={{ position:"relative", height:240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={62} outerRadius={108}
                    paddingAngle={1}
                    stroke="#fff" strokeWidth={2}
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) => {
                      // Only label slices ≥ 7% — anything smaller would overlap with the legend.
                      if (percent < 0.07) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
                          style={{ fontSize:13, fontWeight:700, fontVariantNumeric:"tabular-nums", letterSpacing:"-.3px" }}>
                          {value}
                        </text>
                      );
                    }}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color}/>
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background:"#0f172a", border:"none", borderRadius:6, fontSize:12, padding:"6px 10px" }}
                    labelStyle={{ color:"#cbd5e1", fontSize:11 }}
                    itemStyle={{ color:"#fff", textTransform:"capitalize" }}
                    formatter={(value, name) => [`${value} ${value === 1 ? "account" : "accounts"}`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Centered total inside the donut */}
              <div style={{
                position:"absolute", inset:0, display:"flex",
                alignItems:"center", justifyContent:"center",
                flexDirection:"column", pointerEvents:"none",
              }}>
                <div className="num" style={{ fontSize:28, fontWeight:700, color:"#0f172a", letterSpacing:"-.6px", lineHeight:1 }}>
                  {pieTotal}
                </div>
                <div style={{ fontSize:10.5, color:"#94a3b8", marginTop:4, textTransform:"uppercase", letterSpacing:.6, fontWeight:600 }}>
                  {t("app.admin.totalAccounts")}
                </div>
              </div>
            </div>

            <div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:8 }}>
                {pieData.map(p => (
                  <div key={p.status} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                    background:"#fafbfc", border:"1px solid #e2e8f0", borderRadius:7,
                  }}>
                    <span style={{ width:10, height:10, borderRadius:3, background:p.color, flexShrink:0 }}/>
                    <span style={{ fontSize:12.5, color:"#0f172a", textTransform:"capitalize", flex:1, letterSpacing:"-.1px", fontWeight:500 }}>
                      {p.name}
                    </span>
                    <span className="num" style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{p.value}</span>
                    <span className="num" style={{ fontSize:11, color:"#94a3b8", minWidth:38, textAlign:"end", fontWeight:500 }}>
                      {((p.value / pieTotal) * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </AdminCard>

      <ChurnList rows={churn?.accounts || []}/>
    </AdminPage>
  );
}


function KpiCard({ accent, label, value, sub }) {
  return (
    <div style={{
      background:"#fff", border:"1px solid #e2e8f0", borderRadius:10,
      borderInlineStart:`3px solid ${accent}`, padding:"16px 18px",
      boxShadow:"0 1px 2px rgba(15,23,42,.04)",
    }}>
      <div className="num" style={{ fontSize:24, fontWeight:700, color:"#0f172a", letterSpacing:"-.5px", lineHeight:1.1 }}>
        {value}
      </div>
      <div style={{ fontSize:12, color:"#475569", marginTop:8, fontWeight:600, letterSpacing:"-.1px" }}>
        {label}
      </div>
      {sub && <div style={{ fontSize:11, color:"#94a3b8", marginTop:3, fontWeight:400 }}>{sub}</div>}
    </div>
  );
}


function ChurnList({ rows }) {
  const { t } = useT();
  const cancelled  = rows.filter(r => r.churn_bucket === "canceled");
  const canceling  = rows.filter(r => r.churn_bucket === "canceling");
  const pastDue    = rows.filter(r => r.churn_bucket === "past_due");

  const mailto = (email, name) => {
    const subject = encodeURIComponent("Sorry to see you go — Netrev feedback?");
    const body = encodeURIComponent(
      `Hi${name ? " " + name : ""},\n\n` +
      `I noticed you've cancelled your Netrev subscription. ` +
      `Would you be open to sharing what didn't work, or what we could have done better? ` +
      `It would help us a lot — even one sentence is useful.\n\n` +
      `Thanks,\nYaseen`
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const bucketChip = (bucket) => {
    const styles = {
      canceled:  ["#f1f5f9", "#475569", "#cbd5e1", t("app.admin.chipCancelled")],
      canceling: ["#fef3c7", "#92400e", "#fde68a", t("app.admin.chipCancelling")],
      past_due:  ["#fef2f2", "#991b1b", "#fecaca", t("app.admin.chipPastDue")],
    };
    const [bg, fg, bd, label] = styles[bucket] || styles.canceled;
    return (
      <span style={{
        padding:"2px 8px", borderRadius:4, fontSize:10.5, fontWeight:700,
        background:bg, color:fg, border:`1px solid ${bd}`, letterSpacing:".3px",
      }}>{label}</span>
    );
  };

  return (
    <>
      <AdminSectionLabel>{t("app.admin.cancellationsAtRisk")}</AdminSectionLabel>
      <AdminCard>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid #f1f5f9", display:"flex", flexWrap:"wrap", gap:16, alignItems:"baseline" }}>
          <div style={{ display:"flex", gap:14, fontSize:11.5, color:"#64748b" }}>
            <span><span className="num" style={{ fontWeight:700, color:"#475569" }}>{cancelled.length}</span> {t("app.admin.churnCancelled")}</span>
            <span><span className="num" style={{ fontWeight:700, color:"#92400e" }}>{canceling.length}</span> {t("app.admin.churnCancelling")}</span>
            <span><span className="num" style={{ fontWeight:700, color:"#991b1b" }}>{pastDue.length}</span> {t("app.admin.churnPastDue")}</span>
          </div>
          <div style={{ flex:1, fontSize:11.5, color:"#94a3b8", textAlign:"end" }}>
            {t("app.admin.contactOpensEmail")}
          </div>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding:"36px 24px", textAlign:"center" }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#0f172a", marginBottom:4 }}>{t("app.admin.noCancellationsYet")}</div>
            <div style={{ fontSize:12, color:"#94a3b8" }}>{t("app.admin.nothingToReachOut")}</div>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#fafbfc" }}>
                  {[t("app.admin.colAccount"),t("app.admin.colOwnerEmail"),t("app.admin.colStatus"),t("app.admin.colPlan"),t("app.admin.colDaysActive"),t("app.admin.colCancelledApprox"),t("app.admin.colPeriodEnds"),""].map(h => (
                    <th key={h} style={adminThStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.account_id} style={{
                    borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                    transition:"background .12s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafbfc"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={adminTdStyle}>
                      <div style={{ fontWeight:600, color:"#0f172a", fontSize:13 }}>{r.account_name || "—"}</div>
                      <div className="num" style={{ fontSize:10.5, color:"#cbd5e1", marginTop:2 }}>
                        {r.account_id.slice(0,8)}
                      </div>
                    </td>
                    <td style={{ ...adminTdStyle, fontSize:12.5, color:"#475569" }}>
                      {r.owner_email || "—"}
                    </td>
                    <td style={adminTdStyle}>
                      {bucketChip(r.churn_bucket)}
                    </td>
                    <td style={{ ...adminTdStyle, fontSize:12, color:"#64748b", textTransform:"capitalize" }}>
                      {r.subscription_plan || "—"}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#0f172a", fontSize:13, fontWeight:500 }}>
                      {r.days_active != null ? r.days_active : "—"}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>
                      {fmtDate(r.account_updated_at)}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>
                      {fmtDate(r.current_period_end)}
                    </td>
                    <td style={{ ...adminTdStyle, textAlign:"end" }}>
                      {r.owner_email && (
                        <a href={mailto(r.owner_email, r.account_name)}
                          style={{ ...btnPrimaryDark, fontSize:11.5, padding:"5px 12px", textDecoration:"none", display:"inline-block" }}>
                          {t("app.admin.contact")}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────
// ACCOUNTS TAB
// ─────────────────────────────────────────────────────────────────────
// onSetupFeed({ id, name }): optional, passed only while the integration feed
// is on (AdminView feedOn); hands the account to the feed setup screen.
function AccountsTab({ onInspect, onSetupFeed }) {
  const { t } = useT();
  const [accounts, setAccounts]     = useState([]);
  const [count,    setCount]        = useState(0);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState(null);
  const [q,        setQ]            = useState("");
  const [status,   setStatus]       = useState("");
  const [archived, setArchived]     = useState("0");
  const [sort,     setSort]         = useState("created_desc");
  const [offset,   setOffset]       = useState(0);
  const limit = 50;

  const refresh = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: String(limit), offset: String(offset), sort,
      });
      if (q.trim()) params.set("q", q.trim());
      if (status)   params.set("status", status);
      if (archived) params.set("archived", archived);
      const data = await adminFetch(`/api/admin/accounts?${params.toString()}`);
      setAccounts(data.accounts || []);
      setCount(data.count || 0);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [offset, status, archived, sort]);
  useEffect(() => {
    const timer = setTimeout(() => { setOffset(0); refresh(); }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [q]);

  // Client-side per-column sort + filter over the CURRENT page of accounts
  // (server still owns search + the coarse sort + pagination).
  const acctCols = [
    {key:"account", label:t("app.admin.colAccount"),    type:"text",   value:a=>a.account_name||"", align:"left"},
    {key:"owner",   label:t("app.admin.colOwner"),      type:"text",   value:a=>a.owner_email||"",  align:"left"},
    // Signup phone. Lives in auth.users.raw_user_meta_data->>'phone' and is
    // surfaced by v_admin_accounts_overview.owner_phone (migration 040) — it is
    // NOT a column on accounts, so there is nothing else to read it from.
    {key:"phone",   label:t("app.admin.colPhone"),      type:"text",   value:a=>a.owner_phone||"",  align:"left"},
    // What the operator actually named their sites — active workspaces only,
    // comma-joined by the view (migration 040). Useful for recognising who a
    // signup is when the account name is just the email's local part.
    {key:"wsnames", label:t("app.admin.colSiteNames"),  type:"text",   value:a=>a.workspace_names||"", align:"left"},
    {key:"status",  label:t("app.snap.status"),     type:"text",   value:a=>a.subscription_status||"", align:"left"},
    {key:"sites",   label:t("app.admin.colSites"),      type:"number", value:a=>a.active_workspace_count||0, align:"right"},
    {key:"mrr",     label:t("app.admin.colMrr"),        type:"number", value:a=>Number(a.estimated_mrr||0), align:"right"},
    {key:"period",  label:t("app.admin.colPeriodEndShort"), type:"date",   value:a=>a.trial_ends_at||a.current_period_end, align:"right"},
    {key:"joined",  label:t("app.admin.colJoined"),     type:"date",   value:a=>a.account_created_at, align:"right"},
  ];
  const acctTable = useTableSortFilter(accounts, acctCols);

  return (
    <AdminPage>
      <AdminPageHeader
        icon={ADMIN_PAGE_ICONS.accounts}
        title={t("app.admin.accountsTitle")}
        description={t("app.admin.accountsDesc")}
      />

      <AdminCard>
        {/* Filter bar */}
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", background:"#fafbfc" }}>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder={t("app.admin.searchEmailOrAccount")}
            style={inputStyle}
          />
          <select value={status} onChange={e => { setStatus(e.target.value); setOffset(0); }} style={selectStyleAdmin}>
            <option value="">{t("app.admin.allStatuses")}</option>
            <option value="trialing">{t("app.admin.statusTrialing")}</option>
            <option value="active">{t("app.admin.statusActive")}</option>
            <option value="past_due">{t("app.admin.statusPastDue")}</option>
            <option value="canceled">{t("app.admin.statusCancelled")}</option>
            <option value="incomplete">{t("app.admin.statusIncomplete")}</option>
            <option value="free_override">{t("app.admin.statusFreeOverride")}</option>
          </select>
          <select value={archived} onChange={e => { setArchived(e.target.value); setOffset(0); }} style={selectStyleAdmin}>
            <option value="0">{t("app.admin.activeOnly")}</option>
            <option value="1">{t("app.admin.archivedOnly")}</option>
            <option value="">{t("app.admin.allAccounts")}</option>
          </select>
          <select value={sort} onChange={e => { setSort(e.target.value); setOffset(0); }} style={selectStyleAdmin}>
            <option value="created_desc">{t("app.admin.newestFirst")}</option>
            <option value="created_asc">{t("app.admin.oldestFirst")}</option>
            <option value="email_asc">{t("app.admin.emailAZ")}</option>
          </select>
        </div>

        {error && <div style={{ padding:"12px 20px", color:"#991b1b", fontSize:13, background:"#fef2f2", borderBottom:"1px solid #fecaca" }}>{t("app.admin.errorPrefix")} {error}</div>}

        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#fafbfc" }}>
                {acctCols.map(sc => (
                  <SortableTh key={sc.key} col={sc} table={acctTable} thStyle={adminThStyle} align={sc.align}/>
                ))}
                <th style={adminThStyle}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.admin.loadingEllipsis")}</td></tr>
              )}
              {!loading && accounts.length === 0 && (
                <tr><td colSpan={10} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>
                  {t("app.admin.noAccountsMatchFilters")}
                </td></tr>
              )}
              {!loading && accounts.length > 0 && acctTable.rows.length === 0 && (
                <tr><td colSpan={10} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>
                  {t("app.admin.noAccountsMatchColumn")}
                </td></tr>
              )}
              {!loading && acctTable.rows.map((a, i) => {
                const periodEnd = a.trial_ends_at || a.current_period_end;
                const mrrSym = a.billing_currency === "AED" ? "AED " : a.billing_currency === "USD" ? "$" : a.billing_currency === "INR" ? "₹" : "£";
                return (
                  <tr key={a.account_id} style={{
                    borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                    transition:"background .12s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafbfc"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={adminTdStyle}>
                      <div style={{ fontWeight:600, color:"#0f172a", fontSize:13 }}>{a.account_name || "—"}</div>
                      <div className="num" style={{ fontSize:10.5, color:"#cbd5e1", marginTop:2 }}>
                        {a.account_id.slice(0,8)}
                      </div>
                    </td>
                    <td style={{ ...adminTdStyle, fontSize:12.5, color:"#475569" }}>
                      {a.owner_email || "—"}
                    </td>
                    <td style={{ ...adminTdStyle, fontSize:12.5, color:"#475569" }}>
                      {a.owner_phone ? (
                        <a href={`tel:${a.owner_phone}`}
                           style={{ color:"#2c50dd", textDecoration:"none", fontWeight:600, whiteSpace:"nowrap", unicodeBidi:"plaintext" }}
                           title={a.owner_phone}>
                          {a.owner_phone}
                        </a>
                      ) : "—"}
                    </td>
                    <td style={{ ...adminTdStyle, fontSize:12.5, color:"#475569", maxWidth:220 }}>
                      {a.workspace_names ? (
                        <span title={a.workspace_names}
                              style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {a.workspace_names}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={adminTdStyle}>
                      {renderStatusChip(a.subscription_status)}
                      {a.free_override && (
                        isOverrideExpired(a) ? (
                          <span style={{ marginInlineStart:6, fontSize:10, color:"#92400e", fontWeight:700, letterSpacing:".4px", padding:"2px 6px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:4 }}>
                            {t("app.admin.badgeFreeExpired")}
                          </span>
                        ) : (
                          <span style={{ marginInlineStart:6, fontSize:10, color:"#5b21b6", fontWeight:700, letterSpacing:".4px", padding:"2px 6px", background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:4 }}>
                            {t("app.admin.badgeFree")}
                          </span>
                        )
                      )}
                      {a.archived_at && (
                        <span style={{ marginInlineStart:6, fontSize:10, color:"#64748b", fontWeight:700, letterSpacing:".4px", padding:"2px 6px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:4 }}>
                          {t("app.admin.badgeArchived")}
                        </span>
                      )}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#0f172a", fontWeight:500 }}>{a.active_workspace_count}</td>
                    <td className="num" style={{ ...adminTdStyle, fontWeight:600, color: (a.estimated_mrr > 0) ? "#0f172a" : "#cbd5e1" }}>
                      {mrrSym}{Number(a.estimated_mrr || 0).toFixed(2)}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>
                      {fmtDate(periodEnd)}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>
                      {fmtDate(a.account_created_at)}
                    </td>
                    <td style={{ ...adminTdStyle, textAlign:"end" }}>
                      {onSetupFeed && (
                        <button onClick={() => onSetupFeed({ id: a.account_id, name: a.account_name || "" })}
                          style={{ ...btnSecondary, marginInlineEnd:6, marginBottom:4 }}>
                          {t("feed.accounts.setUp")}
                        </button>
                      )}
                      <button onClick={() => onInspect(a.account_id)} style={btnSecondary}>
                        {t("app.admin.inspect")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{
          padding:"11px 18px", display:"flex", justifyContent:"space-between",
          alignItems:"center", borderTop:"1px solid #f1f5f9", fontSize:12, color:"#64748b",
          background:"#fafbfc",
        }}>
          <span>
            <span className="num" style={{ fontWeight:600, color:"#0f172a" }}>{count.toLocaleString()}</span> {t("app.admin.totalSuffix")}
            {" "}{t("app.admin.showing")} <span className="num">{offset + 1}–{Math.min(offset + accounts.length, count)}</span>
          </span>
          <div style={{ display:"flex", gap:6 }}>
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
              style={btnPage(offset === 0)}>{t("app.admin.prev")}</button>
            <button disabled={offset + accounts.length >= count}
              onClick={() => setOffset(offset + limit)}
              style={btnPage(offset + accounts.length >= count)}>{t("app.admin.next")}</button>
          </div>
        </div>
      </AdminCard>
    </AdminPage>
  );
}


function renderStatusChip(status) {
  const map = {
    active:        ["#ecfdf5", "#047857", "#a7f3d0"],
    trialing:      ["#eff6ff", "#1e40af", "#bfdbfe"],
    past_due:      ["#fef2f2", "#991b1b", "#fecaca"],
    canceled:      ["#f1f5f9", "#475569", "#cbd5e1"],
    incomplete:    ["#fefce8", "#854d0e", "#fde68a"],
    free_override: ["#f5f3ff", "#5b21b6", "#ddd6fe"],
    paused:        ["#f1f5f9", "#475569", "#cbd5e1"],
  };
  const [bg, fg, bd] = map[status] || ["#f1f5f9", "#475569", "#cbd5e1"];
  return (
    <span style={{
      padding:"2px 8px", borderRadius:4, fontSize:10.5, fontWeight:700,
      background:bg, color:fg, border:`1px solid ${bd}`,
      textTransform:"capitalize", letterSpacing:".3px",
    }}>{(status || "—").replace(/_/g," ")}</span>
  );
}


// ─────────────────────────────────────────────────────────────────────
// SNAPSHOT (INSPECT) PANEL — read-only side drawer.
// ─────────────────────────────────────────────────────────────────────
function SnapshotPanel({ accountId, onClose }) {
  const { t } = useT();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [working, setWorking] = useState(false);
  const [reason, setReason]   = useState("");
  // Free-override expiry picker: "none" | "7" | "14" | "30" | "custom"
  const [expiryChoice, setExpiryChoice] = useState("7");
  const [expiryCustom, setExpiryCustom] = useState("");   // yyyy-mm-dd from <input type=date>
  const [extending, setExtending] = useState(false);      // extend form open on an active override
  // Pricing edit state — only one workspace's pricing form open at a time
  const [pricingEditId, setPricingEditId] = useState(null);
  const [pricingMode, setPricingMode]     = useState("standard"); // standard | free | custom
  const [pricingPence, setPricingPence]   = useState("");
  const [pricingReason, setPricingReason] = useState("");
  // New workspace form state
  const [creatingWs, setCreatingWs]       = useState(false);
  const [newWsName, setNewWsName]         = useState("");
  const [newWsMarket, setNewWsMarket]     = useState("uk");
  const [newWsCity, setNewWsCity]         = useState("");
  const [newWsArea, setNewWsArea]         = useState("");
  const [newWsPriceMode, setNewWsPriceMode]     = useState("standard");
  const [newWsPricePence, setNewWsPricePence]   = useState("");
  const [newWsPriceReason, setNewWsPriceReason] = useState("");

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const load = async () => {
    try {
      setLoading(true);
      const d = await adminFetch(`/api/admin/accounts/${accountId}/snapshot`);
      setData(d); setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  // Resolve the expiry picker into { ok, value } — value is an ISO datetime
  // or null for "no expiry". Never a bare sentinel: an invalid custom date
  // must not be mistakable for "no expiry" (which would grant permanently).
  const resolveExpiresAt = () => {
    if (expiryChoice === "none") return { ok: true, value: null };
    if (expiryChoice === "custom") {
      if (!expiryCustom) return { ok: false, value: null };
      const d = new Date(`${expiryCustom}T23:59:59`); // end of the chosen day, local time
      return Number.isNaN(d.getTime()) ? { ok: false, value: null } : { ok: true, value: d.toISOString() };
    }
    return { ok: true, value: new Date(Date.now() + Number(expiryChoice) * 86400000).toISOString() };
  };

  const toggleFree = async (enabled) => {
    if (!data) return;
    if (!enabled && !window.confirm("Revoke free override for this account? They keep their workspace and land on the subscribe paywall (unless they have their own live subscription).")) return;
    let expires_at = null;
    if (enabled) {
      const resolved = resolveExpiresAt();
      if (!resolved.ok) { alert(t("app.snap.expiryInvalid")); return; }
      expires_at = resolved.value;
      if (expires_at && new Date(expires_at).getTime() <= Date.now()) { alert(t("app.snap.expiryMustBeFuture")); return; }
    }
    setWorking(true);
    try {
      await adminFetch(`/api/admin/accounts/${accountId}/free-override`, {
        method: "POST",
        body: enabled
          ? { enabled, reason: reason.trim() || data?.account?.free_override_reason || undefined, expires_at }
          : { enabled },
      });
      await load();
      setReason("");
      setExtending(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  // Open the inline pricing form for a workspace, pre-filling with current override.
  const openPricingEdit = (w) => {
    setPricingEditId(w.id);
    if (w.price_override_pence === null || w.price_override_pence === undefined) {
      setPricingMode("standard"); setPricingPence("");
    } else if (w.price_override_pence === 0) {
      setPricingMode("free"); setPricingPence("");
    } else {
      setPricingMode("custom"); setPricingPence((w.price_override_pence / 100).toFixed(2));
    }
    setPricingReason(w.price_override_reason || "");
  };

  const cancelPricingEdit = () => {
    setPricingEditId(null); setPricingMode("standard"); setPricingPence(""); setPricingReason("");
  };

  // Save the pricing override on a workspace.
  const savePricing = async (workspaceId) => {
    let overridePence;
    if (pricingMode === "standard") overridePence = null;
    else if (pricingMode === "free") overridePence = 0;
    else {
      const parsed = parseFloat(pricingPence);
      if (isNaN(parsed) || parsed < 0) { alert("Enter a valid price in the major currency unit (e.g. 4.99 for £4.99)"); return; }
      overridePence = Math.round(parsed * 100);
    }
    setWorking(true);
    try {
      await adminFetch(`/api/admin/workspaces/${workspaceId}/price-override`, {
        method: "POST",
        body: { override_pence: overridePence, reason: pricingReason.trim() || undefined },
      });
      cancelPricingEdit();
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  // Create a workspace on behalf of this account.
  const submitNewWorkspace = async () => {
    if (!newWsName.trim()) { alert("Name is required"); return; }
    if (!newWsCity.trim()) { alert("City is required"); return; }
    let overridePence = null;
    if (newWsPriceMode === "free") overridePence = 0;
    else if (newWsPriceMode === "custom") {
      const parsed = parseFloat(newWsPricePence);
      if (isNaN(parsed) || parsed < 0) { alert("Enter a valid custom price"); return; }
      overridePence = Math.round(parsed * 100);
    }
    setWorking(true);
    try {
      await adminFetch(`/api/admin/accounts/${accountId}/workspaces`, {
        method: "POST",
        body: {
          name:   newWsName.trim(),
          market: newWsMarket,
          city:   newWsCity.trim(),
          area:   newWsArea.trim() || undefined,
          price_override_pence:  overridePence,
          price_override_reason: newWsPriceMode !== "standard" ? (newWsPriceReason.trim() || undefined) : undefined,
        },
      });
      setCreatingWs(false);
      setNewWsName(""); setNewWsCity(""); setNewWsArea("");
      setNewWsPriceMode("standard"); setNewWsPricePence(""); setNewWsPriceReason("");
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  // Format an override-pence value as a currency string for display.
  const fmtOverride = (pence, currency) => {
    const sym = currency === "AED" ? "AED " : currency === "USD" ? "$" : currency === "INR" ? "₹" : "£";
    return `${sym}${(pence / 100).toFixed(2)}/mo`;
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position:"fixed", inset:0, background:"rgba(15,23,42,.4)",
        zIndex:9999, transition:"opacity .15s",
      }}/>
      {/* Drawer */}
      <div style={{
        position:"fixed", top:0, insetInlineEnd:0, bottom:0, width:"min(600px, 100%)",
        background:"#fff", boxShadow:"-24px 0 60px rgba(15,23,42,.2)", zIndex:10000,
        overflowY:"auto", display:"flex", flexDirection:"column",
        borderInlineStart:"1px solid #e2e8f0",
      }}>
        {/* Header */}
        <div style={{
          background:"#0b1220", color:"#fff", padding:"16px 22px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          position:"sticky", top:0, zIndex:5, borderBottom:"1px solid #1e293b",
        }}>
          <div>
            <div style={{ fontSize:10.5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".7px", fontWeight:700 }}>
              {t("app.snap.inspectingReadOnly")}
            </div>
            <div style={{ fontSize:15, fontWeight:600, marginTop:2, letterSpacing:"-.15px" }}>
              {data?.account?.account_name || t("app.snap.loadingEllipsis")}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:"transparent", border:"1px solid #334155", color:"#cbd5e1",
            borderRadius:6, padding:"5px 11px", cursor:"pointer", fontSize:12,
            fontFamily:"inherit", fontWeight:500,
          }}>{t("app.snap.close")}</button>
        </div>

        {/* Read-only banner */}
        <div style={{
          background:"#fef3c7", borderBottom:"1px solid #fde68a",
          padding:"10px 22px", fontSize:11.5, color:"#854d0e", lineHeight:1.5,
        }}>
          <strong>{t("app.snap.readOnlyBannerStrong")}</strong>{t("app.snap.readOnlyBannerRest")}
        </div>

        {/* Body */}
        <div style={{ padding:20, background:"#fafbfc", flex:1 }}>
          {loading && <Loading/>}
          {error && <ErrorBlock msg={error}/>}
          {data && (
            <>
              <Section title={t("app.snap.owner")}>
                <KV k={t("app.snap.email")} v={data.owner.email}/>
                <KV k={t("app.snap.userCreated")} v={fmtDateTime(data.owner.created_at)}/>
                <KV k={t("app.snap.lastSignIn")} v={fmtDateTime(data.owner.last_sign_in_at)}/>
                <KV k={t("app.snap.userId")} v={<code style={codeStyle}>{data.owner.user_id}</code>}/>
              </Section>

              <Section title={t("app.snap.subscription")}>
                <KV k={t("app.snap.status")} v={renderStatusChip(data.account.subscription_status)}/>
                <KV k={t("app.snap.plan")} v={data.account.subscription_plan}/>
                <KV k={t("app.snap.currency")} v={data.account.billing_currency}/>
                <KV k={t("app.snap.trialEnds")} v={fmtDateTime(data.account.trial_ends_at)}/>
                <KV k={t("app.snap.currentPeriodEnds")} v={fmtDateTime(data.account.current_period_end)}/>
                <KV k={t("app.snap.cancelAtPeriodEnd")} v={data.account.cancel_at_period_end ? t("app.snap.yes") : t("app.snap.no")}/>
                <KV k={t("app.snap.hasActiveAccess")} v={data.account.has_active_access ? t("app.snap.yes") : t("app.snap.no")}/>
                <KV k={t("app.snap.archived")} v={data.account.archived_at ? fmtDateTime(data.account.archived_at) : t("app.snap.no")}/>
                {data.stripe?.customer_id && (
                  <KV k={t("app.snap.stripeCustomer")} v={<code style={codeStyle}>{data.stripe.customer_id}</code>}/>
                )}
                {data.stripe?.subscription_id && (
                  <KV k={t("app.snap.stripeSubscription")} v={<code style={codeStyle}>{data.stripe.subscription_id}</code>}/>
                )}
              </Section>

              <Section title={t("app.snap.freeOverride")}>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12, lineHeight:1.5 }}>
                  {t("app.snap.freeOverrideDesc")}
                </div>
                {(() => {
                  const expired = isOverrideExpired(data.account);
                  const expiresAt = data.account.free_override_expires_at;
                  const expiryPicker = (
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
                      <span style={{ fontSize:12, color:"#475569", fontWeight:600 }}>{t("app.snap.freeOverrideExpiryLabel")}</span>
                      <select
                        value={expiryChoice} onChange={e => setExpiryChoice(e.target.value)}
                        style={{ ...inputStyle, width:"auto", padding:"7px 10px" }}
                      >
                        <option value="7">{t("app.snap.expiry7")}</option>
                        <option value="14">{t("app.snap.expiry14")}</option>
                        <option value="30">{t("app.snap.expiry30")}</option>
                        <option value="custom">{t("app.snap.expiryCustom")}</option>
                        <option value="none">{t("app.snap.expiryNone")}</option>
                      </select>
                      {expiryChoice === "custom" && (
                        <input
                          type="date" value={expiryCustom}
                          onChange={e => setExpiryCustom(e.target.value)}
                          style={{ ...inputStyle, width:"auto", padding:"7px 10px" }}
                        />
                      )}
                    </div>
                  );
                  return data.account.free_override ? (
                    <div>
                      <div style={{
                        padding:"10px 14px",
                        background: expired ? "#fffbeb" : "#f5f3ff",
                        color: expired ? "#92400e" : "#5b21b6",
                        borderRadius:7, fontSize:12.5, marginBottom:12,
                        border: expired ? "1px solid #fde68a" : "1px solid #ddd6fe",
                      }}>
                        <div style={{ fontWeight:700, marginBottom:2 }}>
                          {expired ? t("app.snap.freeOverrideExpiredTitle") : t("app.snap.freeOverrideActive")}
                        </div>
                        <div style={{ fontWeight:400, color: expired ? "#b45309" : "#6d28d9" }}>
                          {expiresAt
                            ? `${expired ? t("app.snap.freeOverrideExpiredPrefix") : t("app.snap.freeOverrideExpiresPrefix")} ${fmtDateTime(expiresAt)}`
                            : t("app.snap.freeOverrideNoExpiry")}
                        </div>
                        {data.account.free_override_reason && (
                          <div style={{ fontWeight:400, color: expired ? "#b45309" : "#6d28d9" }}>
                            {t("app.snap.reasonPrefix")} {data.account.free_override_reason}
                          </div>
                        )}
                      </div>
                      {extending ? (
                        <div>
                          {expiryPicker}
                          <div style={{ display:"flex", gap:8 }}>
                            <button disabled={working} onClick={() => toggleFree(true)} style={btnPrimaryDark}>
                              {working ? t("app.snap.working") : t("app.snap.updateExpiry")}
                            </button>
                            <button disabled={working} onClick={() => setExtending(false)} style={btnSecondary}>
                              {t("app.snap.cancelBtn")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          <button disabled={working} onClick={() => setExtending(true)} style={btnPrimaryDark}>
                            {t("app.snap.extendFreeOverride")}
                          </button>
                          <button disabled={working} onClick={() => toggleFree(false)} style={btnDanger}>
                            {working ? t("app.snap.working") : t("app.snap.revokeFreeOverride")}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <input
                        value={reason} onChange={e => setReason(e.target.value)}
                        placeholder={t("app.snap.freeOverrideReasonPlaceholder")}
                        style={{ ...inputStyle, width:"100%", marginBottom:10 }}
                      />
                      {expiryPicker}
                      <button disabled={working} onClick={() => toggleFree(true)} style={btnPrimaryDark}>
                        {working ? t("app.snap.working") : t("app.snap.grantFreeOverride")}
                      </button>
                    </div>
                  );
                })()}
              </Section>

              <div style={{ marginBottom:18 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, margin:"4px 0 12px" }}>
                  <h2 style={{ fontSize:12.5, fontWeight:700, color:"#475569", margin:0, textTransform:"uppercase", letterSpacing:0.6 }}>
                    {t("app.snap.workspaces")} ({data.workspaces.length})
                  </h2>
                  <div style={{ flex:1, height:1, background:"#e2e8f0" }}/>
                  {!creatingWs && (
                    <button onClick={() => setCreatingWs(true)} style={{
                      padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0",
                      background:"#fff", color:"#0f172a", cursor:"pointer", fontSize:11.5,
                      fontFamily:"inherit", fontWeight:600,
                    }}>{t("app.snap.newWorkspace")}</button>
                  )}
                </div>

                {/* New workspace form */}
                {creatingWs && (
                  <div style={{
                    padding:"14px 14px 12px", border:"1px solid #c7d2fe", borderRadius:7,
                    background:"#eef2ff", marginBottom:10,
                  }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"#3730a3", textTransform:"uppercase", letterSpacing:".6px", marginBottom:10 }}>
                      {t("app.snap.createWorkspaceOnBehalf")}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                      <input value={newWsName} onChange={e => setNewWsName(e.target.value)} placeholder={t("app.snap.wsNamePlaceholder")} style={inputStyle}/>
                      <select value={newWsMarket} onChange={e => setNewWsMarket(e.target.value)} style={selectStyleAdmin}>
                        <option value="uk">{t("app.snap.marketUk")}</option>
                        <option value="uae">{t("app.snap.marketUae")}</option>
                        <option value="us">{t("app.snap.marketUs")}</option>
                        <option value="in">{t("app.snap.marketIn")}</option>
                      </select>
                      <input value={newWsCity} onChange={e => setNewWsCity(e.target.value)} placeholder={t("app.snap.cityPlaceholder")} style={inputStyle}/>
                      <input value={newWsArea} onChange={e => setNewWsArea(e.target.value)} placeholder={t("app.snap.areaPlaceholder")} style={inputStyle}/>
                    </div>

                    {/* Pricing for the new workspace */}
                    <div style={{ fontSize:11, fontWeight:700, color:"#3730a3", textTransform:"uppercase", letterSpacing:".6px", marginTop:14, marginBottom:8 }}>
                      {t("app.snap.pricing")}
                    </div>
                    <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
                      {[
                        ["standard", t("app.snap.standardRate")],
                        ["free",     t("app.snap.freeComp")],
                        ["custom",   t("app.snap.customPrice")],
                      ].map(([m, label]) => (
                        <button key={m} onClick={() => setNewWsPriceMode(m)} style={{
                          padding:"6px 12px", borderRadius:6,
                          border:newWsPriceMode === m ? "1px solid #4f46e5" : "1px solid #e2e8f0",
                          background:newWsPriceMode === m ? "#4f46e5" : "#fff",
                          color:newWsPriceMode === m ? "#fff" : "#475569",
                          cursor:"pointer", fontSize:11.5, fontWeight:600, fontFamily:"inherit",
                        }}>{label}</button>
                      ))}
                    </div>
                    {newWsPriceMode === "custom" && (
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <span style={{ fontSize:13, color:"#475569", fontWeight:600 }}>
                          {newWsMarket === "uae" ? "AED" : newWsMarket === "us" ? "$" : newWsMarket === "in" ? "₹" : "£"}
                        </span>
                        <input value={newWsPricePence} onChange={e => setNewWsPricePence(e.target.value)}
                          placeholder={t("app.snap.pricePlaceholder")} inputMode="decimal" style={{ ...inputStyle, maxWidth:140 }}/>
                        <span style={{ fontSize:12, color:"#94a3b8" }}>{t("app.snap.perMonthSlash")}</span>
                      </div>
                    )}
                    {newWsPriceMode !== "standard" && (
                      <input value={newWsPriceReason} onChange={e => setNewWsPriceReason(e.target.value)}
                        placeholder={t("app.snap.newWsReasonPlaceholder")} style={{ ...inputStyle, width:"100%", marginBottom:10 }}/>
                    )}
                    <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                      <button onClick={() => setCreatingWs(false)} style={btnSecondary} disabled={working}>{t("app.team.cancel")}</button>
                      <button onClick={submitNewWorkspace} style={btnPrimaryDark} disabled={working}>
                        {working ? t("app.snap.creating") : t("app.snap.createWorkspace")}
                      </button>
                    </div>
                  </div>
                )}

                {data.workspaces.length === 0 && !creatingWs && (
                  <div style={{ fontSize:12.5, color:"#94a3b8", padding:"16px", background:"#fafbfc", border:"1px solid #e2e8f0", borderRadius:7 }}>
                    {t("app.snap.noWorkspacesClickNew1")}<b>{t("app.snap.newWorkspace")}</b>{t("app.snap.noWorkspacesClickNew2")}
                  </div>
                )}

                {data.workspaces.map(w => {
                  const hasOverride = w.price_override_pence !== null && w.price_override_pence !== undefined;
                  const isFree   = hasOverride && w.price_override_pence === 0;
                  const isCustom = hasOverride && w.price_override_pence > 0;
                  const editing  = pricingEditId === w.id;
                  return (
                    <div key={w.id} style={{
                      padding:"12px 14px", border:"1px solid #e2e8f0", borderRadius:7,
                      marginBottom:8, background: w.archived_at ? "#fafbfc" : "#fff",
                      borderInlineStart: hasOverride ? `3px solid ${isFree ? "#7c3aed" : "#2563eb"}` : "1px solid #e2e8f0",
                    }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontWeight:600, color:"#0f172a", fontSize:13.5 }}>{w.name}</span>
                          {w.archived_at && (
                            <span style={{ fontSize:9.5, padding:"1px 5px", background:"#f1f5f9", color:"#64748b", border:"1px solid #e2e8f0", borderRadius:3, fontWeight:700, letterSpacing:".3px" }}>
                              {t("app.snap.badgeArchived")}
                            </span>
                          )}
                          {w.cancel_at_period_end && !w.archived_at && (
                            <span style={{ fontSize:9.5, padding:"1px 5px", background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a", borderRadius:3, fontWeight:700, letterSpacing:".3px" }}>
                              {t("app.snap.badgeCancelPending")}
                            </span>
                          )}
                        </div>
                        <div className="num" style={{ fontSize:11, color:"#94a3b8" }}>
                          {w.market} · {w.currency}
                        </div>
                      </div>
                      {(w.city || w.area) && (
                        <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>
                          {[w.city, w.area].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      <div style={{ display:"flex", gap:5, marginTop:8, flexWrap:"wrap" }}>
                        {(w.channels || []).map(ch => (
                          <span key={ch.id} style={{
                            padding:"2px 7px", borderRadius:3, background:"#f1f5f9",
                            color:"#475569", fontSize:10.5, fontWeight:600,
                          }}>{ch.platform}</span>
                        ))}
                      </div>
                      <div className="num" style={{ fontSize:10.5, color:"#cbd5e1", marginTop:8 }}>
                        {w.menu_items_count} {t("app.snap.menuSuffix")} · {w.daily_logs_count} {t("app.snap.logsSuffix")}
                        {w.last_daily_log && ` · ${t("app.snap.lastPrefix")} ${fmtDate(w.last_daily_log)}`}
                      </div>

                      {/* Pricing strip */}
                      <div style={{
                        marginTop:10, paddingTop:10, borderTop:"1px dashed #e2e8f0",
                        display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
                      }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:10.5, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".6px", fontWeight:700, marginBottom:3 }}>
                            {t("app.snap.pricing")}
                          </div>
                          {!hasOverride && (
                            <div style={{ fontSize:12.5, color:"#475569" }}>{t("app.snap.standardRateCard")}</div>
                          )}
                          {isFree && (
                            <div>
                              <span style={{ fontSize:12.5, fontWeight:700, color:"#5b21b6", padding:"2px 7px", background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:4, letterSpacing:".3px" }}>{t("app.snap.badgeFree")}</span>
                              {w.price_override_reason && (
                                <span style={{ fontSize:12, color:"#64748b", marginInlineStart:8 }}>· {w.price_override_reason}</span>
                              )}
                            </div>
                          )}
                          {isCustom && (
                            <div>
                              <span className="num" style={{ fontSize:13, fontWeight:700, color:"#1e40af", padding:"2px 7px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:4 }}>
                                {fmtOverride(w.price_override_pence, w.currency)}
                              </span>
                              {w.price_override_reason && (
                                <span style={{ fontSize:12, color:"#64748b", marginInlineStart:8 }}>· {w.price_override_reason}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {!editing && (
                          <button onClick={() => openPricingEdit(w)} style={{
                            padding:"4px 10px", borderRadius:5, border:"1px solid #e2e8f0",
                            background:"#fff", color:"#475569", cursor:"pointer", fontSize:11.5,
                            fontFamily:"inherit", fontWeight:600, flexShrink:0,
                          }}>{hasOverride ? t("app.snap.edit") : t("app.snap.setOverride")}</button>
                        )}
                      </div>

                      {/* Inline pricing edit form */}
                      {editing && (
                        <div style={{
                          marginTop:10, padding:"12px", background:"#f8fafc",
                          border:"1px solid #e2e8f0", borderRadius:6,
                        }}>
                          <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
                            {[
                              ["standard", t("app.snap.standardRate")],
                              ["free",     t("app.snap.free")],
                              ["custom",   t("app.snap.customPrice")],
                            ].map(([m, label]) => (
                              <button key={m} onClick={() => setPricingMode(m)} style={{
                                padding:"5px 11px", borderRadius:5,
                                border:pricingMode === m ? "1px solid #0f172a" : "1px solid #e2e8f0",
                                background:pricingMode === m ? "#0f172a" : "#fff",
                                color:pricingMode === m ? "#fff" : "#475569",
                                cursor:"pointer", fontSize:11.5, fontWeight:600, fontFamily:"inherit",
                              }}>{label}</button>
                            ))}
                          </div>
                          {pricingMode === "custom" && (
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                              <span style={{ fontSize:13, color:"#475569", fontWeight:600 }}>
                                {w.currency === "AED" ? "AED" : w.currency === "USD" ? "$" : w.currency === "INR" ? "₹" : "£"}
                              </span>
                              <input value={pricingPence} onChange={e => setPricingPence(e.target.value)}
                                placeholder={t("app.snap.pricePlaceholder")} inputMode="decimal" style={{ ...inputStyle, maxWidth:140 }}/>
                              <span style={{ fontSize:12, color:"#94a3b8" }}>{t("app.snap.perMonthSlash")}</span>
                            </div>
                          )}
                          {pricingMode !== "standard" && (
                            <input value={pricingReason} onChange={e => setPricingReason(e.target.value)}
                              placeholder={t("app.snap.editReasonPlaceholder")}
                              style={{ ...inputStyle, width:"100%", marginBottom:10 }}/>
                          )}
                          <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                            <button onClick={cancelPricingEdit} style={btnSecondary} disabled={working}>{t("app.team.cancel")}</button>
                            <button onClick={() => savePricing(w.id)} style={btnPrimaryDark} disabled={working}>
                              {working ? t("app.snap.saving") : t("app.snap.savePricing")}
                            </button>
                          </div>
                          <div style={{ fontSize:11, color:"#94a3b8", marginTop:8, lineHeight:1.5 }}>
                            {t("app.snap.overrideAuditNote")}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Section title={`${t("app.snap.team")} (${data.members.length})`}>
                {data.members.length === 0 && (
                  <div style={{ fontSize:12.5, color:"#94a3b8" }}>{t("app.snap.noTeamMembers")}</div>
                )}
                {data.members.map((m, i) => (
                  <div key={m.member_id} style={{
                    padding:"10px 0", borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                  }}>
                    <div>
                      <div style={{ fontSize:13, color:"#0f172a", fontWeight:500 }}>{m.email || "—"}</div>
                      <div className="num" style={{ fontSize:10.5, color:"#94a3b8", marginTop:1 }}>
                        {t("app.snap.lastSignInPrefix")} {fmtDateTime(m.last_sign_in_at) || t("app.snap.never")}
                      </div>
                    </div>
                    <span style={{
                      padding:"2px 8px", borderRadius:4, fontSize:10.5, fontWeight:700,
                      background: m.role === "brand_owner" ? "#eff6ff" : "#f1f5f9",
                      color: m.role === "brand_owner" ? "#1e40af" : "#475569",
                      border: m.role === "brand_owner" ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                      letterSpacing:".3px",
                    }}>{m.role}</span>
                  </div>
                ))}
              </Section>
            </>
          )}
        </div>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────
// AUDIT LOG TAB
// ─────────────────────────────────────────────────────────────────────
function AuditTab() {
  const { t } = useT();
  const [entries, setEntries] = useState([]);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [offset, setOffset]   = useState(0);
  const [action, setAction]   = useState("");
  const limit = 100;

  const refresh = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (action) params.set("action", action);
      const data = await adminFetch(`/api/admin/audit-log?${params.toString()}`);
      setEntries(data.entries || []);
      setCount(data.count || 0);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [offset, action]);

  // Client-side per-column sort + filter over the current page of audit rows.
  const auditCols = [
    {key:"when",    label:t("app.audit.colWhen"),    type:"date",   value:e=>e.created_at, align:"right"},
    {key:"actor",   label:t("app.audit.colActor"),   type:"text",   value:e=>e.actor_email||e.actor_user_id||"", align:"left"},
    {key:"action",  label:t("app.audit.colAction"),  type:"text",   value:e=>e.action||"", align:"left"},
    {key:"target",  label:t("app.audit.colTarget"),  type:"text",   value:e=>e.target_email||e.target_id||"", align:"left"},
    {key:"details", label:t("app.audit.colDetails"), type:"text",   value:e=>e.details?JSON.stringify(e.details):"", align:"left"},
    {key:"ip",      label:t("app.audit.colIp"),      type:"text",   value:e=>e.ip_address||"", align:"left"},
  ];
  const auditTable = useTableSortFilter(entries, auditCols);

  return (
    <AdminPage>
      <AdminPageHeader
        icon={ADMIN_PAGE_ICONS.audit}
        title={t("app.audit.title")}
        description={t("app.audit.desc")}
        right={<button onClick={refresh} style={btnSecondary}>{t("app.audit.refresh")}</button>}
      />

      <AdminCard>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", background:"#fafbfc" }}>
          <select value={action} onChange={e => { setAction(e.target.value); setOffset(0); }} style={selectStyleAdmin}>
            <option value="">{t("app.audit.allActions")}</option>
            <option value="view_snapshot">{t("app.audit.actionViewSnapshot")}</option>
            <option value="free_override_grant">{t("app.audit.actionGrantOverride")}</option>
            <option value="free_override_revoke">{t("app.audit.actionRevokeOverride")}</option>
            <option value="add_super_admin">{t("app.audit.actionAddSuperAdmin")}</option>
            <option value="remove_super_admin">{t("app.audit.actionRemoveSuperAdmin")}</option>
          </select>
        </div>

        {error && <div style={{ padding:"12px 20px", color:"#991b1b", fontSize:13, background:"#fef2f2", borderBottom:"1px solid #fecaca" }}>{t("app.audit.errorPrefix")} {error}</div>}
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#fafbfc" }}>
                {auditCols.map(sc => (
                  <SortableTh key={sc.key} col={sc} table={auditTable} thStyle={adminThStyle} align={sc.align}/>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.audit.loadingEllipsis")}</td></tr>}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={6} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.audit.noEntries")}</td></tr>
              )}
              {!loading && entries.length > 0 && auditTable.rows.length === 0 && (
                <tr><td colSpan={6} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.audit.noRowsMatch")}</td></tr>
              )}
              {!loading && auditTable.rows.map((e, i) => (
                <tr key={e.id} style={{
                  borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                  transition:"background .12s",
                }}
                  onMouseEnter={ev => ev.currentTarget.style.background = "#fafbfc"}
                  onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}
                >
                  <td className="num" style={{ ...adminTdStyle, fontSize:11, color:"#64748b" }}>
                    {fmtDateTime(e.created_at)}
                  </td>
                  <td style={{ ...adminTdStyle, fontSize:12, color:"#0f172a" }}>{e.actor_email || (e.actor_user_id?.slice(0,8) || "—")}</td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:11 }}>{e.action}</td>
                  <td style={{ ...adminTdStyle, fontSize:12 }}>
                    {e.target_email || (e.target_id ? e.target_id.slice(0,8) : "—")}
                    {e.target_type && (
                      <span style={{ fontSize:10, color:"#94a3b8", marginInlineStart:5 }}>({e.target_type})</span>
                    )}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:10.5, color:"#64748b", maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {e.details && Object.keys(e.details).length > 0
                      ? JSON.stringify(e.details)
                      : "—"}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:11, color:"#94a3b8" }}>
                    {e.ip_address || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{
          padding:"11px 18px", display:"flex", justifyContent:"space-between",
          alignItems:"center", borderTop:"1px solid #f1f5f9", fontSize:12, color:"#64748b",
          background:"#fafbfc",
        }}>
          <span><span className="num" style={{ fontWeight:600, color:"#0f172a" }}>{count.toLocaleString()}</span> {t("app.audit.entriesTotalSuffix")}</span>
          <div style={{ display:"flex", gap:6 }}>
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
              style={btnPage(offset === 0)}>{t("app.audit.prev")}</button>
            <button disabled={offset + entries.length >= count}
              onClick={() => setOffset(offset + limit)}
              style={btnPage(offset + entries.length >= count)}>{t("app.audit.next")}</button>
          </div>
        </div>
      </AdminCard>
    </AdminPage>
  );
}


// ─────────────────────────────────────────────────────────────────────
// SUPER ADMINS TAB
// ─────────────────────────────────────────────────────────────────────
function SuperAdminsTab() {
  const { t } = useT();
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [email, setEmail]     = useState("");
  const [notes, setNotes]     = useState("");
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await adminFetch("/api/admin/super-admins");
      setAdmins(data.super_admins || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { alert("Invalid email"); return; }
    setWorking(true);
    try {
      await adminFetch("/api/admin/super-admins", { method: "POST", body: { email: e, notes: notes.trim() || undefined } });
      setEmail(""); setNotes("");
      await refresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  const remove = async (userId, e) => {
    if (!window.confirm(`Revoke super admin access for ${e || userId}? They'll lose access to this panel immediately.`)) return;
    setWorking(true);
    try {
      await adminFetch(`/api/admin/super-admins/${userId}`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <AdminPage>
      <AdminPageHeader
        icon={ADMIN_PAGE_ICONS.super_admins}
        title={t("app.sadmin.title")}
        description={t("app.sadmin.desc")}
      />

      <AdminSectionLabel>{t("app.sadmin.addSuperAdmin")}</AdminSectionLabel>
      <AdminCard style={{ marginBottom:24, padding:18 }}>
        <div style={{ fontSize:12, color:"#64748b", marginBottom:12, lineHeight:1.5 }}>
          {t("app.sadmin.mustHaveAccount")}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" style={{ ...inputStyle, flex:"1 1 240px" }}/>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder={t("app.sadmin.notesPlaceholder")} style={{ ...inputStyle, flex:"1 1 180px" }}/>
          <button disabled={working || !email.trim()} onClick={add} style={btnPrimaryDark}>
            {working ? t("app.sadmin.working") : t("app.sadmin.addSuperAdminBtn")}
          </button>
        </div>
      </AdminCard>

      <AdminSectionLabel>{t("app.sadmin.currentSuperAdmins")}</AdminSectionLabel>
      <AdminCard>
        {error && <div style={{ padding:"12px 20px", color:"#991b1b", fontSize:13, background:"#fef2f2", borderBottom:"1px solid #fecaca" }}>{t("app.sadmin.errorPrefix")} {error}</div>}

        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#fafbfc" }}>
              {[t("app.sadmin.colEmail"),t("app.sadmin.colLastSignIn"),t("app.sadmin.colAdded"),t("app.sadmin.colNotes"),""].map(h => <th key={h} style={adminThStyle}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.sadmin.loadingEllipsis")}</td></tr>}
            {!loading && admins.length === 0 && (
              <tr><td colSpan={5} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.sadmin.noSuperAdmins")}</td></tr>
            )}
            {!loading && admins.map((a, i) => (
              <tr key={a.user_id} style={{
                borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                transition:"background .12s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = "#fafbfc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <td style={{ ...adminTdStyle, fontSize:13, color:"#0f172a", fontWeight:500 }}>{a.email || a.user_id}</td>
                <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>{fmtDateTime(a.last_sign_in_at) || t("app.sadmin.never")}</td>
                <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>{fmtDate(a.added_at)}</td>
                <td style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>{a.notes || "—"}</td>
                <td style={{ ...adminTdStyle, textAlign:"end" }}>
                  <button onClick={() => remove(a.user_id, a.email)}
                    style={btnGhostDanger} disabled={working}>{t("app.sadmin.revoke")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>
    </AdminPage>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// TEAM PAGE — Brand owners manage operators here
// ═══════════════════════════════════════════════════════════════════════
function TeamPage({ workspaces, account, teamContext, myAccounts=[] }) {
  const { t } = useT();
  const [members, setMembers]     = useState([]);
  const [invites, setInvites]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error,   setError]       = useState(null);
  const [working, setWorking]     = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail]           = useState("");
  const [inviteWsIds, setInviteWsIds]           = useState([]);   // workspace ids the new operator can access
  const [inviteResult, setInviteResult]         = useState(null); // last successful invite — show URL for copy

  // Per-member access editor
  const [editingMemberId, setEditingMemberId]   = useState(null);
  const [editingAccess,   setEditingAccess]     = useState([]);

  const isBrandOwner = teamContext?.role === "brand_owner";

  const refresh = async () => {
    setLoading(true);
    try {
      const calls = [adminFetch("/api/team/members")];
      if (isBrandOwner) calls.push(adminFetch("/api/team/invites"));
      const results = await Promise.all(calls);
      setMembers(results[0].members || []);
      setInvites(results[1]?.invites || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Active (non-archived) workspaces — these are the ones an operator could be assigned to.
  const wsList = Object.values(workspaces || {}).filter(w => !w.archived_at);

  // Lookup helper for workspace name by id
  const wsName = (id) => wsList.find(w => w.id === id)?.name || id.slice(0, 8);

  const toggleInviteWs = (id) => {
    setInviteWsIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const sendInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { alert("Enter a valid email"); return; }
    if (inviteWsIds.length === 0) { alert("Pick at least one workspace for this operator"); return; }
    setWorking(true);
    try {
      const result = await adminFetch("/api/team/invites", {
        method: "POST",
        body: { email, location_access: inviteWsIds },
      });
      setInviteResult(result);
      setInviteEmail(""); setInviteWsIds([]);
      await refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  const revokeInvite = async (id, email) => {
    if (!window.confirm(`Revoke pending invite for ${email}?`)) return;
    setWorking(true);
    try {
      await adminFetch(`/api/team/invites/${id}`, { method: "DELETE" });
      await refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  const resendInvite = async (id) => {
    setWorking(true);
    try {
      const r = await adminFetch(`/api/team/invites/${id}/resend`, { method: "POST" });
      if (r.email_sent) {
        alert("Invite email re-sent.");
      } else {
        // Couldn't send — copy URL to clipboard automatically as a fallback.
        await doCopy(r.invite_url, `resend-${id}`);
        alert("Couldn't send the email — the invite link has been copied to your clipboard.");
      }
      await refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  // Robust clipboard copy with visible feedback. Tries the modern API first,
  // falls back to a hidden textarea + execCommand for older browsers or when
  // navigator.clipboard is unavailable (e.g. in some embedded contexts).
  // Returns true on success. Sets `copiedKey` to flip the button label briefly.
  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = async (url, key = "_") => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch (_) { ok = false; }
    if (!ok) {
      // Fallback: hidden textarea + execCommand. Works in older browsers and
      // in cases where the clipboard API silently rejects.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { ok = false; }
    }
    if (ok) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(prev => prev === key ? null : prev), 1600);
    } else {
      // Last-ditch fallback — show the URL so the user can copy manually.
      window.prompt("Copy this invite link:", url);
    }
  };

  const removeMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this team? They'll lose access immediately.`)) return;
    setWorking(true);
    try {
      await adminFetch(`/api/team/members/${userId}`, { method: "DELETE" });
      await refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  const openAccessEditor = (m) => {
    setEditingMemberId(m.user_id);
    setEditingAccess(Array.isArray(m.location_access) ? m.location_access : []);
  };
  const toggleEditingWs = (id) => {
    setEditingAccess(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const cancelAccessEdit = () => { setEditingMemberId(null); setEditingAccess([]); };
  const saveAccess = async () => {
    if (editingAccess.length === 0) { alert("Operators must have at least one workspace. Remove the member instead if they shouldn't have any."); return; }
    setWorking(true);
    try {
      await adminFetch(`/api/team/members/${editingMemberId}/access`, {
        method: "PUT",
        body: { location_access: editingAccess },
      });
      cancelAccessEdit();
      await refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <AdminPage><Loading/></AdminPage>;

  return (
    <AdminPage>
      <AdminPageHeader
        icon={ADMIN_PAGE_ICONS.accounts}
        title={t("app.team.title")}
        description={isBrandOwner
          ? t("app.team.descOwner")
          : t("app.team.descOperator")}
        right={<button onClick={refresh} style={btnSecondary}>{t("app.team.refresh")}</button>}
      />

      {error && <ErrorBlock msg={error}/>}

      {/* MULTI-ACCOUNT: all teams this user belongs to (shown when in >1 account) */}
      {Array.isArray(myAccounts) && myAccounts.length > 1 && (
        <>
          <AdminSectionLabel>{t("app.team.teamsYouBelongTo")}</AdminSectionLabel>
          <AdminCard style={{ marginBottom:24, padding:0, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#3b5bdb", color:"#fff", textAlign:"start" }}>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>{t("app.team.colBrandAccount")}</th>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>{t("app.team.colYourRole")}</th>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>{t("app.team.colInvitedBy")}</th>
                  <th style={{ padding:"10px 14px", fontWeight:600 }}>{t("app.team.colWorkspaces")}</th>
                </tr>
              </thead>
              <tbody>
                {myAccounts.map((a, i) => (
                  <tr key={a.account_id} style={{ background: i % 2 ? "#f6f8ff" : "#fff", borderTop:"1px solid #eef1f4" }}>
                    <td style={{ padding:"10px 14px", fontWeight:600, color:"#0f172a" }}>
                      {a.account_name}
                      {a.account_id === teamContext?.account_id && (
                        <span style={{ marginInlineStart:8, fontSize:10.5, fontWeight:700, color:"#0ca678", background:"#e6f7f1", padding:"2px 7px", borderRadius:6 }}>{t("app.team.badgeActive")}</span>
                      )}
                      {a.is_archived && (
                        <span style={{ marginInlineStart:8, fontSize:10.5, fontWeight:700, color:"#e03131", background:"#fff0f0", padding:"2px 7px", borderRadius:6 }}>{t("app.team.badgeArchived")}</span>
                      )}
                    </td>
                    <td style={{ padding:"10px 14px", color:"#475569", textTransform:"capitalize" }}>{(a.role || "").replace("_", " ")}</td>
                    <td style={{ padding:"10px 14px", color:"#475569" }}>{a.invited_by_email || "—"}</td>
                    <td style={{ padding:"10px 14px", color:"#475569" }}>
                      {a.role === "brand_owner"
                        ? t("app.team.all")
                        : (Array.isArray(a.location_access) ? `${a.location_access.length}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminCard>
          <div style={{ fontSize:11.5, color:"#94a3b8", marginTop:-14, marginBottom:24 }}>
            {t("app.team.switchBrandsNote")}
          </div>
        </>
      )}

      {/* OPERATOR CONTEXT PANEL — shows operators their team, inviter, and workspaces */}
      {!isBrandOwner && teamContext?.role === "operator" && (
        <AdminCard style={{ marginBottom:24, padding:18, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
          <div style={{ fontSize:11.5, fontWeight:700, color:"#475569", marginBottom:12, textTransform:"uppercase", letterSpacing:".5px" }}>
            {t("app.team.yourMembership")}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:14 }}>
            <div>
              <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginBottom:3 }}>{t("app.team.labelTeam")}</div>
              <div style={{ fontSize:14, fontWeight:600, color:"#0f172a" }}>{teamContext?.account_name || "—"}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginBottom:3 }}>{t("app.team.labelInvitedBy")}</div>
              <div style={{ fontSize:14, fontWeight:600, color:"#0f172a" }}>{teamContext?.invited_by_email || "—"}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginBottom:3 }}>{t("app.team.labelYourRole")}</div>
              <div style={{ fontSize:14, fontWeight:600, color:"#0f172a" }}>{t("app.team.roleOperator")}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginBottom:3 }}>{t("app.team.labelWorkspacesAccess")}</div>
              <div style={{ fontSize:14, fontWeight:600, color:"#0f172a" }}>
                {Array.isArray(teamContext?.location_access) && teamContext.location_access.length > 0
                  ? teamContext.location_access
                      .map(id => (workspaces.find(w => w.id === id)?.name) || null)
                      .filter(Boolean)
                      .join(", ") || `${teamContext.location_access.length} workspace(s)`
                  : "—"}
              </div>
            </div>
          </div>
          <div style={{ fontSize:11.5, color:"#94a3b8", marginTop:12 }}>
            {t("app.team.needAccessPrefix")}{teamContext?.invited_by_email || t("app.team.needAccessFallback")}{t("app.team.needAccessSuffix")}
          </div>
        </AdminCard>
      )}

      {/* INVITE FORM (brand owner only) */}
      {isBrandOwner && (
        <>
          <AdminSectionLabel>{t("app.team.inviteOperator")}</AdminSectionLabel>
          <AdminCard style={{ marginBottom:24, padding:18 }}>
            <div style={{ fontSize:12.5, color:"#64748b", marginBottom:12, lineHeight:1.5 }}>
              {t("app.team.inviteDesc")}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:14 }}>
              <input
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder={t("app.team.operatorEmailPlaceholder")}
                type="email"
                style={{ ...inputStyle, flex:"1 1 240px" }}
              />
            </div>
            <div style={{ fontSize:11.5, color:"#475569", fontWeight:600, marginBottom:8, textTransform:"uppercase", letterSpacing:".5px" }}>
              {t("app.team.workspacesTheyAccess")}
            </div>
            {wsList.length === 0 ? (
              <div style={{ fontSize:12.5, color:"#94a3b8", padding:"10px 0" }}>
                {t("app.team.noActiveWorkspacesAssign")}
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:8, marginBottom:14 }}>
                {wsList.map(w => (
                  <label key={w.id} style={{
                    display:"flex", alignItems:"center", gap:9, padding:"9px 12px",
                    background:inviteWsIds.includes(w.id) ? "#eff6ff" : "#fafbfc",
                    border:inviteWsIds.includes(w.id) ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                    borderRadius:7, cursor:"pointer", fontSize:13,
                  }}>
                    <input
                      type="checkbox"
                      checked={inviteWsIds.includes(w.id)}
                      onChange={() => toggleInviteWs(w.id)}
                      style={{ accentColor:"#1e40af" }}
                    />
                    <span style={{ fontWeight:600, color:"#0f172a" }}>{w.name}</span>
                    <span style={{ fontSize:11, color:"#94a3b8", marginInlineStart:"auto" }}>
                      {w.city || ""}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <button onClick={sendInvite} disabled={working || !inviteEmail.trim() || inviteWsIds.length === 0}
              style={{ ...btnPrimaryDark, opacity: (working || !inviteEmail.trim() || inviteWsIds.length === 0) ? .5 : 1 }}>
              {working ? t("app.team.sending") : t("app.team.sendInvite")}
            </button>

            {inviteResult && (
              <div style={{
                marginTop:14, padding:"12px 14px", background:"#f0fdf4", border:"1px solid #86efac",
                borderRadius:7, fontSize:12.5, color:"#15803d",
              }}>
                <div style={{ fontWeight:700, marginBottom:6 }}>
                  {t("app.team.inviteCreatedFor")} {inviteResult.email}
                </div>
                <div style={{ color:"#166534", marginBottom:8 }}>
                  {inviteResult.email_sent
                    ? t("app.team.emailSent")
                    : t("app.team.emailNotSent")}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <code style={{ flex:1, fontSize:11, background:"#fff", padding:"6px 9px", borderRadius:5, border:"1px solid #bbf7d0", color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {inviteResult.invite_url}
                  </code>
                  <button onClick={() => doCopy(inviteResult.invite_url, `result-${inviteResult.invite_id}`)} style={btnSecondary}>
                    {copiedKey === `result-${inviteResult.invite_id}` ? t("app.team.copied") : t("app.team.copyLink")}
                  </button>
                </div>
              </div>
            )}
          </AdminCard>
        </>
      )}

      {/* PENDING INVITES (brand owner only) */}
      {isBrandOwner && invites.length > 0 && (
        <>
          <AdminSectionLabel>{t("app.team.pendingInvites")} ({invites.length})</AdminSectionLabel>
          <AdminCard style={{ marginBottom:24 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#fafbfc" }}>
                  {[t("app.team.colEmail"),t("app.team.colWorkspacesPlain"),t("app.team.colExpires"),t("app.team.colStatus"),""].map(h => (
                    <th key={h} style={adminThStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invites.map((inv, i) => (
                  <tr key={inv.id} style={{
                    borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                  }}>
                    <td style={{ ...adminTdStyle, fontWeight:500, color:"#0f172a" }}>{inv.email}</td>
                    <td style={{ ...adminTdStyle, fontSize:12, color:"#475569" }}>
                      {(inv.location_access || []).map(wsName).join(", ")}
                    </td>
                    <td className="num" style={{ ...adminTdStyle, fontSize:11.5, color:"#64748b" }}>
                      {fmtDate(inv.expires_at)}
                    </td>
                    <td style={adminTdStyle}>
                      {inv.expired
                        ? <span style={{ fontSize:10.5, padding:"2px 8px", background:"#fef2f2", color:"#991b1b", border:"1px solid #fecaca", borderRadius:4, fontWeight:700, letterSpacing:".3px" }}>{t("app.team.badgeExpired")}</span>
                        : <span style={{ fontSize:10.5, padding:"2px 8px", background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a", borderRadius:4, fontWeight:700, letterSpacing:".3px" }}>{t("app.team.badgePending")}</span>}
                    </td>
                    <td style={{ ...adminTdStyle, textAlign:"end" }}>
                      <button onClick={() => doCopy(inv.invite_url, `inv-${inv.id}`)} style={{ ...btnSecondary, marginInlineEnd:6, fontSize:11.5, padding:"4px 10px" }}>
                        {copiedKey === `inv-${inv.id}` ? t("app.team.copied") : t("app.team.copyLink")}
                      </button>
                      <button onClick={() => resendInvite(inv.id)} style={{ ...btnSecondary, marginInlineEnd:6, fontSize:11.5, padding:"4px 10px" }} disabled={working}>{t("app.team.resend")}</button>
                      <button onClick={() => revokeInvite(inv.id, inv.email)} style={{ ...btnGhostDanger, fontSize:11.5, padding:"4px 10px" }} disabled={working}>{t("app.team.revoke")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminCard>
        </>
      )}

      {/* MEMBERS — visible to everyone in the team */}
      <AdminSectionLabel>{t("app.team.members")} ({members.length})</AdminSectionLabel>
      <AdminCard>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#fafbfc" }}>
              {[t("app.team.colEmail"),t("app.team.colRole"),t("app.team.colWorkspaceAccess"),t("app.team.colJoined"),t("app.team.colLastSignIn"),""].map(h => (
                <th key={h} style={adminThStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={6} style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.team.noTeamMembers")}</td></tr>
            )}
            {members.map((m, i) => (
              <Fragment key={m.user_id}>
                <tr style={{ borderTop: i === 0 ? "none" : "1px solid #f1f5f9" }}>
                  <td style={{ ...adminTdStyle, fontWeight:500, color:"#0f172a" }}>{m.email || "—"}</td>
                  <td style={adminTdStyle}>
                    <span style={{
                      fontSize:10.5, padding:"2px 8px", borderRadius:4, fontWeight:700, letterSpacing:".3px",
                      background: m.role === "brand_owner" ? "#eff6ff" : "#f1f5f9",
                      color:      m.role === "brand_owner" ? "#1e40af" : "#475569",
                      border:     m.role === "brand_owner" ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                      textTransform:"uppercase",
                    }}>{m.role === "brand_owner" ? t("app.team.roleBrandOwner") : t("app.team.roleOperator")}</span>
                  </td>
                  <td style={{ ...adminTdStyle, fontSize:12, color:"#475569" }}>
                    {m.role === "brand_owner"
                      ? <span style={{ color:"#94a3b8", fontStyle:"italic" }}>{t("app.team.allWorkspaces")}</span>
                      : (m.location_access || []).length === 0
                        ? <span style={{ color:"#dc2626" }}>{t("app.team.noAccessSet")}</span>
                        : (m.location_access || []).map(wsName).join(", ")}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:11.5, color:"#64748b" }}>{fmtDate(m.accepted_at || m.invited_at)}</td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:11.5, color:"#64748b" }}>{m.last_sign_in_at ? fmtDateTime(m.last_sign_in_at) : "—"}</td>
                  <td style={{ ...adminTdStyle, textAlign:"end" }}>
                    {isBrandOwner && m.role === "operator" && editingMemberId !== m.user_id && (
                      <>
                        <button onClick={() => openAccessEditor(m)} style={{ ...btnSecondary, marginInlineEnd:6, fontSize:11.5, padding:"4px 10px" }} disabled={working}>{t("app.team.editAccess")}</button>
                        <button onClick={() => removeMember(m.user_id, m.email)} style={{ ...btnGhostDanger, fontSize:11.5, padding:"4px 10px" }} disabled={working}>{t("app.team.remove")}</button>
                      </>
                    )}
                  </td>
                </tr>
                {editingMemberId === m.user_id && (
                  <tr style={{ borderTop:"1px solid #e2e8f0", background:"#f8fafc" }}>
                    <td colSpan={6} style={{ padding:"14px 18px" }}>
                      <div style={{ fontSize:11.5, fontWeight:700, color:"#475569", marginBottom:10, textTransform:"uppercase", letterSpacing:".5px" }}>
                        {t("app.team.editAccessForPrefix")} {m.email}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:8, marginBottom:12 }}>
                        {wsList.map(w => (
                          <label key={w.id} style={{
                            display:"flex", alignItems:"center", gap:9, padding:"7px 11px",
                            background: editingAccess.includes(w.id) ? "#eff6ff" : "#fff",
                            border: editingAccess.includes(w.id) ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                            borderRadius:6, cursor:"pointer", fontSize:12.5,
                          }}>
                            <input type="checkbox" checked={editingAccess.includes(w.id)} onChange={() => toggleEditingWs(w.id)} style={{ accentColor:"#1e40af" }}/>
                            <span style={{ fontWeight:600, color:"#0f172a" }}>{w.name}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                        <button onClick={cancelAccessEdit} style={btnSecondary} disabled={working}>{t("app.team.cancel")}</button>
                        <button onClick={saveAccess} style={btnPrimaryDark} disabled={working}>{working ? t("app.team.saving") : t("app.team.save")}</button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </AdminCard>
    </AdminPage>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ACCEPT INVITE PAGE — landing page when user clicks an invite link
// URL: /?accept_invite=<token>
// ═══════════════════════════════════════════════════════════════════════
function AcceptInvitePage({ token, user, onAuthRedirect, teamContext }) {
  const { t } = useT();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  // If the user signed in to a different account than the invite's, OR they're
  // already a member of the invite's account, we surface a friendly message
  // rather than auto-firing accept (which would then fail).
  const [alreadyMember, setAlreadyMember] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/team/invites/${encodeURIComponent(token)}/preview`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Invite not found");
        setPreview(d);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Detect "already a member of this account" — usually because the brand
  // owner is testing the link in their own session.
  useEffect(() => {
    if (!preview || !user || !teamContext) return;
    if (teamContext.account_id && teamContext.account_id === preview.account_id) {
      setAlreadyMember(true);
    }
  }, [preview, user, teamContext]);

  const accept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await adminFetch("/api/team/invites/accept", {
        method: "POST",
        body: { token },
      });
      setDone(true);
      // Brief success flash, then clean the URL and reload so team context refreshes
      setTimeout(() => {
        window.history.replaceState({}, "", "/");
        window.location.reload();
      }, 1100);
    } catch (e) {
      // Already-member errors are friendlier — show the dedicated UI instead
      // of a red error block.
      const msg = e.message || "";
      if (/already.*member|already.*team/i.test(msg)) {
        setAlreadyMember(true);
      } else {
        setError(msg);
      }
      setAccepting(false);
    }
  };

  // No auto-accept. The user explicitly clicks "Accept invite & join" — gives
  // them a chance to see what they're joining and confirm before joining.
  // (Earlier versions auto-fired on landing, which surprised people who
  //  clicked the link just to check what it was.)

  const goToDashboard = () => {
    window.history.replaceState({}, "", "/");
    window.location.reload();
  };

  return (
    <div style={{
      minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
      background:"linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)", padding:20,
    }}>
      <div style={{
        background:"#fff", borderRadius:14, maxWidth:480, width:"100%",
        boxShadow:"0 18px 50px rgba(15,23,42,.12)", border:"1px solid #e2e8f0",
        padding:"36px 36px 32px",
      }}>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11.5, fontWeight:700, color:"#3b5bdb", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>{t("app.invite.teamInvite")}</div>
          <h1 style={{ fontSize:24, fontWeight:700, color:"#0f172a", letterSpacing:"-.5px", margin:0, lineHeight:1.2 }}>
            {loading ? t("app.invite.loadingInvite")
              : error ? t("app.invite.cantOpen")
              : done ? t("app.invite.welcomeToTeam")
              : alreadyMember ? t("app.invite.alreadyOnTeam")
              : `${t("app.invite.invitedToPrefix")}${preview?.account_name || t("app.invite.invitedToFallback")}`}
          </h1>
        </div>

        {error && (
          <>
            <div style={{ padding:"12px 14px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, color:"#991b1b", fontSize:13, marginBottom:14, lineHeight:1.5 }}>
              {error}
            </div>
            <button onClick={goToDashboard} style={{ ...btnSecondary, width:"100%", padding:"10px 14px", fontSize:13 }}>
              {t("app.invite.returnToNetrev")}
            </button>
          </>
        )}

        {loading && <Loading/>}

        {!loading && !error && preview && !done && alreadyMember && (
          <>
            <div style={{ fontSize:13.5, color:"#475569", lineHeight:1.55, marginBottom:18 }}>
              {t("app.invite.alreadyMemberOf")}<b style={{ color:"#0f172a" }}>{preview.account_name}</b>{teamContext?.role === "brand_owner" ? t("app.invite.asBrandOwner") : ""}.
              <br/><br/>
              {teamContext?.role === "brand_owner"
                ? <>{t("app.invite.brandOwnerTestNote")}</>
                : <>{t("app.invite.notNeeded")}</>}
            </div>
            <button onClick={goToDashboard} style={{ ...btnPrimaryDark, width:"100%", padding:"12px 18px", fontSize:14 }}>
              {t("app.invite.goToDashboard")}
            </button>
          </>
        )}

        {!loading && !error && preview && !done && !alreadyMember && (
          <>
            <div style={{ fontSize:13.5, color:"#475569", lineHeight:1.55, marginBottom:20 }}>
              {preview.inviter
                ? <>{t("app.invite.adminInvitedPrefix")}<b style={{ color:"#0f172a" }}>{preview.inviter}</b>{t("app.invite.adminInvitedMid")}<b style={{ color:"#0f172a" }}>{preview.email}</b>{t("app.invite.adminInvitedSuffix")}</>
                : <>{t("app.invite.invitedAsOperator")}</>}
              <br/>
              {t("app.invite.ableToViewData")}
            </div>

            {user ? (
              <button onClick={accept} disabled={accepting} style={{ ...btnPrimaryDark, width:"100%", padding:"12px 18px", fontSize:14 }}>
                {accepting ? t("app.invite.joiningTeam") : t("app.invite.acceptAndJoin")}
              </button>
            ) : (
              <>
                <div style={{ padding:"11px 13px", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:7, fontSize:12.5, color:"#92400e", marginBottom:14, lineHeight:1.5 }}>
                  {t("app.invite.signInWithEmailPrefix")}{preview.email}{t("app.invite.signInWithEmailSuffix")}
                </div>
                <button onClick={() => onAuthRedirect && onAuthRedirect(preview.email)} style={{ ...btnPrimaryDark, width:"100%", padding:"12px 18px", fontSize:14 }}>
                  {t("app.invite.signInToAccept")}
                </button>
              </>
            )}
          </>
        )}

        {done && (
          <div style={{ fontSize:14, color:"#15803d", padding:"12px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8 }}>
            {t("app.invite.joinedPrefix")}<b>{preview?.account_name}</b>{t("app.invite.joinedSuffix")}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Shared bits — re-using AdminSubscriptions design language
// ─────────────────────────────────────────────────────────────────────
function Loading() {
  const { t } = useT();
  return <div style={{ padding:48, textAlign:"center", color:"#94a3b8", fontSize:13 }}>{t("app.shared.loadingEllipsis")}</div>;
}
function ErrorBlock({ msg }) {
  return (
    <div style={{
      margin:18, padding:"12px 16px", background:"#fef2f2", color:"#991b1b",
      borderRadius:7, fontSize:13, border:"1px solid #fecaca",
    }}>{msg}</div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <AdminSectionLabel>{title}</AdminSectionLabel>
      <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:7, padding:14, boxShadow:"0 1px 2px rgba(15,23,42,.04)" }}>
        {children}
      </div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div style={{ display:"flex", padding:"6px 0", borderBottom:"1px dashed #e2e8f0", fontSize:13 }}>
      <div style={{ width:170, color:"#94a3b8", fontSize:12, fontWeight:500 }}>{k}</div>
      <div style={{ flex:1, color:"#0f172a", wordBreak:"break-all" }}>{v ?? "—"}</div>
    </div>
  );
}

// Style tokens — matching Subscriptions page palette.
const inputStyle = {
  padding:"8px 11px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", fontSize:13, fontFamily:"inherit", color:"#0f172a",
  boxSizing:"border-box",
};
const selectStyleAdmin = {
  padding:"8px 11px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", fontSize:13, fontFamily:"inherit", color:"#0f172a",
};
const adminThStyle = {
  padding:"10px 14px", textAlign:"start", fontSize:10, fontWeight:700,
  color:"#64748b", textTransform:"uppercase", letterSpacing:".7px",
  borderBottom:"1px solid #e2e8f0",
};
const adminTdStyle = { padding:"12px 14px", verticalAlign:"middle" };
const codeStyle = {
  background:"#f1f5f9", padding:"1px 5px", borderRadius:3, fontSize:11,
  fontVariantNumeric:"tabular-nums", color:"#475569",
};

const btnPrimaryDark = {
  padding:"8px 16px", borderRadius:7, border:"none",
  background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",
  color:"#fff", cursor:"pointer", fontSize:13, fontFamily:"inherit",
  fontWeight:600, letterSpacing:"-.1px", boxShadow:"0 2px 6px rgba(15,23,42,.18)",
};
const btnSecondary = {
  padding:"8px 14px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", color:"#0f172a", cursor:"pointer", fontSize:12.5,
  fontFamily:"inherit", fontWeight:600, letterSpacing:"-.1px",
  boxShadow:"0 1px 2px rgba(15,23,42,.04)",
};
const btnDanger = {
  padding:"8px 16px", borderRadius:7, border:"none",
  background:"#991b1b", color:"#fff", cursor:"pointer", fontSize:13,
  fontFamily:"inherit", fontWeight:600,
};
const btnGhostDanger = {
  padding:"5px 12px", borderRadius:6, border:"1px solid #fecaca",
  background:"#fff", color:"#991b1b", cursor:"pointer", fontSize:11.5,
  fontFamily:"inherit", fontWeight:600,
};
const btnPage = (disabled) => ({
  padding:"6px 12px", borderRadius:6, border:"1px solid #e2e8f0",
  background:"#fff", color: disabled ? "#cbd5e1" : "#475569",
  cursor: disabled ? "not-allowed" : "pointer", fontSize:12,
  fontFamily:"inherit", opacity: disabled ? .6 : 1, fontWeight:600,
});

function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
  } catch { return "—"; }
}
function fmtDateTime(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return "—"; }
}
// SVG icon components for the sidebar — single 18×18 outline style, currentColor stroke.
// ── Viewport hook ───────────────────────────────────────────────────
// Live window width for structural responsive branches (sidebar → drawer,
// toolbar alignment). Extracted from the winW/resize-listener pattern that
// previously lived inline in AdminView; SSR-safe default matches the old one.
function useViewport() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return width;
}

// Replaces emoji for a more professional look. ~1KB total.
const NavIcon = ({d, viewBox = "0 0 24 24"}) => (
  <svg width="17" height="17" viewBox={viewBox} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,opacity:0.85}}>
    {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path}/>) : <path d={d}/>}
  </svg>
);
const ICONS = {
  overview:      <NavIcon d={["M3 3v18h18","M7 15l4-4 4 4 6-7"]}/>,
  analysis:      <NavIcon d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"]}/>,
  pricetool:     <NavIcon d={["M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z","M7 7h.01"]}/>,
  dailylog:      <NavIcon d={["M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2","M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v0z","M9 12h6","M9 16h4"]}/>,
  config:        <NavIcon d={["M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z","M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"]}/>,
  subscriptions: <NavIcon d={["M2 4h20v16H2z","M2 10h20","M6 15h2","M11 15h2"]}/>,
  team:          <NavIcon d={["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]}/>,
  // Admin section
  admin_overview:     <NavIcon d={["M3 12h4l3-9 4 18 3-9h4"]}/>,
  admin_accounts:     <NavIcon d={["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]}/>,
  admin_audit:        <NavIcon d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"]}/>,
  admin_super_admins: <NavIcon d={["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z","M9 12l2 2 4-4"]}/>,
  admin_feed:         <NavIcon d={FEED_ICON_PATHS}/>,
};

// Brand mark for the Insights tab — renders the trailing "+" as a small,
// raised superscript (~0.62em) so it reads as "Insight⁺" instead of a full-size
// "+". lineHeight:0 keeps it from increasing the nav row / title line height;
// it inherits colour from the parent so theme colours are untouched. The label
// data below stays the plain string "Insight+" (used for nothing programmatic
// today, but kept stable for any future aria/equality/title use).
const InsightPlus = (
  <>Insight<sup style={{fontSize:"0.62em",verticalAlign:"super",lineHeight:0,fontWeight:"inherit",letterSpacing:0}}>+</sup></>
);

const ADMIN_NAV = [
  // Overview is hidden for now (owner request). The AdminOverview page component
  // and its /overview route are kept intact — restore it by re-adding this entry.
  // Default landing + any route to "overview" now resolve to Insight+ (analysis).
  {id:"analysis",     label:"Insight+"},
  {id:"pricetool",    label:"Price Tool"},
  {id:"dailylog",     label:"Daily Log"},
  {id:"config",       label:"Configuration"},
  {id:"subscriptions",label:"Subscriptions"},
];

// Super admin sidebar items — only rendered for users in the super_admins table.
// Each id is prefixed with "admin_" so the main-area router can branch cleanly.
// admin_feed (the integration feed setup screen) is filtered out at render
// time unless GET /api/feed/enabled says so (AdminView feedOn).
const SUPER_ADMIN_NAV = [
  {id:"admin_overview",     label:"Overview"},
  {id:"admin_accounts",     label:"Accounts"},
  {id:"admin_feed",         label:"Integrations"},
  {id:"admin_audit",        label:"Audit log"},
  {id:"admin_super_admins", label:"Super admins"},
];

// i18n key map for the super-admin nav: its ids (admin_*) don't map 1:1 onto
// the catalog keys, so resolve each id to its key here and translate at render
// time. Falls back to the item's English label if an id is ever unmapped.
const SUPER_ADMIN_NAV_KEYS = {
  admin_overview:     "nav.admin.overview",
  admin_accounts:     "nav.admin.accounts",
  admin_audit:        "nav.admin.audit",
  admin_super_admins: "nav.admin.superAdmins",
  admin_feed:         "nav.admin.feed",
};

function AdminView({cfg, setCfg, tiers, setTiers, workspaces, setWorkspaces, user, onLogout, currency, setCurrency, account, accountStatus, onUpgrade, refreshAccount, refreshWorkspaces, teamContext, myAccounts=[], activeAccountId=null, switchAccount, mobileNavOpen=false, setMobileNavOpen}) {
  // i18n translator for sidebar chrome (nav labels, section headers). The nav
  // arrays (ADMIN_NAV / SUPER_ADMIN_NAV) are module-level consts, so labels are
  // translated at render time from each item's id rather than moving the arrays.
  const { t } = useT();
  // Filter out archived workspaces for the main views; Subscriptions sees the full set.
  // Archived = workspace.archived_at is set. Pending-cancel workspaces stay visible (still active until period end).
  const activeWorkspaces = useMemo(() => {
    const out = {};
    for (const [id, w] of Object.entries(workspaces || {})) {
      if (!w.archived_at) out[id] = w;
    }
    return out;
  }, [workspaces]);

  const isSuperAdmin = user?.plan === "superadmin";
  const hasNoWorkspaces = Object.keys(activeWorkspaces).length === 0;
  // PAID-ONLY features (Price Tool, AI insight): a paid subscription (incl. the
  // past_due dunning window and a cancelled sub still inside its paid period),
  // an unexpired free override, or a super admin. A trial — with or without a
  // card on file — stays greyed until the first payment lands.
  //
  // Checked against BOTH the account the user owns AND the account they
  // actually work in (teamContext.paid_access, computed server-side by
  // /api/team/me with the same predicate as the /api/anthropic guard). An
  // invited operator of a paying brand owns an empty pre-trial stub account,
  // so gating on the owned account alone would lock a paying customer's staff
  // out of features their brand pays for. The owned-account arm is what makes
  // the unlock immediate after payment (it re-polls every 60s), the
  // teamContext arm is what covers operators.
  const ownAccountPaid = isOverrideActive(account)
    || account?.subscription_status === "active"
    || account?.subscription_status === "past_due"
    || (account?.subscription_status === "canceled" && account?.current_period_end && new Date(account.current_period_end) > new Date());
  const paidAccess = isSuperAdmin || ownAccountPaid || teamContext?.paid_access === true;

  // ── Integration feed setup screen (pages/AdminFeed.js). Its nav entry,
  // route and the Accounts "Set up feed" hand-off exist only once GET
  // /api/feed/enabled answers enabled + super_admin. feedOn: null while the
  // probe is out (render nothing, don't bounce), then true/false. Only super
  // admins probe; this hides navigation only, the API enforces its own gates.
  const feedAccess = useFeedAdminAccess(isSuperAdmin);
  const feedOn = feedAccess === null ? null : feedAdminVisible(feedAccess);
  const [feedPrefill, setFeedPrefill] = useState(null);   // { id, name } handed over by the Accounts list

  // ── Tab routing (3c): keep the active tab in sync with the URL so each admin
  // tab is a real, bookmarkable route (/overview, /insights, …).
  const navigate = useNavigate();
  const location = useLocation();
  // Default nav. Prefer the tab named in the URL (deep-link / refresh); else
  // super admins land on admin overview, users with no workspaces on
  // subscriptions onboarding, everyone else on overview.
  const [nav, setNav]   = useState(
    PATH_TO_NAV[typeof window !== "undefined" ? window.location.pathname : ""]
      || (isSuperAdmin ? "admin_overview" : (hasNoWorkspaces ? "subscriptions" : "analysis"))
  );
  // active tab -> address bar
  useEffect(() => {
    const p = NAV_TO_PATH[nav];
    if (p && location.pathname !== p) navigate(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);
  // address bar (back/forward, deep link) -> active tab
  useEffect(() => {
    const t = PATH_TO_NAV[location.pathname];
    if (t && t !== nav) setNav(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // ── Role guard (3c security): the URL can deep-link / refresh into ANY tab,
  // but operators must never reach config / team / subscriptions, and only super
  // admins may reach the /admin/* tabs. Bounce any forbidden tab to overview.
  // (The render switch also gates the restricted tabs, so nothing leaks even in
  // the single redirect frame.)
  useEffect(() => {
    const role = teamContext?.role;
    const canManage = role === "brand_owner";
    const restricted = nav === "config" || nav === "team" || nav === "subscriptions";
    // Only redirect a restricted tab once the role is actually known — avoids
    // bouncing a brand owner during initial load. The render switch above fails
    // CLOSED (tab hidden) while role is unknown, so nothing leaks in the meantime.
    // Overview is hidden for now — bounce any route to it onto Insight+ (analysis).
    if (nav === "overview") { setNav("analysis"); return; }
    if (role != null && restricted && !canManage) setNav("analysis");
    if (String(nav).startsWith("admin_") && !isSuperAdmin) setNav("analysis");
    // The feed screen: bounce only once the probe has said no (null = still asking).
    if (nav === "admin_feed" && isSuperAdmin && leaveFeedScreen(feedOn)) setNav("admin_overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, teamContext?.role, isSuperAdmin, feedOn]);
  // The Accounts hand-off is for one visit to the feed screen only.
  useEffect(() => {
    if (nav !== "admin_feed") setFeedPrefill(null);
  }, [nav]);
  // When the portal nav tab changes, scroll back to the top so each section
  // starts at the top rather than keeping the previous tab's scroll position.
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
      // Also reset any inner scrolling main container (desktop + mobile shells).
      document.querySelectorAll('[data-portal-scroll]').forEach(el => { el.scrollTop = 0; });
    } catch(_){}
  }, [nav]);
  const [wsId, setWsId] = useState(() => {
    // Restore the last-selected workspace across refreshes (if still valid for
    // this account / operator scope); otherwise fall back to the first.
    try {
      const saved = localStorage.getItem("netrev_active_ws");
      if (saved && activeWorkspaces[saved]) return saved;
    } catch(_){}
    return Object.keys(activeWorkspaces)[0]||"";
  });
  // Persist the active workspace whenever it changes to a valid selection.
  useEffect(() => {
    if (wsId && activeWorkspaces[wsId]) {
      try { localStorage.setItem("netrev_active_ws", wsId); } catch(_){}
    }
  }, [wsId, activeWorkspaces]);
  const [chId, setChId] = useState(null);
  // Shared date-range filter — lifted here so the pill-bar toolbar can render it
  // once and BOTH Daily Log and Insights read the same selection (switching tabs
  // keeps the chosen range). Page-action triggers below let the toolbar host the
  // page's primary buttons next to the channel pills.
  // Default landing view = "Today" (single current day). All platforms is the
  // default channel scope (chId=null below). Both Overview and Insights read
  // this shared range, so they open on All platforms + Today.
  const [range,setRange]=useState("1D");
  const [customFrom,setCustomFrom]=useState("");
  const [customTo,setCustomTo]=useState("");
  // Responsive toolbar: right-aligned when it fits on the header line; when the
  // window is narrow the right-hand toolbar wraps to a new line and aligns LEFT
  // (underneath the left nav) instead of hanging on the right.
  const winW = useViewport();
  const toolbarNarrow = winW < 1200;
  // Below 768px the static 232px sidebar becomes a slide-in drawer (opened by
  // the top-bar hamburger). Desktop ≥768px renders exactly as before.
  const isMobile = winW < 768;
  const closeMobileNav = () => {
    if (setMobileNavOpen) setMobileNavOpen(false);
    // Focus restoration: the drawer unmounts with the focused element inside it,
    // so hand focus back to the hamburger (same pattern as RangeFilter's close()).
    if (typeof document !== "undefined") document.querySelector(".nv-menu-btn")?.focus?.();
  };
  // Leaving the mobile breakpoint always resets the drawer so it can't pop
  // back open unexpectedly when the window shrinks again.
  useEffect(() => {
    if (!isMobile && mobileNavOpen) closeMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);
  // Open drawer locks background scroll (body + the main scroll pane keep
  // their position while the fixed overlay is up). Mobile-only by definition
  // since mobileNavOpen can only be true below the breakpoint.
  useEffect(() => {
    if (!mobileNavOpen || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileNavOpen]);
  // Escape closes the open drawer (mirrors backdrop click / nav selection).
  useEffect(() => {
    if (!mobileNavOpen || typeof window === "undefined") return;
    const onKey = (e) => { if (e.key === "Escape") closeMobileNav(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNavOpen]);
  // Daily Log toolbar plumbing — the +Add / Export buttons live in the top
  // toolbar and drive the page via lifted state (mirrors PROD/origin-main).
  const [dlAddOpen,setDlAddOpen]=useState(false);    // Daily Log: "Add log entry" form open
  const [dlExportTick,setDlExportTick]=useState(0);  // Daily Log: incremented to trigger CSV/xlsx export
  const [dlTab,setDlTab]=useState("log");            // Daily Log: "log" | "cpc" | "cofund" tab (lifted so the toolbar +Add switches)
  const [dlCpcAddTick,setDlCpcAddTick]=useState(0);  // Daily Log: incremented to open the CPC add form
  const [dlCoFundAddTick,setDlCoFundAddTick]=useState(0); // Daily Log: incremented to open the platform-contribution add form
  const [ptAddTick,setPtAddTick]=useState(0);        // Price Tool: incremented to trigger "Add menu item"
  // Inspect drawer state. Only ever populated by super admins clicking
  // "Inspect" in the admin accounts view. Lifted to AdminView so the
  // drawer stays mounted at this level (over the whole main area).
  const [inspectAccountId, setInspectAccountId] = useState(null);
  const ws = activeWorkspaces[wsId] || Object.values(activeWorkspaces)[0];
  const channels = ws?.channels ? Object.values(ws.channels) : [];
  const ch = chId ? ws?.channels?.[chId] : channels[0];
  const userPlan = user?.plan || "trial";
  const hasWorkspaces = Object.keys(activeWorkspaces).length > 0;

  // Sync global currency: workspace currency takes priority, falls back to user toggle
  useEffect(() => {
    const wsCurrency = ws?.currency;
    const effective = wsCurrency || currency || "GBP";
    setGlobalCurrency(effective);
    if(wsCurrency && wsCurrency !== currency) setCurrency(wsCurrency);
  }, [ws, currency]);

  // Keep the channel pill (chId) valid for the current workspace. When the
  // active account is switched, wsId changes directly (bypassing switchWs), so
  // chId can still point at the previous account's channel. This self-heals:
  // if chId isn't a channel of the current ws, reset to its first channel (or
  // null for the "All" view). Prevents a stale/empty pill after switching.
  useEffect(() => {
    if (!ws) return;
    const validChannelIds = ws.channels ? Object.keys(ws.channels) : [];
    if (chId !== null && !validChannelIds.includes(chId)) {
      setChId(validChannelIds.length > 0 ? validChannelIds[0] : null);
    }
  }, [wsId, ws]); // eslint-disable-line react-hooks/exhaustive-deps

  // When workspace changes, reset to first channel
  function switchWs(id) {
    setWsId(id);
    const newWs = activeWorkspaces[id];
    if(newWs?.channels) setChId(Object.keys(newWs.channels)[0]);
    else setChId(null);
    if(newWs?.currency){
      setCurrency(newWs.currency);
      setGlobalCurrency(newWs.currency);
    }
  }

  // First workspace ever → straight into the Daily Log with the entry form
  // open, so the operator logs one day and sees a real margin within a minute
  // of signing up (the "first value" moment). The workspace list in this
  // closure may not include the new row yet, so set ids directly; the
  // chId self-heal effect above picks the first channel once it loads.
  function onFirstWorkspaceCreated(id) {
    setWsId(id);
    setChId(null);
    setDlTab("log");
    setDlAddOpen(true);
    setNav("dailylog");
    track("first_workspace_created", { workspace_id: id });
  }

  // Nav taps close the mobile drawer (no-op on desktop, where it's never open).
  const go = (id) => { setNav(id); closeMobileNav(); };
  const goWs = (id) => { switchWs(id); closeMobileNav(); };

  // Sidebar inner content — shared VERBATIM between the desktop 232px rail and
  // the <768px slide-in drawer, so markup and colours stay identical in both.
  const sidebarInner = (
    <>
        {/* ACCOUNT SWITCHER — only for users who belong to more than one brand account */}
        {Array.isArray(myAccounts) && myAccounts.length > 1 && (
          <div style={{marginBottom:18}}>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginBottom:8,paddingInlineStart:2}}>
              {t("app.view.brandSwitch")} ({myAccounts.length})
            </div>
            <div style={{position:"relative"}}>
              <select
                value={activeAccountId || ""}
                onChange={e => switchAccount && switchAccount(e.target.value)}
                style={{width:"100%",padding:"10px 32px 10px 12px",borderRadius:9,border:"1px solid rgba(255,255,255,.25)",background:"rgba(255,255,255,.10)",color:"#fff",fontSize:13,fontWeight:600,fontFamily:"inherit",cursor:"pointer",outline:"none",appearance:"none",WebkitAppearance:"none",MozAppearance:"none",letterSpacing:"-.15px"}}
              >
                {myAccounts.map(a => (
                  <option key={a.account_id} value={a.account_id} style={{color:"#0f172a"}}>
                    {a.account_name}{a.role === "operator" ? ` ${t("app.view.operatorSuffix")}` : ""}{a.is_archived ? ` ${t("app.view.archivedSuffix")}` : ""}
                  </option>
                ))}
              </select>
              {/* Dropdown chevron — makes it obvious this is a switcher */}
              <div style={{position:"absolute",insetInlineEnd:11,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",color:"rgba(255,255,255,.7)",fontSize:11}}>▼</div>
            </div>
            <div style={{fontSize:10.5,color:"rgba(255,255,255,.4)",marginTop:6,paddingInlineStart:2}}>{t("app.view.tapToSwitchBrands")}</div>
            <div style={{height:1,background:"rgba(255,255,255,.08)",margin:"16px 0 4px"}}/>
          </div>
        )}
        {/* ADMIN SECTION — only rendered for super admins */}
        {isSuperAdmin && (
          <>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,paddingInlineStart:10}}>{t("nav.section.admin")}</div>
            {visibleSuperAdminNav(SUPER_ADMIN_NAV, feedOn).map(n => {
              const active = nav === n.id;
              return (
                <button key={n.id} onClick={()=>go(n.id)}
                  style={{display:"flex",alignItems:"center",gap:11,width:"100%",padding:"10px 12px",borderRadius:9,border:"none",background:active?"rgba(255,255,255,.10)":"transparent",color:active?"#fff":"rgba(255,255,255,.65)",cursor:"pointer",fontSize:13,fontWeight:active?600:500,marginBottom:3,fontFamily:"inherit",textAlign:"start",letterSpacing:"-.15px",transition:"all .12s",position:"relative"}}
                  onMouseEnter={e=>{if(!active){e.currentTarget.style.background="rgba(255,255,255,.06)"; e.currentTarget.style.color="#fff";}}}
                  onMouseLeave={e=>{if(!active){e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(255,255,255,.65)";}}}>
                  {active && <div style={{position:"absolute",insetInlineStart:-14,top:8,bottom:8,width:3,borderRadius:"0 3px 3px 0",background:"#60a5fa"}}/>}
                  {ICONS[n.id]}
                  <span style={{flex:1}}>{t(SUPER_ADMIN_NAV_KEYS[n.id] || ("nav."+n.id))}</span>
                </button>
              );
            })}
            <div style={{height:1,background:"rgba(255,255,255,.08)",margin:"14px 0 14px"}}/>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,paddingInlineStart:10}}>{t("app.view.workspace")}</div>
          </>
        )}
        {!isSuperAdmin && (
          <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginBottom:14,paddingInlineStart:10}}>{t("app.view.menu")}</div>
        )}
        {/* Workspace items — Overview, Insights, Price Tool, Daily Log, Configuration */}
        {(() => {
          const role = teamContext?.role;
          const baseWorkspaceIds = ["overview","analysis","pricetool","dailylog","config"];
          let items = ADMIN_NAV.filter(n => baseWorkspaceIds.includes(n.id));
          if (role !== "brand_owner") items = items.filter(n => n.id !== "config");
          if (!hasWorkspaces && !isSuperAdmin) items = items.slice(0, 1);
          return items;
        })().map(n => {
          const active = nav === n.id;
          // Paid-only items (Price Tool) stay visible but greyed during a
          // trial; tapping opens the subscribe modal instead of navigating.
          const locked = n.id === "pricetool" && !paidAccess;
          return (
            <button key={n.id} onClick={()=>{ if (locked) { closeMobileNav(); if (onUpgrade) onUpgrade(); } else go(n.id); }}
              title={locked ? t("app.lock.subscribeToUnlock") : undefined}
              style={{display:"flex",alignItems:"center",gap:11,width:"100%",padding:"10px 12px",borderRadius:9,border:"none",background:active?"rgba(255,255,255,.10)":"transparent",color:active?"#fff":locked?"rgba(255,255,255,.35)":"rgba(255,255,255,.65)",cursor:"pointer",fontSize:13,fontWeight:active?600:500,marginBottom:3,fontFamily:"inherit",textAlign:"start",letterSpacing:"-.15px",transition:"all .12s",position:"relative"}}
              onMouseEnter={e=>{if(!active && !locked){e.currentTarget.style.background="rgba(255,255,255,.06)"; e.currentTarget.style.color="#fff";}}}
              onMouseLeave={e=>{if(!active && !locked){e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(255,255,255,.65)";}}}>
              {active && <div style={{position:"absolute",insetInlineStart:-14,top:8,bottom:8,width:3,borderRadius:"0 3px 3px 0",background:"#60a5fa"}}/>}
              {ICONS[n.id]}
              <span style={{flex:1}}>{(hasWorkspaces || isSuperAdmin) ? (n.id==="analysis" ? InsightPlus : t("nav."+n.id)) : t("app.view.createWorkspace")}</span>
              {locked && <span style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:6,background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.55)",whiteSpace:"nowrap"}}>🔒 {t("app.lock.paidOnlyNav")}</span>}
            </button>
          );
        })}
        {/* Account section — brand owners + super admins only. Crucially,
            operators NEVER see this section, even if they're also a super
            admin. The operator role is the principle of least privilege for
            the account they joined; super admin powers stay in the Admin
            section above. */}
        {teamContext?.role === "brand_owner" && (
          <>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginTop:18,marginBottom:10,paddingInlineStart:10}}>{t("nav.section.account")}</div>
            {[
              { id: "team",          label: "Team" },
              { id: "subscriptions", label: "Subscriptions" },
            ].map(n => {
              const active = nav === n.id;
              return (
                <button key={n.id} onClick={()=>go(n.id)}
                  style={{display:"flex",alignItems:"center",gap:11,width:"100%",padding:"10px 12px",borderRadius:9,border:"none",background:active?"rgba(255,255,255,.10)":"transparent",color:active?"#fff":"rgba(255,255,255,.65)",cursor:"pointer",fontSize:13,fontWeight:active?600:500,marginBottom:3,fontFamily:"inherit",textAlign:"start",letterSpacing:"-.15px",transition:"all .12s",position:"relative"}}
                  onMouseEnter={e=>{if(!active){e.currentTarget.style.background="rgba(255,255,255,.06)"; e.currentTarget.style.color="#fff";}}}
                  onMouseLeave={e=>{if(!active){e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(255,255,255,.65)";}}}>
                  {active && <div style={{position:"absolute",insetInlineStart:-14,top:8,bottom:8,width:3,borderRadius:"0 3px 3px 0",background:"#60a5fa"}}/>}
                  {ICONS[n.id]}
                  <span style={{flex:1}}>{t("nav."+n.id)}</span>
                </button>
              );
            })}
          </>
        )}
        <div style={{marginTop:"auto",paddingTop:16,borderTop:"1px solid rgba(255,255,255,.08)"}}>
          {/* Currency */}
          <div style={{marginBottom:14,padding:"10px 12px",borderRadius:9,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.08)"}}>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",marginBottom:7,textTransform:"uppercase",letterSpacing:1}}>{t("app.view.currency")}</div>
            <div style={{display:"flex",gap:5}}>
              {["GBP","AED","USD","INR"].map(c=>(
                <button key={c} onClick={()=>setCurrency(c)}
                  style={{flex:1,padding:"6px 0",borderRadius:6,border:"1px solid "+(currency===c?"#60a5fa":"rgba(255,255,255,.12)"),background:currency===c?"rgba(96,165,250,.18)":"rgba(255,255,255,.04)",color:currency===c?"#fff":"rgba(255,255,255,.6)",cursor:"pointer",fontSize:11.5,fontWeight:currency===c?700:500,fontFamily:"inherit",letterSpacing:"-.1px",transition:"all .12s"}}>
                  {c==="GBP"?t("app.view.gbpLabel"):c==="USD"?"$ USD":c==="INR"?"₹ INR":"AED"}
                </button>
              ))}
            </div>
          </div>
          {/* Workspaces */}
          {hasWorkspaces&&(
            <>
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,paddingInlineStart:10,marginTop:4}}>{t("app.view.workspaces")}</div>
              {Object.values(activeWorkspaces).map(w => {
                const sel = wsId===w.id;
                return (
                  <button key={w.id} onClick={()=>goWs(w.id)}
                    style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 11px",borderRadius:9,border:"none",background:sel?"rgba(255,255,255,.10)":"transparent",cursor:"pointer",marginBottom:3,fontFamily:"inherit",textAlign:"start",transition:"all .12s",position:"relative"}}
                    onMouseEnter={e=>{if(!sel){e.currentTarget.style.background="rgba(255,255,255,.06)";}}}
                    onMouseLeave={e=>{if(!sel){e.currentTarget.style.background="transparent";}}}>
                    {sel && <div style={{position:"absolute",insetInlineStart:-14,top:8,bottom:8,width:3,borderRadius:"0 3px 3px 0",background:"#60a5fa"}}/>}
                    <div style={{width:8,height:8,borderRadius:4,background:w.color||C.blue,flexShrink:0,boxShadow:sel?`0 0 0 3px ${(w.color||C.blue)}40`:"none",transition:"box-shadow .12s"}}/>
                    <div style={{overflow:"hidden",flex:1,minWidth:0}}>
                      <div style={{fontSize:12.5,fontWeight:sel?600:500,color:sel?"#fff":"rgba(255,255,255,.7)",whiteSpace:"nowrap",textOverflow:"ellipsis",overflow:"hidden",letterSpacing:"-.15px"}}>{w.name||t("app.view.workspaceFallback")}</div>
                      <div style={{fontSize:10.5,color:"rgba(255,255,255,.45)",fontWeight:500,marginTop:1,whiteSpace:"nowrap",textOverflow:"ellipsis",overflow:"hidden"}}>{w.city||w.platform||""}</div>
                      {w.cancel_at_period_end && <div style={{fontSize:9.5,color:"#fbbf24",fontWeight:700,marginTop:2,textTransform:"uppercase",letterSpacing:.4}}>{t("app.view.cancelPending")}</div>}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
    </>
  );

  return (
    <div style={{display:"flex",height:"calc(100vh - 64px)",overflow:"hidden"}}>
      {/* Sidebar — navy gradient matching brand (desktop rail, ≥768px only) */}
      {!isMobile && (
        <div style={{width:232,background:`linear-gradient(165deg, #0f172a 0%, ${C.blueDark} 100%)`,borderInlineEnd:"1px solid rgba(255,255,255,.08)",padding:"22px 14px",flexShrink:0,display:"flex",flexDirection:"column",overflowY:"auto"}}>
          {sidebarInner}
        </div>
      )}
      {/* Mobile nav drawer (<768px) — the SAME sidebar content in a fixed
          overlay above the top bar AND the Help widget (z 940/950 — above
          help's 900, below modals at z 1000). Closes on backdrop click, nav
          selection, or Escape. Logical inset props keep it on the correct
          side in Arabic RTL. */}
      {isMobile && mobileNavOpen && (
        <>
          <div onClick={closeMobileNav} style={{position:"fixed",inset:0,background:"rgba(15,23,42,.45)",zIndex:940,animation:"nvFadeIn .18s ease"}}/>
          <div role="dialog" aria-modal="true" aria-label={t("app.view.navigation")} style={{position:"fixed",top:0,bottom:0,insetInlineStart:0,width:"min(85vw, 300px)",zIndex:950,background:`linear-gradient(165deg, #0f172a 0%, ${C.blueDark} 100%)`,borderInlineEnd:"1px solid rgba(255,255,255,.08)",padding:"22px 14px",display:"flex",flexDirection:"column",overflowY:"auto",animation:"nvFadeIn .18s ease"}}>
            {sidebarInner}
          </div>
        </>
      )}

      {/* Main */}
      <div data-portal-scroll style={{flex:1,overflowY:"auto",background:C.bg2}}>
        {/* Top banner — workspace + channel switcher. Hidden on Subscriptions and
            Team (page-specific headers instead) and on admin views (no workspace context). */}
        {nav !== "subscriptions" && nav !== "team" && !nav.startsWith("admin_") && (
        <div className="nv-ws-banner" style={{position:"sticky",top:0,zIndex:50,background:"#fff",borderBottom:`1px solid ${C.border}`,padding:"14px 28px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          {hasWorkspaces&&ws?(
            <>
              <div style={{width:10,height:10,borderRadius:5,background:ws.color||C.blue,flexShrink:0,boxShadow:`0 0 0 3px ${(ws.color||C.blue)}22`}}/>
              <span style={{fontSize:14.5,fontWeight:700,color:C.text,letterSpacing:"-.3px"}}>{ws.name}</span>
              <span style={{fontSize:13,color:C.muted,fontWeight:500}}>· {ws.city||""}{ws.area?` (${ws.area})`:""}</span>
              {ws.market==="uae"&&<span style={{fontSize:10,padding:"3px 9px",borderRadius:12,background:C.green10,color:C.greenDark,fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}>{t("app.view.uaeBadge")}</span>}
              {/* Channel/platform switcher */}
              {channels.length>0&&(
                <>
                  <span style={{color:C.border,margin:"0 6px",fontSize:18}}>·</span>
                  <div className="nv-chpills" style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {channels.map(c=>{
                      const sel=(chId||(nav==="config"?channels[0]?.id:null))===c.id;
                      return (
                      <button key={c.id} onClick={()=>setChId(c.id)}
                        style={{padding:"5px 12px",borderRadius:8,border:`1.5px solid ${sel?c.color:C.border}`,background:sel?c.color+"15":"#fff",color:sel?c.color:C.muted,cursor:"pointer",fontSize:11.5,fontWeight:sel?700:600,fontFamily:"inherit",letterSpacing:"-.1px",transition:"all .15s"}}>
                        {c.platform}
                      </button>
                      );
                    })}
                    {/* "All" pill — hidden on Configuration tab (platform-specific settings) */}
                    {nav!=="config"&&(
                      <button onClick={()=>setChId(null)}
                        style={{padding:"5px 12px",borderRadius:8,border:`1.5px solid ${!chId?C.blue:C.border}`,background:!chId?C.blue10:"#fff",color:!chId?C.blueDark:C.muted,cursor:"pointer",fontSize:11.5,fontWeight:!chId?700:600,fontFamily:"inherit",letterSpacing:"-.1px",transition:"all .15s"}}>
                        {t("app.view.all")}
                      </button>
                    )}
                  </div>
                </>
              )}
              {/* Toolbar slot — page-specific controls aligned with the channel
                  pills. Daily Log & Insights share the date filter; Daily Log
                  adds Export + Add; Price Tool adds "Add menu item". Hidden on
                  pages that don't need it. */}
              <div className="nv-toolbar" style={{flex:"1 1 auto",minWidth:0,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:toolbarNarrow?"flex-start":"flex-end",alignSelf:"flex-start"}}>
                {(nav==="dailylog"||nav==="analysis"||nav==="overview") && (
                  <RangeFilter range={range} setRange={setRange} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo}/>
                )}
                {/* Daily Log: Export CSV + ALL THREE "+ Add" buttons live in the
                    toolbar (shown together, always). "+ Add log entry" switches
                    to the log tab and opens the log add-form; "+ Add CPC spend"
                    switches to the CPC tab and opens the CPC add-form; "+ Add
                    platform contribution" switches to the cofund tab and opens
                    the platform-contribution add-form. */}
                {nav==="dailylog" && (
                  <>
                    <button onClick={()=>setDlExportTick(n=>n+1)}
                      style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.sub,cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit",display:"flex",alignItems:"center",gap:7}}
                      title={t("app.view.exportCsvTitle")}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      {t("app.view.exportCsv")}
                    </button>
                    <button onClick={()=>{ setDlTab("log"); setDlAddOpen(true); }}
                      style={{padding:"7px 14px",borderRadius:8,border:"none",background:C.blue,color:"#fff",cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit"}}>
                      {t("app.view.addLogEntry")}
                    </button>
                    <button onClick={()=>{ setDlTab("cpc"); setDlCpcAddTick(n=>n+1); }}
                      style={{padding:"7px 14px",borderRadius:8,border:"none",background:C.blue,color:"#fff",cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit"}}>
                      {t("app.view.addCpcSpend")}
                    </button>
                    <button onClick={()=>{ setDlTab("cofund"); setDlCoFundAddTick(n=>n+1); }}
                      style={{padding:"7px 14px",borderRadius:8,border:"none",background:C.blue,color:"#fff",cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit"}}>
                      {t("app.view.addCoFund")}
                    </button>
                  </>
                )}
                {nav==="pricetool" && paidAccess && (
                  <button onClick={()=>setPtAddTick(n=>n+1)}
                    style={{padding:"7px 14px",borderRadius:8,border:"none",background:C.blue,color:"#fff",cursor:"pointer",fontSize:12.5,fontWeight:600,fontFamily:"inherit"}}>
                    {t("app.view.addMenuItem")}
                  </button>
                )}
                {!(nav==="dailylog"||nav==="analysis"||nav==="pricetool"||nav==="overview") && (
                  <span style={{fontSize:11.5,color:C.muted2,fontWeight:500}}>{ws.menu?.length||0} {t("app.view.itemsSuffix")} · {ws.history?.length||0} {t("app.view.recordsSuffix")}</span>
                )}
              </div>
            </>
          ):(
            <span style={{fontSize:13,color:C.muted}}>{t("app.view.noWorkspacesYet")}</span>
          )}
        </div>
        )}

        {/* Cancellation-pending banner — shown above main content for the currently selected workspace.
            Suppressed on Subscriptions page since per-workspace status is shown inline there. */}
        {nav !== "subscriptions" && nav !== "team" && !nav.startsWith("admin_") && ws?.cancel_at_period_end && (
          <div className="nv-cancel-banner" style={{margin:"0 24px",marginTop:16,padding:"12px 18px",background:"#fef3c7",border:"1px solid #fde68a",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18}}>⏳</span>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#92400e"}}>{t("app.view.cancellationPendingPrefix")} {ws.name}</div>
                <div style={{fontSize:12,color:"#a16207",marginTop:1}}>
                  {t("app.view.cancellationPendingBody1")}{ws.cancel_scheduled_for ? new Date(ws.cancel_scheduled_for).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : t("app.view.cancellationPendingFallbackDate")}{t("app.view.cancellationPendingBody2")}
                </div>
              </div>
            </div>
            <button onClick={()=>setNav("subscriptions")} style={{padding:"7px 13px",borderRadius:7,border:"1px solid #d97706",background:"#fff",color:"#92400e",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{t("app.view.manage")}</button>
          </div>
        )}

        {isSuperAdmin && nav.startsWith("admin_") ? (
          /* Admin views — super admin only. Fail CLOSED: gate the render on
             isSuperAdmin (not just the nav string) so a non-super-admin who
             deep-links/refreshes into /admin/* never paints the admin shell,
             even for the single frame before the redirect effect (3206) bounces
             nav back to overview. Admin DATA is already protected server-side
             (every /api/admin/* is behind requireSuperAdmin → 403), but the
             shell should never appear either. */
          <>
            {nav === "admin_overview"     && <OverviewTab/>}
            {nav === "admin_accounts"     && <AccountsTab onInspect={(id) => setInspectAccountId(id)}
              onSetupFeed={feedSetupHandler(feedOn, (acct) => { setFeedPrefill(acct); go("admin_feed"); })}/>}
            {nav === "admin_feed"         && <AdminFeed prefillAccount={feedPrefill}/>}
            {nav === "admin_audit"        && <AuditTab/>}
            {nav === "admin_super_admins" && <SuperAdminsTab/>}
          </>
        ) : !hasWorkspaces && !isSuperAdmin ?(
          <div style={{padding:"40px 24px",maxWidth:920,margin:"0 auto"}}>
            {Object.keys(workspaces || {}).length === 0 ? (
              <>
                {/* Welcome banner — only when user has zero workspaces ever (fresh signup) */}
                <div style={{background:`linear-gradient(135deg,#0f172a 0%,${C.blueDark} 100%)`,borderRadius:18,padding:"40px 44px",color:"#fff",marginBottom:24,position:"relative",overflow:"hidden",boxShadow:"0 12px 32px rgba(15,23,42,0.18)"}}>
                  <div style={{position:"absolute",top:-80,right:-40,width:280,height:280,background:`radial-gradient(circle,${C.blue}44 0%,transparent 65%)`,borderRadius:"50%",pointerEvents:"none"}}/>
                  <div style={{position:"absolute",bottom:-100,left:-60,width:320,height:320,background:`radial-gradient(circle,${C.green}33 0%,transparent 60%)`,borderRadius:"50%",pointerEvents:"none"}}/>
                  <div style={{position:"relative"}}>
                    <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.1)",padding:"5px 12px",borderRadius:20,marginBottom:18,fontSize:11.5,fontWeight:600,letterSpacing:0.3}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:C.green,boxShadow:`0 0 8px ${C.green}`}}/>
                      {t("app.view.welcomeToNetrev")}
                    </div>
                    <h2 style={{fontSize:32,fontWeight:800,letterSpacing:"-1px",lineHeight:1.15,margin:"0 0 14px",maxWidth:600}}>
                      {t("app.view.setUpFirstRestaurant")}
                    </h2>
                    <p style={{fontSize:15,color:"#cbd5e1",lineHeight:1.65,maxWidth:560,marginBottom:0}}>
                      {t("app.view.welcomeBody1")}<b style={{color:"#fff"}}>{t("app.view.workspaceWord")}</b>{t("app.view.welcomeBody2")}
                    </p>
                  </div>
                </div>

                {/* Steps preview */}
                <div className="nv-welcome-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
                  {[
                    ["1",t("app.view.step1Title"),t("app.view.step1Desc")],
                    ["2",t("app.view.step2Title"),t("app.view.step2Desc")],
                    ["3",t("app.view.step3Title"),t("app.view.step3Desc")],
                  ].map(([n,stepTitle,stepDesc])=>(
                    <div key={n} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 20px"}}>
                      <div style={{width:28,height:28,borderRadius:8,background:`linear-gradient(135deg,${C.blue} 0%,${C.blueDark} 100%)`,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,marginBottom:10}}>{n}</div>
                      <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:5,letterSpacing:"-.2px"}}>{stepTitle}</div>
                      <div style={{fontSize:12.5,color:C.muted,lineHeight:1.5}}>{stepDesc}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              /* All workspaces archived — show a softer prompt */
              <div style={{padding:"20px 24px",marginBottom:20,background:"#fff",borderRadius:12,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14}}>
                <div style={{width:36,height:36,background:"#f1f5f9",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:18}}>📋</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{t("app.view.noActiveWorkspaces")}</div>
                  <div style={{fontSize:12.5,color:C.muted}}>{t("app.view.noActiveWorkspacesBody")}</div>
                </div>
              </div>
            )}

            {/* Workspace creation + Subscriptions management */}
            <AdminSubscriptions workspaces={workspaces} setWorkspaces={setWorkspaces} account={account} user={user} refreshAccount={refreshAccount} onShowUpgrade={onUpgrade} onCreated={onFirstWorkspaceCreated}/>
          </div>
        ):(
          <>
            {["overview","analysis","pricetool","dailylog","config"].includes(nav) && !ws ? (
              <div style={{padding:48, textAlign:"center", color:C.muted}}>
                <div style={{fontSize:16, fontWeight:600, color:C.text, marginBottom:6}}>{t("app.view.noWorkspaceSelected")}</div>
                <div style={{fontSize:13, lineHeight:1.6, maxWidth:380, margin:"0 auto"}}>
                  {t("app.view.createWsToTrackPrefix")}<b>{t("app.view.subscriptionsWord")}</b>{t("app.view.createWsToTrackSuffix")}{isSuperAdmin ? t("app.view.createWsToTrackAdmin") : t("app.view.createWsToTrackPeriod")}
                </div>
              </div>
            ) : (
              <>
                {nav==="overview"      && <AdminOverview ws={ws} cfg={cfg} currency={currency} chId={chId} range={range} setRange={setRange} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} onNavigate={setNav}/>}
                {nav==="analysis"      && <AdminAnalysis ws={ws} cfg={cfg} currency={currency} chId={chId} range={range} customFrom={customFrom} customTo={customTo} onNavigate={setNav} paidAccess={paidAccess} onShowUpgrade={onUpgrade}/>}
                {nav==="pricetool"     && (paidAccess
                  ? <AdminPriceTool ws={ws} tiers={tiers} cfg={cfg} currency={currency} chId={chId} setWorkspaces={setWorkspaces} wsId={wsId} account={account} onShowUpgrade={onUpgrade} refreshWorkspaces={refreshWorkspaces} addTick={ptAddTick}/>
                  : <LockedFeature title={t("app.lock.priceToolTitle")} body={t("app.lock.priceToolBody")} onUpgrade={onUpgrade}/>)}
                {nav==="dailylog"      && <AdminDailyLog key={wsId} ws={ws} cfg={cfg} userPlan="pro" currency={currency} chId={chId} setWorkspaces={setWorkspaces} wsId={wsId} refreshWorkspaces={refreshWorkspaces} refreshAccount={refreshAccount} range={range} setRange={setRange} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} addOpen={dlAddOpen} setAddOpen={setDlAddOpen} exportTick={dlExportTick} dlTab={dlTab} setDlTab={setDlTab} cpcAddTick={dlCpcAddTick} coFundAddTick={dlCoFundAddTick}/>}
                {nav==="config"        && teamContext?.role === "brand_owner" && <AdminConfig cfg={cfg} setCfg={setCfg} tiers={tiers} setTiers={setTiers} workspaces={activeWorkspaces} setWorkspaces={setWorkspaces} wsId={wsId} chId={chId} account={account} onShowUpgrade={onUpgrade}/>}
              </>
            )}
            {nav==="subscriptions" && teamContext?.role === "brand_owner" && <AdminSubscriptions workspaces={workspaces} setWorkspaces={setWorkspaces} account={account} user={user} refreshAccount={refreshAccount} onShowUpgrade={onUpgrade} onCreated={onFirstWorkspaceCreated}/>}
            {nav==="team" && teamContext?.role === "brand_owner" && <TeamPage workspaces={workspaces} account={account} teamContext={teamContext} myAccounts={myAccounts}/>}
          </>
        )}
        {/* #5: persistent compliance disclaimer — shown on data/finance pages. */}
        {["overview","analysis","pricetool","dailylog"].includes(nav) && (
          <div style={{margin:"8px 24px 28px",padding:"12px 16px",background:C.bg2,border:`1px solid ${C.border2}`,borderRadius:10,fontSize:11,lineHeight:1.6,color:C.muted}}>
            <strong style={{color:C.sub,fontWeight:600}}>{t("app.view.disclaimerLabel")}</strong>{t("app.view.disclaimerBody")}
          </div>
        )}
      </div>
      {/* Inspect side drawer — only ever shown when an admin clicked Inspect on an account row */}
      {inspectAccountId && (
        <SnapshotPanel
          accountId={inspectAccountId}
          onClose={() => setInspectAccountId(null)}
        />
      )}
      <HelpWidget user={user}/>
    </div>
  );
}

// ── Help / support widget — floating button (all logged-in views) ─────
function HelpWidget({user}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [err, setErr] = useState("");

  async function send() {
    if (status === "sending") return;
    if (!subject.trim()) { setErr("Please add a subject."); setStatus("error"); return; }
    if (message.trim().length < 10) { setErr("Please describe the issue in a bit more detail (at least 10 characters)."); setStatus("error"); return; }
    setStatus("sending"); setErr("");
    try {
      const attachments = await Promise.all(files.map(f => new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve({ filename: f.name, contentType: f.type, content: String(r.result).split(",")[1] || "" });
        r.onerror = () => reject(new Error(`Couldn't read ${f.name}`));
        r.readAsDataURL(f);
      })));
      const session = await getCurrentSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");
      const res = await fetch(`${API_BASE}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ subject, message, attachments }),
      }).catch(() => { throw new Error(t(API_ERROR_NETWORK_KEY)); });
      // Express answers a sentence, .NET a sentence or a code: apiErrorText
      // shows a sentence as sent and turns a code into one. A body that isn't
      // JSON (an nginx page) is the generic sentence, never the parser's
      // message, and a 2xx without a JSON body is not "sent". With the status,
      // a 5xx shows only a crafted sentence: Resend's relayed answer (which
      // can quote the user's address) or an exception's message reads as the
      // generic sentence.
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) throw new Error(apiErrorText(data, t, { status: res.status }));
      setStatus("sent");
      setTimeout(() => {
        setOpen(false); setStatus("idle"); setSubject(""); setMessage(""); setFiles([]);
      }, 2500);
    } catch (e) {
      setErr(e.message || t(API_ERROR_UNKNOWN_KEY));
      setStatus("error");
    }
  }

  return (
    <>
      {open && (
        <div style={{position:"fixed",insetInlineEnd:20,bottom:80,width:320,zIndex:900,background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:18,boxShadow:C.s4,display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{t("app.help.needHelp")}</div>
            <div style={{fontSize:12,color:C.muted,marginTop:3,lineHeight:1.5}}>{t("app.help.sendMessageReply")}</div>
          </div>
          {status==="sent" ? (
            <div style={{padding:"10px 12px",background:C.green10,border:`1px solid ${C.green}33`,borderRadius:8,fontSize:12.5,color:C.greenDark,fontWeight:600,lineHeight:1.5}}>
              {t("app.help.thanksReplyPrefix")} {user?.email}.
            </div>
          ) : (
            <>
              <Inp label={t("app.help.subject")} value={subject} onChange={setSubject} placeholder={t("app.help.subjectPlaceholder")}/>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:10.5,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7}}>{t("app.help.message")}</label>
                <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={4} placeholder={t("app.help.messagePlaceholder")}
                  style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:8,outline:"none",color:C.text,padding:"10px 12px",width:"100%",fontSize:13.5,fontFamily:"inherit",fontWeight:500,resize:"vertical",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {files.length > 0 && files.map((f, i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.sub,background:C.bg3,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 8px"}}>
                    <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
                    <button onClick={()=>setFiles(files.filter((_, x)=>x!==i))} title={t("app.help.removeTooltip")} style={{border:"none",background:"none",cursor:"pointer",color:C.muted,fontSize:13,lineHeight:1,padding:0,fontFamily:"inherit"}}>✕</button>
                  </div>
                ))}
                {files.length < 3 && (
                  <label style={{fontSize:12,color:C.blue,fontWeight:600,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5,width:"fit-content"}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    {t("app.help.attachScreenshots")} ({files.length}/3)
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple style={{display:"none"}}
                      onChange={e=>{
                        const picked = Array.from(e.target.files || []);
                        e.target.value = "";
                        const ok = [];
                        for (const f of picked) {
                          if (!/^image\//.test(f.type)) { setErr(`${f.name} isn't an image.`); setStatus("error"); continue; }
                          if (f.size > 4 * 1024 * 1024) { setErr(`${f.name} is over 4 MB — please crop or compress it.`); setStatus("error"); continue; }
                          ok.push(f);
                        }
                        if (ok.length) { setFiles(prev=>[...prev, ...ok].slice(0, 3)); if (status === "error") { setStatus("idle"); setErr(""); } }
                      }}/>
                  </label>
                )}
              </div>
              {status==="error" && err && (
                <div style={{padding:"9px 12px",background:C.red10,border:`1px solid ${C.red}33`,borderRadius:8,fontSize:12,color:C.redDark,fontWeight:500,lineHeight:1.5}}>{err}</div>
              )}
              <Btn full onClick={send} disabled={status==="sending"}>
                {status==="sending" ? t("app.help.sending") : t("app.help.send")}
              </Btn>
            </>
          )}
        </div>
      )}
      <button onClick={()=>setOpen(o=>!o)} title={t("app.help.helpSupportTooltip")} aria-label={t("app.help.helpSupportTooltip")}
        style={{position:"fixed",insetInlineEnd:20,bottom:20,width:48,height:48,borderRadius:24,border:"none",background:C.blue,color:"#fff",cursor:"pointer",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:C.sBlue,transition:"all .15s",fontFamily:"inherit"}}
        onMouseEnter={e=>{e.currentTarget.style.background=C.blueDark;}}
        onMouseLeave={e=>{e.currentTarget.style.background=C.blue;}}>
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        )}
      </button>
    </>
  );
}


// ── Admin: Overview ──────────────────────────────────────────────────
// ─── Lightweight markdown renderer for AI output ────────────────────

// ─── PDF export — opens print-friendly view in new window ────────────





// ── Admin: Performance ───────────────────────────────────────────────

// ── Admin: Price Tool ────────────────────────────────────────────────

// ── Channel Settings (commission + card fee) ─────────────────────────

// ── Admin: Daily Log ─────────────────────────────────────────────────


// ── Admin: Subscriptions ─────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════
// CardRequiredModal — appears when a pre-trial user tries to create a workspace.
// Forces them to add card-on-file (start trial) OR pay now (skip trial),
// before the workspace gets created. The workspace data is stashed in
// localStorage; the webhook completes creation server-side after Stripe payment,
// OR the frontend reads the stash on return and completes creation.
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// RECOVER PASSWORD — handles password reset link from email
// ═══════════════════════════════════════════════════════════════════════
function RecoverPassword({onComplete}) {
  const { t } = useT();
  const [pwd,setPwd]=useState("");
  const [pwd2,setPwd2]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [done,setDone]=useState(false);

  async function submit(){
    if(!pwd||!pwd2){setErr("Please fill in both password fields");return;}
    if(pwd.length<8){setErr("Password must be at least 8 characters");return;}
    if(pwd!==pwd2){setErr("Passwords don't match");return;}
    setLoading(true);setErr("");
    try{
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if(error){
        setErr(error.message||"Couldn't update password");
        setLoading(false);
        return;
      }
      setDone(true);
      setTimeout(()=>onComplete(),2500);
    }catch(e){
      setErr("Connection error — please try again");
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:"100vh",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 24px",fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"}}>
      <div style={{width:"100%",maxWidth:420}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:32,justifyContent:"center"}}>
          <img src="/logo.svg" alt="Netrev.ai" width={34} height={34} style={{borderRadius:9,display:"block"}}/>
          <span style={{fontSize:17,fontWeight:700,color:"#0f172a"}}>Netrev<span style={{color:"#3b82f6"}}>.ai</span></span>
        </div>

        {done ? (
          <div style={{textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:14,background:"linear-gradient(135deg,#10b981,#059669)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",boxShadow:"0 8px 20px rgba(16,185,129,0.3)"}}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h1 style={{fontSize:26,fontWeight:600,color:"#0f172a",letterSpacing:"-.8px",margin:"0 0 8px"}}>{t("app.recover.passwordUpdated")}</h1>
            <p style={{fontSize:14,color:"#64748b",fontWeight:400,lineHeight:1.55,margin:0}}>{t("app.recover.redirectingSignIn")}</p>
          </div>
        ) : (
          <>
            <div style={{marginBottom:30}}>
              <h1 style={{fontSize:30,fontWeight:600,color:"#0f172a",letterSpacing:"-.9px",margin:"0 0 8px",lineHeight:1.2}}>{t("app.recover.setNewPassword")}</h1>
              <p style={{fontSize:14.5,color:"#64748b",fontWeight:400,lineHeight:1.55,margin:0}}>{t("app.recover.chooseStrongPassword")}</p>
            </div>
            {err && <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:9,padding:"11px 14px",fontSize:13.5,color:"#991b1b",marginBottom:18,fontWeight:500}}>{err}</div>}
            <div style={{marginBottom:16}}>
              <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("app.recover.newPassword")}</label>
              <input value={pwd} onChange={e=>setPwd(e.target.value)} type="password" placeholder={t("app.recover.atLeast8")} autoFocus autoComplete="new-password" style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
            </div>
            <div style={{marginBottom:22}}>
              <label style={{fontSize:13,fontWeight:600,color:"#0f172a",display:"block",marginBottom:8}}>{t("app.recover.confirmNewPassword")}</label>
              <input value={pwd2} onChange={e=>setPwd2(e.target.value)} type="password" placeholder={t("app.recover.repeatPassword")} autoComplete="new-password" onKeyDown={e=>e.key==="Enter"&&submit()} style={{width:"100%",padding:"13px 16px",borderRadius:9,border:"1px solid transparent",fontSize:14,color:"#0f172a",background:"#f1f5f9",fontFamily:"inherit",outline:"none",transition:"all 0.15s",boxSizing:"border-box"}} onFocus={e=>{e.target.style.background="#fff";e.target.style.borderColor="#3b82f6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.12)"}} onBlur={e=>{e.target.style.background="#f1f5f9";e.target.style.borderColor="transparent";e.target.style.boxShadow="none"}}/>
            </div>
            <button onClick={submit} disabled={loading} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:loading?"#94a3b8":"linear-gradient(135deg,#1e1b4b 0%,#0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:loading?"none":"0 4px 14px rgba(15,23,42,0.25)"}}>
              {loading?t("app.recover.updating"):t("app.recover.updatePassword")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// STRIPE BILLING COMPONENTS (Round B)
// ═══════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────
// HOOK — fetch account + access status from Supabase
// ─────────────────────────────────────────────────────────────────────
function useAccountStatus(user) {
  const [account, setAccount] = useState(null);
  const [status, setStatus] = useState("loading");
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!user?.email) return;
    if (user.plan === "superadmin") {
      setAccount({ name: "Super Admin", subscription_status: "free_override", free_override: true });
      setStatus("active");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const authUser = await getCurrentUser();
        if (!authUser) { if (!cancelled) { setStatus("no_account"); setLoading(false); } return; }

        const { data: acc, error } = await getAccountByOwner(authUser.id);
        if (cancelled) return;
        if (error) throw error;
        if (!acc) { setStatus("no_account"); setLoading(false); return; }

        setAccount(acc);
        // The full status ladder (free-override expiry, trial states, paywall
        // fall-throughs) lives in lib/accountAccess so it stays unit-testable
        // and in lock-step with the DB's has_active_access (migration 029).
        setStatus(computeAccountStatus(acc));
      } catch (e) {
        if (!cancelled) { console.error("useAccountStatus:", e); setStatus("error"); }
      }
      if (!cancelled) setLoading(false);
    })();

    // Auto-refresh so trial expiry / payment events reflect without manual refresh.
    // Pause while the tab is hidden (idle background tabs shouldn't poll); the
    // focus handler below refreshes immediately when the user returns.
    const interval = setInterval(() => {
      if (!cancelled && !document.hidden) setRefreshKey(k => k + 1);
    }, 60000);

    // Refresh when tab regains focus (catches users coming back from Stripe Checkout)
    const onFocus = () => { if (!cancelled) setRefreshKey(k => k + 1); };
    window.addEventListener("focus", onFocus);

    // Back-button return from a SAME-TAB payment redirect restores the page
    // from the browser's back/forward cache with all state frozen (stale
    // "trialing" account, modal still open) and fires NEITHER focus NOR
    // visibilitychange — pageshow with event.persisted is the only signal.
    // Without this, a user who paid on Stripe's hosted page and pressed Back
    // kept seeing pre-payment UI until the 60s poll.
    const onPageShow = (e) => { if (e.persisted && !cancelled) setRefreshKey(k => k + 1); };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [user?.email, refreshKey]);

  return { account, status, loading, refresh: () => setRefreshKey(k => k + 1) };
}

function timeUntil(targetISO) {
  if (!targetISO) return null;
  const ms = new Date(targetISO) - new Date();
  if (ms < 0) return { expired: true, label: "expired" };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (days > 1) return { expired: false, label: `${days} days`, days, urgent: days < 3 };
  if (days === 1) return { expired: false, label: "1 day", days, urgent: true };
  if (hours > 1) return { expired: false, label: `${hours} hours`, days: 0, urgent: true };
  if (hours === 1) return { expired: false, label: "1 hour", days: 0, urgent: true };
  if (minutes > 1) return { expired: false, label: `${minutes} min`, days: 0, urgent: true };
  return { expired: false, label: "<1 min", days: 0, urgent: true };
}

// Paid-only feature placeholder (Price Tool during a trial). Greyed panel with
// one action: open the subscribe modal.
function LockedFeature({ title, body, onUpgrade }) {
  const { t: tr } = useT();
  return (
    <div className="nv-page" style={{padding:24}}>
      <div style={{maxWidth:560,margin:"40px auto",padding:"28px 28px 24px",borderRadius:14,border:"1px dashed #cbd5e1",background:"#f8fafc",textAlign:"center",opacity:.95}}>
        <div style={{fontSize:28,marginBottom:10}} aria-hidden="true">🔒</div>
        <div style={{fontSize:17,fontWeight:700,color:"#0f172a",letterSpacing:"-.3px",marginBottom:8}}>{title}</div>
        <div style={{fontSize:13.5,color:"#64748b",lineHeight:1.6,marginBottom:18}}>{body}</div>
        <button onClick={onUpgrade} style={{padding:"10px 18px",borderRadius:9,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{tr("app.lock.subscribeToUnlock")}</button>
      </div>
    </div>
  );
}

function TrialBanner({ account, onUpgrade }) {
  const { t: tr } = useT();
  const [, setTick] = useState(0);
  useEffect(() => {
    // Tick the countdown label; skip while hidden (no one's watching it).
    const i = setInterval(() => { if (!document.hidden) setTick(t => t + 1); }, 60000);
    return () => clearInterval(i);
  }, []);
  if (!account) return null;
  // Comped account with a TIMED override: show the free-access countdown
  // (works like the trial banner — subscribe before it ends, or ask for an
  // extension). No-expiry overrides show nothing, as before, and accounts
  // that ALSO pay for their own active subscription see nothing — their
  // access doesn't end when the override does.
  const overrideCountdown = isOverrideCapped(account) && account.free_override_expires_at;
  if (isOverrideActive(account) && !overrideCountdown) return null;
  if (!overrideCountdown && account.subscription_status !== "trialing") return null;
  const t = timeUntil(overrideCountdown ? account.free_override_expires_at : account.trial_ends_at);
  if (!t || t.expired) return null;
  // No-card trial (migrations 041/043): no Stripe subscription yet. The banner
  // IS the "add a card" prompt — calm for the first 5 days, urgent for the
  // last 2 so nobody is surprised by the paywall on day 7.
  const noCard = !overrideCountdown && !account.stripe_subscription_id;
  const urgent = t.urgent || (noCard && (t.days ?? 0) <= 2);
  const bg = urgent ? "linear-gradient(90deg,#fef3c7 0%,#fde68a 100%)" : "linear-gradient(90deg,#e0e7ff 0%,#c7d2fe 100%)";
  const color = urgent ? "#92400e" : "#3730a3";
  const accent = urgent ? "#d97706" : "#4f46e5";
  return (
    <div style={{background:bg,borderBottom:`1px solid ${accent}33`,padding:"10px 24px",display:"flex",alignItems:"center",justifyContent:"center",gap:14,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:8,height:8,borderRadius:4,background:accent,animation:urgent?"pulse 1.5s ease-in-out infinite":"none"}}/>
        <span style={{fontSize:13.5,fontWeight:600,color,letterSpacing:"-.1px"}}>{urgent?"⏰ ":""}{tr(overrideCountdown ? "app.trial.freeAccessEndsInPrefix" : "app.trial.endsInPrefix")} {t.label}{noCard ? ` · ${tr(urgent ? "app.trial.noCardUrgentHint" : "app.trial.noCardHint")}` : ""}</span>
      </div>
      <button onClick={onUpgrade} style={{padding:"6px 14px",background:"#0f172a",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>{tr(noCard ? "app.trial.addCard" : "app.trial.subscribeNow")}</button>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  );
}

function SubscriptionStatus({ account, status }) {
  const { t: tr } = useT();
  if (!account) return null;
  if (account.free_override || account.subscription_status === "free_override") {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#f0fdf4",color:"#15803d",border:"1px solid #86efac",borderRadius:20,fontSize:11.5,fontWeight:600}}>
        <span style={{width:6,height:6,borderRadius:3,background:"#22c55e"}}/>{tr("app.status.free")}
      </span>
    );
  }
  if (status === "active" && account.subscription_status === "active") {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#f0fdf4",color:"#15803d",border:"1px solid #86efac",borderRadius:20,fontSize:11.5,fontWeight:600}}>
        <span style={{width:6,height:6,borderRadius:3,background:"#22c55e"}}/>{account.subscription_plan === "annual" ? tr("app.status.annual") : tr("app.status.monthly")}
      </span>
    );
  }
  if (account.subscription_status === "trialing") {
    // Pre-trial: account created but no workspaces yet → trial hasn't begun
    if (!account.trial_ends_at) {
      return (
        <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#eff6ff",color:"#1e40af",border:"1px solid #93c5fd",borderRadius:20,fontSize:11.5,fontWeight:600}}>
          <span style={{width:6,height:6,borderRadius:3,background:"#3b82f6"}}/>{tr("app.status.trialStartsOnFirstWs")}
        </span>
      );
    }
    const t = timeUntil(account.trial_ends_at);
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#fef3c7",color:"#92400e",border:"1px solid #fcd34d",borderRadius:20,fontSize:11.5,fontWeight:600}}>
        <span style={{width:6,height:6,borderRadius:3,background:"#f59e0b"}}/>{tr("app.status.trialPrefix")} {t?.label || "—"}
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5",borderRadius:20,fontSize:11.5,fontWeight:600}}>
        <span style={{width:6,height:6,borderRadius:3,background:"#ef4444"}}/>{tr("app.status.paymentFailed")}
      </span>
    );
  }
  return null;
}

function UpgradeModal({ open, onClose, account, siteCount = 1, refreshAccount, refreshWorkspaces }) {
  const { t } = useT();
  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // The end-trial-now path doesn't redirect to Stripe; we just call our backend
  // which talks to Stripe synchronously. We track this separately so the loading
  // text can say "Activating…" rather than the misleading "Redirecting to Stripe…".
  const [endTrialFlow, setEndTrialFlow] = useState(false);
  // Briefly show a success state in the modal before closing — lets the user
  // see that the action completed without a jarring page reload.
  const [success, setSuccess] = useState(false);
  // 3DS trial-conversion: the bank's challenge runs on Stripe's hosted invoice
  // page, which has NO return-redirect. So we open it in a new tab and keep
  // THIS tab in the app, polling the account until the invoice.paid webhook
  // flips it active — the user never has to find their way back manually.
  const [awaitingAuth, setAwaitingAuth] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  // Escape hatch for DB↔Stripe drift: if the account row says "active" but the
  // Stripe subscription is actually dead, the already-subscribed panel would
  // otherwise remove the only way to start a fresh subscription.
  const [forceChooser, setForceChooser] = useState(false);

  useEffect(() => {
    if (!awaitingAuth || !open) return;
    const iv = setInterval(() => { if (refreshAccount) refreshAccount(); }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingAuth, open]);

  useEffect(() => {
    if (!awaitingAuth || account?.subscription_status !== "active") return;
    setAwaitingAuth(false);
    setSuccess(true);
    if (refreshWorkspaces) refreshWorkspaces();
    const tm = setTimeout(() => {
      setSuccess(false);
      setLoading(false);
      onClose && onClose();
    }, 1200);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingAuth, account?.subscription_status]);

  if (!open) return null;

  const currency = account?.billing_currency || "GBP";
  const symbol = getCurrencySymbol(currency);
  const monthlyRate = getFirstSiteRate(currency);
  const additionalRate = getAdditionalRate(currency);
  const sites = Math.max(1, siteCount);
  const monthlyTotal = calcMonthlyTotal(currency, sites);
  const annualTotal = calcAnnualTotal(currency, sites);

  // Already paid: showing the plan chooser is meaningless ("Continue to
  // Stripe" would only 409) — render the all-set panel instead. Reached when
  // the modal opens from a stale banner/paywall right after a payment lands.
  const alreadyActive = !!account?.stripe_subscription_id
    && account?.subscription_status === "active"
    && !awaitingAuth && !success && !forceChooser;

  async function startCheckout(opts = {}) {
    const skip_trial = !!opts.skip_trial;
    setLoading(true); setErr("");
    try {
      const session = await getCurrentSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");

      // If the user is already trialing with a live subscription, we can't go
      // through create-checkout (which would create a duplicate). Use the
      // dedicated end-trial-now endpoint that converts their existing
      // subscription to active immediately.
      const isAlreadyTrialing = skip_trial
        && account?.subscription_status === "trialing"
        && !!account?.stripe_subscription_id;

      if (isAlreadyTrialing) {
        setEndTrialFlow(true);   // changes button text from "Redirecting to Stripe…" to "Activating…"
        const res = await fetch(`${API_BASE}/api/stripe/end-trial-now`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ plan }),
        });
        const data = await res.json();
        if (res.status === 400 && data.code === "no_payment_method") {
          // Edge case: trial subscription exists but no card on file.
          // Send them to the billing portal to add one.
          setErr("No card on file. Opening billing portal to add one.");
          const pres = await fetch(`${API_BASE}/api/stripe/create-portal`, {
            method: "POST", headers: { "Authorization": `Bearer ${token}` },
          });
          const pdata = await pres.json();
          if (pdata.url) window.location.href = pdata.url;
          else setLoading(false);
          return;
        }
        // The trial-conversion charge needs 3D-Secure / bank authentication
        // (402 + payment_url). Ending a trial charges the card off-session, so
        // the bank's challenge can't run here — it happens on Stripe's hosted
        // invoice page. That page has no return-redirect, so open it in a NEW
        // tab and keep this one in the app: the polling effect above detects
        // the invoice.paid webhook flipping the account active. If the browser
        // blocks the popup, fall back to a same-tab redirect.
        if (data.code === "authentication_required" && data.payment_url) {
          // NOTE: passing "noopener" in the features string makes window.open
          // return null EVEN WHEN THE TAB OPENS — which made this code think
          // the popup was blocked and redirect this tab too (two Stripe tabs,
          // no app tab left). Open plainly so the blocked-check is real, then
          // sever the opener on the handle for the same security effect.
          const payTab = window.open(data.payment_url, "_blank");
          if (!payTab) { window.location.href = data.payment_url; return; }
          try { payTab.opener = null; } catch (_) { /* cross-origin nav already committed */ }
          setPaymentUrl(data.payment_url);
          setEndTrialFlow(false);
          setAwaitingAuth(true);
          setLoading(false);
          return;
        }
        if (!res.ok) throw new Error(data.error || "Failed to end trial");

        // Graceful refresh — no jarring page reload. Refetch account and
        // workspace data so the unlocked state shows up in the UI, then
        // briefly show a success state in the modal before closing.
        const refreshes = [];
        if (refreshAccount)    refreshes.push(refreshAccount());
        if (refreshWorkspaces) refreshes.push(refreshWorkspaces());
        await Promise.all(refreshes).catch(e => console.warn("post-trial refresh:", e));
        setSuccess(true);
        // Close after a brief success flash so the user sees that it worked.
        setTimeout(() => {
          setSuccess(false);
          setLoading(false);
          onClose && onClose();
        }, 900);
        return;
      }

      // Normal path: no existing subscription. Create a fresh checkout session.
      track("checkout_opened", { skip_trial, plan });
      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ plan, skip_trial }),
      });
      const data = await res.json();
      if (res.status === 409 && data.code === "subscription_exists") {
        alert("You already have an active subscription. Refreshing to sync.");
        window.location.reload();
        return;
      }
      if (!res.ok) throw new Error(data.error || "Checkout failed");
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      setErr(e.message); setLoading(false); setEndTrialFlow(false);
    }
  }

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:580,width:"100%",boxShadow:"0 25px 60px rgba(15,23,42,.35)",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{padding:"28px 32px 20px",borderBottom:"1px solid #e2e8f0"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16}}>
            <div>
              <div style={{fontSize:11.5,fontWeight:700,color:"#3b5bdb",letterSpacing:1.2,textTransform:"uppercase",marginBottom:8}}>{t("app.umodal.subscribeToNetrev")}</div>
              <h2 style={{fontSize:24,fontWeight:700,color:"#0f172a",letterSpacing:"-.6px",margin:0,lineHeight:1.2}}>{t("app.umodal.pickYourPlan")}</h2>
              <p style={{fontSize:13.5,color:"#64748b",marginTop:6,lineHeight:1.5,fontWeight:500}}>{sites} {sites === 1 ? t("app.umodal.siteSingular") : t("app.umodal.sitePlural")} • {t("app.umodal.billedIn")} {currency}</p>
            </div>
            <button onClick={onClose} style={{width:32,height:32,border:"none",background:"#f1f5f9",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        <div style={{padding:"20px 32px"}}>
          {!alreadyActive && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
            <div onClick={() => setPlan("monthly")} style={{padding:18,borderRadius:12,cursor:"pointer",border:plan === "monthly" ? "2px solid #3b5bdb" : "2px solid #e2e8f0",background:plan === "monthly" ? "#f6f8ff" : "#fff",transition:"all .15s"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{t("app.umodal.monthly")}</div>
              <div className="num" style={{fontSize:26,fontWeight:800,color:"#0f172a",letterSpacing:"-.8px"}}>{symbol}{monthlyTotal.toFixed(2)}</div>
              <div style={{fontSize:12,color:"#64748b",marginTop:4,fontWeight:500}}>{t("app.umodal.perMonth")}</div>
              <div style={{fontSize:11.5,color:"#94a3b8",marginTop:10,lineHeight:1.5}}>{t("app.umodal.cancelAnytime")}</div>
            </div>
            <div onClick={() => setPlan("annual")} style={{padding:18,borderRadius:12,cursor:"pointer",position:"relative",border:plan === "annual" ? "2px solid #3b5bdb" : "2px solid #e2e8f0",background:plan === "annual" ? "#f6f8ff" : "#fff",transition:"all .15s"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{t("app.umodal.annual")}</div>
              <div className="num" style={{fontSize:26,fontWeight:800,color:"#0f172a",letterSpacing:"-.8px"}}>{symbol}{annualTotal.toFixed(2)}</div>
              <div style={{fontSize:12,color:"#64748b",marginTop:4,fontWeight:500}}>{t("app.umodal.perYear")}</div>
              <div style={{fontSize:11.5,color:"#94a3b8",marginTop:10,lineHeight:1.5}}>{t("app.umodal.twelveMonthCommitment")}</div>
            </div>
          </div>}
          {(() => {
            const hasCardOnFile = !!account?.stripe_subscription_id;
            // ANY trialing account with a subscription on file belongs on the
            // end-trial-now path — including one whose DB trial date has
            // already passed (e.g. a comped account subscribing while its old
            // trial sub is still alive in Stripe). Requiring a FUTURE
            // trial_ends_at here sent those users to create-checkout, which
            // correctly 409s on the existing sub — an alert-and-reload loop
            // with no way to pay.
            const trialingWithSub = account?.subscription_status === "trialing";

            // Three button-set scenarios:
            // A) No card on file (pre-trial): "Add payment method · Start trial" + "Skip trial · pay now"
            //    Used when user clicks "Subscribe" from somewhere other than the gate.
            // B) Card on file, in trial: ONE button "Subscribe now · end trial & unlock features"
            //    They've already given us a card. Only meaningful choice is pay-now-to-skip-remaining-trial.
            // C) Card on file, paid: shouldn't reach here (modal shouldn't open) — fallback to a portal redirect.

            // Already paid (scenario C): the plan chooser is meaningless and
            // "Continue to Stripe" would only 409 — say so instead. Reached
            // when the modal is opened from a stale banner/paywall right after
            // a payment confirms (e.g. returning from the hosted invoice page).
            // The "choose a plan anyway" link covers DB↔Stripe drift (row says
            // active, Stripe sub dead) — create-checkout self-heals that case.
            if (alreadyActive) {
              return (
                <div style={{padding:"18px 16px",background:"#f0fdf4",border:"1px solid #86efac",borderRadius:12,textAlign:"center"}}>
                  <div style={{fontSize:14.5,fontWeight:700,color:"#15803d",marginBottom:6}}>Your subscription is already active</div>
                  <div style={{fontSize:12.5,color:"#64748b",lineHeight:1.6}}>
                    You're all set — no payment is needed. You can change your plan or payment method any time from Manage billing.
                  </div>
                  <button onClick={onClose} style={{marginTop:12,padding:"9px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    Close
                  </button>
                  <div style={{marginTop:10}}>
                    <button onClick={() => setForceChooser(true)} style={{border:"none",background:"none",color:"#94a3b8",fontSize:11.5,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                      Doesn't look right? Choose a plan anyway
                    </button>
                  </div>
                </div>
              );
            }

            if (hasCardOnFile && trialingWithSub) {
              // Waiting for the bank's 3D-Secure confirmation in the other tab —
              // this panel updates itself the moment the payment webhook lands.
              if (awaitingAuth || success) {
                return (
                  <div style={{padding:"18px 16px",background:success ? "#f0fdf4" : "#f6f8ff",border:`1px solid ${success ? "#86efac" : "#c7d2fe"}`,borderRadius:12,textAlign:"center"}}>
                    <div style={{fontSize:14.5,fontWeight:700,color:success ? "#15803d" : "#1e1b4b",marginBottom:6}}>
                      {success ? "Payment confirmed — subscription active!" : "Waiting for your bank's confirmation…"}
                    </div>
                    <div style={{fontSize:12.5,color:"#64748b",lineHeight:1.6}}>
                      {success
                        ? "Unlocking your account…"
                        : "Complete the payment authentication in the tab we just opened. This page updates automatically once your bank confirms — you don't need to come back manually. Your trial stays active until the payment is confirmed, so nothing is lost if you change your mind."}
                    </div>
                    {!success && paymentUrl && (
                      <button onClick={() => window.open(paymentUrl, "_blank", "noopener")} style={{marginTop:12,padding:"9px 14px",borderRadius:8,border:"1px solid #c7d2fe",background:"#fff",color:"#3b5bdb",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                        Reopen the payment page
                      </button>
                    )}
                  </div>
                );
              }
              return (
                <>
                  <div style={{padding:"12px 14px",background:"#f0fdf4",border:"1px solid #86efac",borderRadius:9,marginBottom:14,fontSize:12.5,color:"#15803d",fontWeight:500,display:"flex",alignItems:"flex-start",gap:8}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}><polyline points="20 6 9 17 4 12"/></svg>
                    <span>{t("app.umodal.cardOnFilePrefix")}<strong>{t("app.umodal.cardOnFileStrong")}</strong>{t("app.umodal.cardOnFileSuffix")}</span>
                  </div>
                  {err && <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,fontSize:12.5,color:"#991b1b",marginBottom:14,fontWeight:500}}>{err}</div>}
                  <button onClick={() => startCheckout({ skip_trial: true })} disabled={loading} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:loading ? "#94a3b8" : (success ? "#16a34a" : "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)"),color:"#fff",fontSize:14.5,fontWeight:600,cursor:loading ? "not-allowed" : "pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:loading ? "none" : "0 4px 14px rgba(15,23,42,.25)",transition:"background .2s"}}>
                    {success ? t("app.umodal.unlockedRefreshing")
                      : loading ? (endTrialFlow ? t("app.umodal.activating") : t("app.umodal.redirectingStripe"))
                      : t("app.umodal.payNowEndTrial")}
                  </button>
                  <p style={{fontSize:11,color:"#94a3b8",textAlign:"center",marginTop:12,lineHeight:1.5}}>
                    {endTrialFlow
                      ? t("app.umodal.usingCardOnFile")
                      : t("app.umodal.redirectSecureSsl")}
                  </p>
                </>
              );
            }

            // No card on file — the normal case for a no-card trial (migration
            // 041). Two honest choices: add a card and keep the remaining trial
            // (Stripe carries trial_ends_at — nothing is charged today), or pay
            // now to unlock every platform immediately.
            const trialEndTs = account?.trial_ends_at ? new Date(account.trial_ends_at) : null;
            const trialRunning = account?.subscription_status === "trialing" && trialEndTs && trialEndTs > new Date();
            // Stripe will not accept a trial_end under 48h away, so
            // create-checkout floors it (48h + 10min). Show the SAME date the
            // customer will actually be charged on, not the raw trial end.
            const MIN_TRIAL_END_AHEAD_MS = 48 * 3600 * 1000 + 10 * 60 * 1000;
            const chargeTs = trialEndTs ? new Date(Math.max(trialEndTs.getTime(), Date.now() + MIN_TRIAL_END_AHEAD_MS)) : null;
            const trialEndLabel = chargeTs ? chargeTs.toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
            const primaryBtn = {width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:loading ? "#94a3b8" : "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:loading ? "not-allowed" : "pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:loading ? "none" : "0 4px 14px rgba(15,23,42,.25)"};
            return (
              <>
                {err && <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,fontSize:12.5,color:"#991b1b",marginBottom:14,fontWeight:500}}>{err}</div>}
                {trialRunning ? (
                  <>
                    <button onClick={() => startCheckout({ skip_trial: false })} disabled={loading} style={primaryBtn}>
                      {loading ? t("app.umodal.redirectingStripe") : t("app.umodal.addCardKeepTrial")}
                    </button>
                    <p style={{fontSize:12,color:"#475569",textAlign:"center",marginTop:10,lineHeight:1.5,fontWeight:500}}>{t("app.umodal.noChargeUntilPrefix")}{trialEndLabel}{t("app.umodal.noChargeUntilSuffix")}</p>
                    <button onClick={() => startCheckout({ skip_trial: true })} disabled={loading} style={{width:"100%",marginTop:12,padding:"12px 16px",borderRadius:10,border:"1.5px solid #c7d2fe",background:"#fff",color:"#3730a3",fontSize:13.5,fontWeight:600,cursor:loading ? "not-allowed" : "pointer",fontFamily:"inherit"}}>
                      {t("app.umodal.payNowUnlock")}
                    </button>
                    {/* Adding a card leaves the account 'trialing', which keeps
                        the single-site and paid-feature limits. Users who open
                        this from the one-workspace alert must not pay for the
                        wrong option. */}
                    <p style={{fontSize:11.5,color:"#64748b",textAlign:"center",marginTop:8,lineHeight:1.5}}>{t("app.umodal.payNowAlsoLiftsLimit")}</p>
                  </>
                ) : (
                  <button onClick={() => startCheckout({ skip_trial: false })} disabled={loading} style={primaryBtn}>
                    {loading ? t("app.umodal.redirectingStripe") : t("app.umodal.continueToStripe")}
                  </button>
                )}
                <p style={{fontSize:11,color:"#94a3b8",textAlign:"center",marginTop:12,lineHeight:1.5}}>{t("app.umodal.redirectSecureSsl")}</p>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function TrialEndPaywall({ account, user, workspaces, onSignOut, onCancelEverything, variant = "trial" }) {
  useEffect(() => { track("paywall_seen", { variant }); }, [variant]);
  const { t } = useT();
  // variant "override": the account's FREE OVERRIDE expired (or was revoked)
  // — same keep-and-subscribe flow, honest copy.
  const isOverrideVariant = variant === "override";
  // Filter out archived workspaces — paywall is about deciding which CURRENT workspaces to keep
  const wsList = Object.values(workspaces || {}).filter(w => !w.archived_at);

  // Diagnostic logging — helps debug "workspaces disappear on refresh" reports
  useEffect(() => {
    const total = Object.keys(workspaces || {}).length;
    const archived = total - wsList.length;
    console.log(`[TrialEndPaywall] mounted | total workspaces: ${total} | active: ${wsList.length} | archived: ${archived}`);
    if (total === 0) {
      console.warn("[TrialEndPaywall] No workspaces available — check loadWorkspaces logs above.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsList.length]);

  // Default: all non-archived workspaces checked
  const [keptIds, setKeptIds] = useState(() => new Set(wsList.map(w => w.id)));

  // When wsList changes (async load from DB after refresh), make sure keptIds reflects available workspaces.
  // This prevents the "everything looks unchecked after page refresh" bug — useState only inits once.
  useEffect(() => {
    if (wsList.length === 0) return;
    setKeptIds(prev => {
      const wsIds = new Set(wsList.map(w => w.id));
      // If no overlap with existing kept set (e.g. loaded fresh after refresh), reset to "all checked"
      const intersect = [...prev].filter(id => wsIds.has(id));
      if (intersect.length === 0) return wsIds;
      // Otherwise keep current selection but drop any IDs no longer present
      return new Set(intersect);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsList.length, wsList.map(w => w.id).join(",")]);

  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);

  const currency = account?.billing_currency || "GBP";
  const symbol = getCurrencySymbol(currency);
  const monthlyFirst = getFirstSiteRate(currency);
  const monthlyAdditional = getAdditionalRate(currency);

  const keptCount = keptIds.size;
  // A comped account whose override expired may own ZERO workspaces — the
  // keep-list flow has nothing to keep, so subscribing goes through a plain
  // create-checkout instead (they create their workspace after paying).
  // Priced as the first site.
  const emptyOverrideCheckout = isOverrideVariant && wsList.length === 0;
  const billedCount = emptyOverrideCheckout ? 1 : keptCount;
  const monthlyTotal = calcMonthlyTotal(currency, billedCount);
  const annualTotal = calcAnnualTotal(currency, billedCount);
  const total = plan === "annual" ? annualTotal : monthlyTotal;
  const archiveCount = wsList.length - keptCount;
  const ctaDisabled = loading || (keptCount === 0 && !emptyOverrideCheckout);

  function toggleWorkspace(id) {
    setKeptIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function startCheckout() {
    if (keptCount === 0 && !emptyOverrideCheckout) {
      setErr("Pick at least one workspace to keep, or cancel your account.");
      return;
    }
    setLoading(true); setErr("");
    try {
      const session = await getCurrentSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");
      // Nothing to keep (zero-workspace comped account): plain subscription
      // checkout — subscribe-selected would reject an empty keep list.
      const endpoint = emptyOverrideCheckout ? "/api/stripe/create-checkout" : "/api/stripe/subscribe-selected";
      const body = emptyOverrideCheckout
        ? { plan, skip_trial: true }
        : { keep_workspace_ids: Array.from(keptIds), plan };
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Checkout failed");
      if (data.url) window.location.href = data.url;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      console.error("paywall checkout error:", e);
      setErr(e.message);
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 24px",fontFamily:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif"}}>
      <div style={{maxWidth:680,width:"100%",background:"#fff",borderRadius:16,padding:"40px 40px 36px",boxShadow:"0 25px 60px rgba(15,23,42,.15)",border:"1px solid #e2e8f0"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:28}}>
          <img src="/logo.svg" alt="Netrev.ai" width={34} height={34} style={{borderRadius:9,display:"block"}}/>
          <span style={{fontSize:17,fontWeight:700,color:"#0f172a"}}>Netrev<span style={{color:"#3b5bdb"}}>.ai</span></span>
        </div>

        <div style={{fontSize:11.5,fontWeight:700,color:"#3b5bdb",letterSpacing:1.2,textTransform:"uppercase",marginBottom:8}}>{isOverrideVariant ? t("app.paywallT.freeAccessEnded") : t("app.paywallT.trialEnded")}</div>
        <h1 style={{fontSize:26,fontWeight:700,color:"#0f172a",letterSpacing:"-.7px",margin:"0 0 10px",lineHeight:1.2}}>{t("app.paywallT.chooseWorkspaces")}</h1>
        <p style={{fontSize:14.5,color:"#64748b",lineHeight:1.6,fontWeight:500,marginBottom:24}}>
          {t("app.paywallT.youCreatedPrefix")}{wsList.length} {wsList.length === 1 ? t("app.paywallT.workspaceSingular") : t("app.paywallT.workspacePlural")}{isOverrideVariant ? t("app.paywallT.duringOverrideBody") : t("app.paywallT.duringTrialBody")}
        </p>

        {/* Workspace list with checkboxes */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden",marginBottom:20}}>
          {wsList.length === 0 ? (
            <div style={{padding:"24px",textAlign:"center",fontSize:13,color:"#94a3b8"}}>{t("app.paywallT.noWorkspacesToDisplay")}</div>
          ) : wsList.map((w, idx) => {
            const checked = keptIds.has(w.id);
            const isFirst = checked && Array.from(keptIds).indexOf(w.id) === 0;
            return (
              <label key={w.id} style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px",cursor:"pointer",borderBottom:idx === wsList.length - 1 ? "none" : "1px solid #f1f5f9",background:checked ? "#fff" : "#fafafa",transition:"background .15s"}}>
                <input type="checkbox" checked={checked} onChange={() => toggleWorkspace(w.id)} style={{width:18,height:18,accentColor:"#3b5bdb",cursor:"pointer",flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,color:checked ? "#0f172a" : "#94a3b8",letterSpacing:"-.1px"}}>
                    {w.name || t("app.paywallT.untitled")}
                  </div>
                  <div style={{fontSize:12,color:"#94a3b8",marginTop:2,fontWeight:500}}>
                    {w.market || "UK"} {w.platform ? `· ${w.platform}` : ""}
                    {!checked && <span style={{color:"#dc2626",marginInlineStart:6,fontWeight:600}}>{t("app.paywallT.willBeArchived")}</span>}
                  </div>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:checked ? "#0f172a" : "#cbd5e1",fontVariantNumeric:"tabular-nums",letterSpacing:"-.2px"}}>
                  {checked ? `${symbol}${(Array.from(keptIds).indexOf(w.id) === 0 ? monthlyFirst : monthlyAdditional).toFixed(2)}/mo` : "—"}
                </div>
              </label>
            );
          })}
        </div>

        {/* Plan toggle */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
          <button onClick={() => setPlan("monthly")} style={{padding:"12px 14px",borderRadius:10,cursor:"pointer",border:plan === "monthly" ? "2px solid #3b5bdb" : "2px solid #e2e8f0",background:plan === "monthly" ? "#f6f8ff" : "#fff",fontFamily:"inherit",textAlign:"start",transition:"all .15s"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{t("app.paywallT.monthly")}</div>
            <div style={{fontSize:18,fontWeight:800,color:"#0f172a",fontVariantNumeric:"tabular-nums",letterSpacing:"-.4px"}}>{symbol}{monthlyTotal.toFixed(2)}<span style={{fontSize:12,fontWeight:600,color:"#94a3b8"}}>/mo</span></div>
          </button>
          <button onClick={() => setPlan("annual")} style={{padding:"12px 14px",borderRadius:10,cursor:"pointer",border:plan === "annual" ? "2px solid #3b5bdb" : "2px solid #e2e8f0",background:plan === "annual" ? "#f6f8ff" : "#fff",fontFamily:"inherit",textAlign:"start",transition:"all .15s"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{t("app.paywallT.annual")}</div>
            <div style={{fontSize:18,fontWeight:800,color:"#0f172a",fontVariantNumeric:"tabular-nums",letterSpacing:"-.4px"}}>{symbol}{annualTotal.toFixed(2)}<span style={{fontSize:12,fontWeight:600,color:"#94a3b8"}}>/yr</span></div>
          </button>
        </div>

        {/* Summary */}
        <div style={{padding:"14px 16px",background:"#f8fafc",borderRadius:10,border:"1px solid #e2e8f0",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:"#0f172a"}}>
              {t("app.paywallT.keepingPrefix")} {billedCount} {billedCount === 1 ? t("app.paywallT.workspaceSingular") : t("app.paywallT.workspacePlural")}
            </div>
            {archiveCount > 0 && (
              <div style={{fontSize:12,color:"#64748b",marginTop:2,fontWeight:500}}>{archiveCount} {t("app.paywallT.willBeArchivedRetention")}</div>
            )}
          </div>
          <div style={{textAlign:"end"}}>
            <div style={{fontSize:11,fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:1}}>{t("app.paywallT.total")}</div>
            <div style={{fontSize:20,fontWeight:800,color:"#0f172a",fontVariantNumeric:"tabular-nums",letterSpacing:"-.5px"}}>
              {symbol}{total.toFixed(2)}<span style={{fontSize:12,fontWeight:600,color:"#94a3b8"}}>/{plan === "annual" ? "yr" : "mo"}</span>
            </div>
          </div>
        </div>

        {err && (
          <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,fontSize:12.5,color:"#991b1b",marginBottom:14,fontWeight:500}}>{err}</div>
        )}

        {/* CTA */}
        <button onClick={startCheckout} disabled={ctaDisabled} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:ctaDisabled ? "#94a3b8" : "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:ctaDisabled ? "not-allowed" : "pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:ctaDisabled ? "none" : "0 4px 14px rgba(15,23,42,.25)",marginBottom:12}}>
          {loading ? t("app.paywallT.redirectingStripe") : (keptCount === 0 && !emptyOverrideCheckout) ? t("app.paywallT.selectAtLeastOne") : t("app.paywallT.continueToStripe")}
        </button>

        {/* Secondary actions */}
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>{
            setConfirm({
              title: t("app.paywallT.cancelTitle"),
              body: t("app.paywallT.cancelBody"),
              confirmLabel: t("app.paywallT.cancelConfirmLabel"),
              cancelLabel: t("app.paywallT.cancelGoBack"),
              confirmVariant: "danger",
              onConfirm: () => onCancelEverything?.(),
            });
          }} style={{flex:1,padding:"10px 14px",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
            {t("app.paywallT.cancelEverything")}
          </button>
          <button onClick={onSignOut} style={{flex:1,padding:"10px 14px",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
            {t("app.paywallT.signOut")}
          </button>
        </div>

        <p style={{fontSize:11.5,color:"#94a3b8",textAlign:"center",marginTop:18,lineHeight:1.5}}>
          {t("app.paywallT.securePaymentNote")}
        </p>
      </div>
      {confirm && <ConfirmDialog {...confirm} onClose={()=>setConfirm(null)}/>}
    </div>
  );
}

function PaywallScreen({ account, status, user, onUpgrade, onSignOut }) {
  const { t } = useT();
  const isTrialExpired = status === "trial_expired";
  const isPastDue = status === "past_due";
  const isArchived = status === "archived";
  const isCanceledExpired = status === "canceled_expired";

  // Past-due users have a LIVE subscription that just failed payment → the Stripe
  // Customer Portal is correct (update card, payment retries, no new sub needed).
  // Archived users have a CANCELLED/dead subscription → the billing portal is a
  // dead end (it only manages billing, can't resubscribe, and traps them with no
  // route back). They need a fresh CHECKOUT, which on completion un-archives the
  // account + workspaces (webhook: checkout.session.completed → archived_at: null
  // + reactivate_account). So archived → onUpgrade (checkout), NOT the portal.
  // NOTE: users who still have an ACTIVE workspace/subscription never reach this
  // screen (status === "active"); they restore individual workspaces in-app via
  // restore-workspace, which adds the seat back to their existing sub. So a fresh
  // checkout here cannot double-charge — by definition there's no live sub.
  const useStripePortal = isPastDue;

  const [portalLoading, setPortalLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const session = await getCurrentSession();
      const token = session?.access_token;
      // INR (India): a failed renewal usually just needs a one-time OTP to
      // register the card's recurring e-mandate — the backend hands back the
      // open invoice's hosted payment page (which runs it) instead of the
      // Customer Portal (which can't). Everyone else gets the portal as before.
      const endpoint = account?.billing_currency === "INR" ? "payment-recovery-link" : "create-portal";
      const res = await fetch(`${API_BASE}/api/stripe/${endpoint}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else { alert(data.error || "Could not open billing portal"); setPortalLoading(false); }
    } catch (e) {
      console.error("Portal error:", e);
      alert("Couldn't open billing portal. Please try again.");
      setPortalLoading(false);
    }
  }

  let title, body, ctaLabel = t("app.paywall.subscribeNow");
  if (isTrialExpired) {
    title = t("app.paywall.trialHasEnded");
    body = t("app.paywall.trialHasEndedBody");
  } else if (isPastDue && account?.billing_currency === "INR") {
    // India: the CTA opens the open invoice's hosted page (see openPortal) —
    // a one-time verification to complete, not a card to replace.
    title = t("app.paywall.paymentNeedsVerification");
    body = t("app.paywall.paymentNeedsVerificationBodyINR");
    ctaLabel = t("app.paywall.completeVerification");
  } else if (isPastDue) {
    title = t("app.paywall.paymentFailed");
    body = t("app.paywall.paymentFailedBody");
    ctaLabel = t("app.paywall.updatePaymentMethod");
  } else if (isArchived) {
    const days = account?.data_retention_until ? Math.max(0, Math.ceil((new Date(account.data_retention_until) - new Date()) / 86400000)) : 0;
    title = t("app.paywall.accountArchived");
    body = `${t("app.paywall.archivedBody1")}${days}${days === 1 ? t("app.paywall.archivedBody2") : t("app.paywall.archivedBody2Plural")}${t("app.paywall.archivedBodyDays")}`;
    ctaLabel = t("app.paywall.resubscribe");
  } else if (isCanceledExpired) {
    title = t("app.paywall.dataRetentionExpired");
    body = t("app.paywall.dataRetentionExpiredBody");
  } else {
    title = t("app.paywall.subscriptionRequired");
    body = t("app.paywall.subscriptionRequiredBody");
  }

  const realHandleClick = useStripePortal ? openPortal : onUpgrade;

  // Only confirm for explicit re-engagement flows where billing is about to
  // restart. For past-due (just updating a card) we don't need a confirm —
  // the action is clearly remedial.
  const wantConfirm = (isArchived || isCanceledExpired);

  const handleClick = wantConfirm
    ? () => {
        setConfirm({
          title: t("app.paywall.reactivateTitle"),
          body: (
            <div>
              <div style={{fontSize:13.5,color:"#475569",lineHeight:1.6,marginBottom:14}}>
                {t("app.paywall.subscribingWill")}
              </div>
              <ul style={{margin:0,padding:"0 0 12px 18px",fontSize:13,color:"#0f172a",lineHeight:1.7}}>
                <li><b>{t("app.paywall.reactBullet1Strong")}</b>{t("app.paywall.reactBullet1Rest")}</li>
                <li><b>{t("app.paywall.reactBullet2Strong")}</b>{t("app.paywall.reactBullet2Rest")}</li>
                {isArchived && account?.data_retention_until && (
                  <li><b>{t("app.paywall.reactBullet3Strong")}</b>{t("app.paywall.reactBullet3Rest")}</li>
                )}
              </ul>
              <div style={{padding:"10px 12px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:7,fontSize:12,color:"#64748b",lineHeight:1.5}}>
                {t("app.paywall.cancelAnytimeSettings")}{(useStripePortal || isArchived) ? t("app.paywall.wellTakeYouToStripe") : ""}
              </div>
            </div>
          ),
          confirmLabel: t("app.paywall.reactConfirmLabel"),
          confirmVariant: "primary",
          onConfirm: async () => { await realHandleClick(); },
        });
      }
    : realHandleClick;

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif"}}>
      <div style={{maxWidth:520,width:"100%",background:"#fff",borderRadius:16,padding:40,boxShadow:"0 25px 60px rgba(15,23,42,.15)",border:"1px solid #e2e8f0"}}>
        <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:28}}>
          <img src="/logo.svg" alt="Netrev.ai" width={34} height={34} style={{borderRadius:9,display:"block"}}/>
          <span style={{fontSize:17,fontWeight:700,color:"#0f172a"}}>Netrev<span style={{color:"#3b5bdb"}}>.ai</span></span>
        </div>
        <div style={{width:56,height:56,borderRadius:14,background:isPastDue ? "linear-gradient(135deg, #fee2e2, #fca5a5)" : "linear-gradient(135deg, #fef3c7, #fcd34d)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:22}}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={isPastDue ? "#991b1b" : "#92400e"} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            {isPastDue ? (<><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></>) : (<><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>)}
          </svg>
        </div>
        <h1 style={{fontSize:26,fontWeight:700,color:"#0f172a",letterSpacing:"-.7px",margin:"0 0 12px",lineHeight:1.2}}>{title}</h1>
        <p style={{fontSize:15,color:"#64748b",lineHeight:1.6,fontWeight:500,marginBottom:28}}>{body}</p>
        {account && (
          <div style={{padding:"14px 16px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,marginBottom:24}}>
            <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{t("app.paywall.yourAccount")}</div>
            <div style={{fontSize:13.5,fontWeight:600,color:"#0f172a",marginBottom:2}}>{account.name}</div>
            <div style={{fontSize:12,color:"#64748b"}}>{user?.email}</div>
          </div>
        )}
        <button onClick={handleClick} disabled={portalLoading} style={{width:"100%",padding:"14px 16px",borderRadius:10,border:"none",background:portalLoading?"#94a3b8":"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",color:"#fff",fontSize:14.5,fontWeight:600,cursor:portalLoading?"wait":"pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:portalLoading?"none":"0 4px 14px rgba(15,23,42,.25)",marginBottom:12}}>
          {portalLoading ? t("app.paywall.openingStripe") : `${ctaLabel} →`}
        </button>
        <button onClick={onSignOut} style={{width:"100%",padding:"10px 14px",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{t("app.paywall.signOut")}</button>
        <p style={{fontSize:11.5,color:"#94a3b8",textAlign:"center",marginTop:20,lineHeight:1.5}}>{t("app.paywall.needHelpEmail")}<a href="mailto:hello@netrev.ai" style={{color:"#3b5bdb",fontWeight:600,textDecoration:"none"}}>hello@netrev.ai</a></p>
      </div>
      {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)}/>}
    </div>
  );
}

function ManageBillingButton({ size = "md" }) {
  const { t } = useT();
  const [loading, setLoading] = useState(false);

  // Reset loading state if page is restored from bfcache (user came back via back button).
  // Without this, button stays stuck on "Opening…" after returning from Stripe Portal.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  async function openPortal() {
    setLoading(true);
    try {
      const session = await getCurrentSession();
      const token = session?.access_token;
      const res = await fetch(`${API_BASE}/api/stripe/create-portal`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else { alert(data.error || "Could not open billing portal"); setLoading(false); }
    } catch (e) {
      alert("Couldn't open billing portal. Please try again."); setLoading(false);
    }
  }
  return (
    <button onClick={openPortal} disabled={loading} style={{padding:size === "sm" ? "7px 14px" : "10px 18px",borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",color:"#0f172a",fontSize:size === "sm" ? 12.5 : 13.5,fontWeight:600,cursor:loading ? "wait" : "pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>
      {loading ? t("app.billing.opening") : t("app.billing.manageBilling")}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════
export default function App() {
  const [view,  setView]  = useState(()=>{
    // Prevent the home page from flashing on refresh. If the user is returning
    // from Stripe checkout, OR there's a stored Supabase session in
    // localStorage, start on a neutral "loading" screen instead of "landing".
    // The session-restore effect then routes to admin/app once getSession()
    // resolves — so a logged-in user never sees the landing page flash.
    try {
      if (typeof window !== "undefined") {
        // Public legal pages are reachable regardless of session — honour the URL
        // for them BEFORE the "logged-in → loading" shortcut below, so a signed-in
        // visitor (or an app-store crawler) opening /privacy, /app-privacy or /terms
        // sees the policy instead of being routed into the dashboard.
        const _legal = PATH_TO_VIEW[window.location.pathname];
        if (_legal === "privacy" || _legal === "appPrivacy" || _legal === "terms") return _legal;
        const sub = new URL(window.location.href).searchParams.get("subscription");
        if (sub === "success") return "loading";
        // Supabase persists its session under a key like "sb-<ref>-auth-token".
        // If one exists, we're probably logged in → show loading, not landing.
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
            const v = window.localStorage.getItem(k);
            if (v && v.length > 20) return "loading";
          }
        }
        // No stored session → honour the URL so /login, /story, /reset-password
        // render the right screen on refresh; unknown paths fall to landing.
        const pv = PATH_TO_VIEW[window.location.pathname];
        if (pv) return pv;
      }
    } catch(_){}
    return "landing";
  });
  // Which form the auth screen opens on ("login" | "signup") — driven by which
  // CTA the user clicked on the landing/story pages.
  const [authInitialMode,setAuthInitialMode]=useState("login");
  // ── Top-level screen <-> URL sync (3c): keep the address bar honest for the
  // logged-out screens. Admin tab URLs are handled inside AdminView; the invite
  // + Stripe-return flows are guarded so their URL params are never clobbered.
  const routerNav = useNavigate();
  const routerLoc = useLocation();
  useEffect(() => {
    if (view === "loading" || view === "admin") return;            // transient / AdminView owns admin URLs
    if (typeof window !== "undefined" && window.location.search.includes("accept_invite")) return; // keep invite URL
    const target = VIEW_TO_PATH[view];
    if (target && routerLoc.pathname !== target) routerNav(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  useEffect(() => {
    const pv = PATH_TO_VIEW[routerLoc.pathname];
    if (!pv || view === "loading" || (view === "admin" && !(pv === "privacy" || pv === "appPrivacy" || pv === "terms"))) return;     // don't fight the auth/session machine (but DO allow admin -> a public legal page)
    if (pv !== view) setView(pv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.pathname]);
  // ── i18n: active language + RTL direction. `tr` is the bound translator
  // (named `tr`, not `t`, because several `.map(t => …)` loops in this
  // component already use `t` as the element variable). Setting lang/dir on
  // document.documentElement flips the WHOLE document — including portals and
  // modals rendered outside this tree — so RTL applies everywhere.
  const { t: tr, lang } = useT();
  useEffect(() => {
    try {
      if (typeof document !== "undefined") {
        document.documentElement.lang = lang;
        document.documentElement.dir = langDir(lang);
      }
    } catch (_) { /* SSR / no-DOM safety */ }
  }, [lang]);
  const [user,  setUser]  = useState(null);
  const [tab,   setTab]   = useState("log");
  const [wsId,  setWsId]  = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  // Mobile (<768px) portal nav drawer — opened by the top-bar hamburger; the
  // drawer itself renders inside AdminView (which owns the sidebar markup).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Any route away from the admin shell (logout, session expiry, paywall
  // bounce) closes the drawer — otherwise the stale open flag would cover the
  // screen on the next mobile login (AdminView unmount doesn't clear it).
  useEffect(() => { if (view !== "admin" && mobileNavOpen) setMobileNavOpen(false); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  const [workspaces, setWorkspacesRaw] = useState({});
  const [wsLoading, setWsLoading] = useState(false);
  // True once the FIRST workspace load has completed. Distinguishes "the map
  // is empty because the account genuinely owns zero workspaces" from "the
  // map is empty because loading hasn't finished" — the override-expired
  // paywall needs that distinction (it must not offer the plain-checkout
  // empty-state to an account whose workspaces just haven't arrived yet).
  const [wsLoadedOnce, setWsLoadedOnce] = useState(false);
  // True while settling the return from Stripe checkout (refreshing the account
  // + creating/restoring the just-paid workspace). Gates the admin view onto a
  // loading screen so the "subscribe" paywall never flashes before the paid
  // workspace + active status have loaded. Seeded from the URL on first render,
  // because the post-checkout effect below strips ?subscription=success.
  const [postStripeSettling, setPostStripeSettling] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        return new URL(window.location.href).searchParams.get("subscription") === "success";
      }
    } catch (_) {}
    return false;
  });
  const [cfg,   setCfg]   = useState(DEFAULT_CFG);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [currency, setCurrency] = useState("GBP");
  // Team context — populated after auth by /api/team/me. Tells the UI
  // whether the user is a brand_owner or operator, and which workspaces
  // an operator can see. Brand owners get null location_access (= all).
  const [teamContext, setTeamContext] = useState({ role: null, location_access: null, account_id: null });
  // Aggregator ingestion (test): the Connect modal + the INGESTION_ENABLED master
  // flag, fetched at runtime so the top-bar button shows/hides with no redeploy
  // (stays hidden wherever the env var is unset, e.g. prod, until release).
  const [connectOpen, setConnectOpen] = useState(false);
  const [ingestionOn, setIngestionOn] = useState(false);
  useEffect(() => {
    if (teamContext?.role !== "brand_owner") { setIngestionOn(false); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/ingestion/enabled`)
      .then(r => r.json())
      .then(j => { if (!cancelled) setIngestionOn(!!j?.enabled); })
      .catch(() => { if (!cancelled) setIngestionOn(false); });
    return () => { cancelled = true; };
  }, [teamContext?.role]);
  // Multi-account support: all accounts this user belongs to, + which one is active.
  const [myAccounts, setMyAccounts] = useState([]);          // [{account_id, account_name, role, location_access, ...}]
  const [activeAccountId, setActiveAccountId] = useState(() => {
    // Restore the last-active account so a refresh keeps you on the account you
    // were working in (operator or brand). The account resolver below validates
    // membership and safely falls back to the default if it's no longer valid.
    try { return localStorage.getItem("netrev_active_account") || null; } catch(_){ return null; }
  });
  // Persist the active account whenever it changes.
  useEffect(() => {
    if (activeAccountId) { try { localStorage.setItem("netrev_active_account", activeAccountId); } catch(_){} }
  }, [activeAccountId]);

  // Stripe / billing state (Round B)
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { account, status: accountStatus, refresh: refreshAccount } = useAccountStatus(user);

  // ── Session loss / clean re-login ──────────────────────────────────
  // Distinguishes a user-initiated "Sign out" (deliberate) from an involuntary
  // SIGNED_OUT raised by Supabase when autoRefreshToken can't renew an expired/
  // revoked session. On the involuntary case we stash where the user was so they
  // can be dropped straight back after re-login, instead of onto a blank screen.
  const deliberateLogoutRef = useRef(false);
  const STASH_KEY = "__netrev_return_to";

  // Allows children to force a workspace re-fetch from DB after add/edit operations
  // (safety net for cases where local setWorkspaces state updates don't propagate cleanly).
  // CRITICAL: This must NOT trigger wsLoading=true, otherwise AdminView unmounts and
  // remounts, resetting nav/wsId state. Refresh-after-save reads silently from DB.
  const refreshWorkspaces = async () => {
    if (!user) return;
    const isDemoAccount = user.email === "test@netrev.ai";
    if (isDemoAccount) return;
    try {
      const session = await getCurrentSession();
      if (!session?.user?.id) return;
      let dbWorkspaces = await loadWorkspacesFromDB(session.user.id, teamContext?.account_id);
      // Same app-layer operator filter as initial load.
      if (teamContext?.role === "operator" && Array.isArray(teamContext.location_access) && dbWorkspaces) {
        const allowed = new Set(teamContext.location_access);
        dbWorkspaces = Object.fromEntries(
          Object.entries(dbWorkspaces).filter(([id]) => allowed.has(id))
        );
      }
      if (dbWorkspaces) {
        setWorkspacesRaw(dbWorkspaces);
        // Do NOT call setWsId — let user stay on their current workspace
        // Do NOT call setWsLoading — would unmount AdminView and reset nav state
      }
    } catch (e) {
      console.warn("[refreshWorkspaces] error:", e);
    }
  };

  // Refetch workspaces/sales when the tab regains focus (or becomes visible),
  // so changes made on another device (the mobile app) or by a teammate show up
  // without a manual reload. Silent: refreshWorkspaces preserves nav/wsId and
  // never sets wsLoading, so the current view is untouched.
  useEffect(() => {
    if (!user) return;
    let lastRefetch = 0;
    const onFocus = () => {
      if (document.hidden) return;
      // 'focus' and 'visibilitychange' both fire when returning to the tab —
      // collapse the pair (and any rapid re-focus) into a single refetch.
      const now = Date.now();
      if (now - lastRefetch < 1500) return;
      lastRefetch = now;
      refreshWorkspaces();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    // Back/forward-cache restore (same-tab return from a payment redirect)
    // fires neither of the above — pageshow(persisted) is the only signal.
    const onPageShow = (e) => { if (e.persisted) onFocus(); };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, teamContext?.account_id]);

  // Detect post-checkout redirect from Stripe and refresh account + workspace state
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const sub = url.searchParams.get("subscription");
    if (sub === "success") {
      // Safety watchdog: fetch/getCurrentSession have no request timeout, so if any
      // awaited call in the settle below hangs, force-clear the loading gate after
      // 20s — the user is never stranded on the "Setting up your account…" spinner.
      const settleWatchdog = setTimeout(() => setPostStripeSettling(false), 20000);
      // Stripe redirected us back — wait briefly for webhook, then refresh everything
      setTimeout(async () => {
        try {
        await refreshAccount();

        // Resolve current user fresh (the user state from closure may be null —
        // useEffect runs on mount before session restore completes).
        const sess = await getCurrentSession();
        const uid = sess?.user?.id;
        if (!uid) {
          console.warn("[post-stripe] No session — can't complete workspace creation");
          setView("landing"); // don't strand the user on the loading screen
          return;
        }

        // Complete the workspace creation that was paused for the card gate.
        // Read the stashed workspace details from localStorage and create it now.
        //
        // IMPORTANT: we create via the BACKEND /api/workspaces/create-billed
        // endpoint, NOT the frontend createWorkspaceInDB. The frontend insert
        // uses the user's JWT and is subject to RLS — and the workspaces INSERT
        // policy requires has_active_access(account_id), which can be FALSE for
        // a few seconds right after Stripe redirect (the trial-activating
        // webhook may not have landed yet). That race caused the "new row
        // violates row-level security policy" (42501) error and an empty
        // dashboard. The backend uses the service role, which bypasses RLS, so
        // creation is reliable regardless of webhook timing.
        // Decide: did the webhook RESTORE an archived workspace (resubscribe-
        // after-cancel), or is this a brand-new signup that needs its first
        // workspace created? We check the DB first. If the account already has
        // an active workspace (because reactivate_account un-archived it), we do
        // NOT create a new one — the user gets their original workspace + data
        // back. We only create from the pending stash when there are genuinely
        // zero active workspaces to fall back on.
        let createdWsId = null;
        let restoredExisting = false;
        try {
          const preWorkspaces = await loadWorkspacesFromDB(uid, teamContext?.account_id);
          const preKeys = preWorkspaces ? Object.keys(preWorkspaces) : [];
          if (preKeys.length > 0) {
            // An active workspace already exists (restored by the webhook) — use it.
            restoredExisting = true;
            setWorkspacesRaw(preWorkspaces);
            setWsId(preKeys[0]);
            // The archived workspace is back; discard any stale pending stash so
            // we don't also create a duplicate new workspace.
            localStorage.removeItem("netrev_pending_workspace");
            console.log("[post-stripe] Restored existing workspace(s) after payment:", preKeys.length);
          }
        } catch(e) { console.warn("[post-stripe] pre-check for restored workspaces failed:", e); }

        try {
          const pendingRaw = restoredExisting ? null : localStorage.getItem("netrev_pending_workspace");
          if (pendingRaw) {
            const pending = JSON.parse(pendingRaw);
            // Only consume if stashed in the last 30 minutes (avoid stale)
            if (pending && (Date.now() - (pending.ts || 0)) < 30 * 60 * 1000) {
              // Map stashed channelsData → the create-billed channel shape.
              const channels = (pending.channelsData || []).map(ch => ({
                platform:     ch.platform,
                color:        ch.color,
                commission:   ch.commission ?? 0,
                card_fee_pct: ch.cardFeePct ?? ch.card_fee_pct ?? 0,
              }));
              const token = sess?.access_token;
              const resp = await fetch(`${API_BASE}/api/workspaces/create-billed`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                  name:    pending.brand,
                  market:  pending.market,
                  city:    pending.city || pending.area || "Main location",
                  area:    pending.area,
                  color:   pending.color,
                  channels,
                }),
              });
              const respData = await resp.json().catch(() => null);
              // The prorated charge needs 3D-Secure / bank authentication (402 +
              // needs_checkout) — finish it on Stripe Checkout; the webhook
              // activates the parked workspace once the payment lands (shouldn't
              // fire on a first workspace, whose seat the subscription checkout
              // already covers, but handle it so the flow can never dead-end).
              if (respData?.needs_checkout && respData?.checkout_url) {
                localStorage.removeItem("netrev_pending_workspace");
                window.location.href = respData.checkout_url;
                return;
              }
              if (resp.ok) {
                createdWsId = respData?.workspace?.id || null;
                console.log("[post-stripe] Created workspace via backend:", createdWsId);
              } else {
                console.warn("[post-stripe] create-billed failed:", resp.status, respData?.error || "");
              }
            }
            localStorage.removeItem("netrev_pending_workspace");
          }
        } catch(e) { console.warn("[post-stripe] Pending workspace creation failed:", e); }

        // Re-fetch workspaces from DB regardless — guarantees UI shows latest
        // state. Retry a few times while empty: on a resubscribe the workspace is
        // un-archived by the Stripe webhook, which can land a little after this
        // refresh; without the retry the paywall could show for a moment on a
        // slow webhook.
        try {
          let dbWorkspaces = null;
          for (let attempt = 0; attempt < 5; attempt++) {
            dbWorkspaces = await loadWorkspacesFromDB(uid, teamContext?.account_id);
            // Count only ACTIVE (non-archived) workspaces — loadWorkspacesFromDB
            // returns archived rows too, so on a resubscribe keep waiting until the
            // webhook actually un-archives one rather than stopping on a stale row.
            if (dbWorkspaces && Object.values(dbWorkspaces).some(w => !w?.archived_at)) break;
            await new Promise(r => setTimeout(r, 1200));
            refreshAccount(); // fire the account re-fetch (state-setter; nothing to await)
          }
          if (dbWorkspaces) {
            setWorkspacesRaw(dbWorkspaces);
            // Auto-select the just-created workspace, or the first one
            const wsKeys = Object.keys(dbWorkspaces);
            if (createdWsId && dbWorkspaces[createdWsId]) {
              setWsId(createdWsId);
            } else if (wsKeys.length > 0) {
              setWsId(wsKeys[0]);
            }
            console.log("[post-stripe] Loaded", wsKeys.length, "workspaces from DB");
          }
        } catch(e) { console.warn("[post-stripe] loadWorkspacesFromDB failed:", e); }

        setUser(prev => prev ? { ...prev, plan: "active" } : prev);
        } finally {
          // Settle finished (success or handled failure) — drop the loading gate
          // so the real workspace (or the correct paywall) renders.
          clearTimeout(settleWatchdog);
          setPostStripeSettling(false);
        }
      }, 1500);
      url.searchParams.delete("subscription");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    } else if (sub === "canceled") {
      // User backed out of Stripe — clean up any pending stash so they're not auto-created later
      try { localStorage.removeItem("netrev_pending_workspace"); } catch(e){}
      url.searchParams.delete("subscription");
      window.history.replaceState({}, "", url.toString());
    }

    // Return from an SCA/3D-Secure payment for an added/restored workspace
    // (payment-mode Checkout — distinct from ?subscription=, which is the
    // subscription checkout). The WEBHOOK grants the workspace; here we just
    // poll until it shows up so the user sees their new site without a manual
    // refresh. On "canceled" nothing was charged and the backend cleans up the
    // parked row when the session expires — only strip the params.
    const wsPay = url.searchParams.get("workspace_payment");
    if (wsPay === "success") {
      setTimeout(async () => {
        try {
          const sess = await getCurrentSession();
          const uid = sess?.user?.id;
          if (!uid) return;
          let dbWorkspaces = null;
          for (let attempt = 0; attempt < 8; attempt++) {
            dbWorkspaces = await loadWorkspacesFromDB(uid, teamContext?.account_id);
            // The webhook grant flips the parked row (archived +
            // status='pending_payment') to active — once no parked row remains,
            // the paid workspace is live (or was already granted before the
            // first poll). Keep polling only while one is still parked.
            const stillParked = dbWorkspaces && Object.values(dbWorkspaces)
              .some(w => w?.archived_at && w?.status === "pending_payment");
            if (!stillParked) break;
            await new Promise(r => setTimeout(r, 1200));
          }
          if (dbWorkspaces) setWorkspacesRaw(dbWorkspaces);
          refreshAccount();
          console.log("[workspace-payment] Refreshed workspaces after SCA payment");
        } catch (e) { console.warn("[workspace-payment] post-payment refresh failed:", e); }
      }, 1200);
      url.searchParams.delete("workspace_payment");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    } else if (wsPay === "canceled") {
      // The user backed out of the payment — nothing was charged, so nothing
      // may look created: expire the session server-side and delete the parked
      // row NOW (instead of the phantom "archived" row lingering until the
      // session's natural ~35-min expiry), then re-fetch the list.
      const canceledSessionId = url.searchParams.get("session_id");
      setTimeout(async () => {
        try {
          const sess = await getCurrentSession();
          const token = sess?.access_token;
          if (token && canceledSessionId) {
            await fetch(`${API_BASE}/api/stripe/cancel-workspace-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
              body: JSON.stringify({ session_id: canceledSessionId }),
            });
          }
          const uid = sess?.user?.id;
          if (uid) {
            const dbWorkspaces = await loadWorkspacesFromDB(uid, teamContext?.account_id);
            if (dbWorkspaces) setWorkspacesRaw(dbWorkspaces);
          }
        } catch (e) { console.warn("[workspace-payment] cancel cleanup failed:", e); }
      }, 300);
      url.searchParams.delete("workspace_payment");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ws  = workspaces[wsId] || Object.values(workspaces)[0];
  const setWs = fn => setWorkspaces(p => ({...p, [wsId]: fn(p[wsId])}));

  // Restore Supabase session on app load (keeps user logged in on refresh)
  useEffect(() => {
    // Detect password-recovery via the latched module-level flag + sessionStorage +
    // current pathname. Pathname survives SDK init; sessionStorage survives a full
    // re-mount if React re-runs effects.
    const path = window.location.pathname || "";
    const ssFlag = (() => { try { return sessionStorage.getItem("__netrev_recovery") === "1"; } catch (_) { return false; } })();
    const isRecoveryFlow =
      __DETECTED_RECOVERY ||
      ssFlag ||
      path === "/reset-password" ||
      path.startsWith("/reset-password/");

    // eslint-disable-next-line no-console
    if (isRecoveryFlow) console.log("[netrev] Showing recovery view", { __DETECTED_RECOVERY, ssFlag, path });

    // Listen for auth state changes (covers PKCE recovery event fired post-mount)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        try { sessionStorage.setItem("__netrev_recovery", "1"); } catch (_) {}
        setView("recover");
        return;
      }
      // Genuine session loss — fired by Supabase when the (auto-)refresh fails,
      // i.e. the refresh token is expired/revoked. (It is ALSO fired by our own
      // handleLogout's signOut(); deliberateLogoutRef tells the two apart.)
      // Centrally route to a CLEAN re-login rather than dead-ending on a broken
      // admin screen with empty workspaces. Skip during the recovery flow (the
      // recover screen issues its own session changes).
      if (event === "SIGNED_OUT") {
        if (deliberateLogoutRef.current) { deliberateLogoutRef.current = false; return; }
        let recovering = false;
        try { recovering = sessionStorage.getItem("__netrev_recovery") === "1"; } catch (_) {}
        if (recovering) return;
        // Stash where the user was (the admin tab lives in the URL path) so we can
        // restore it after they sign back in. Only stash real admin tab routes.
        try {
          const p = window.location.pathname || "";
          if (PATH_TO_NAV[p]) sessionStorage.setItem(STASH_KEY, p);
        } catch (_) {}
        setUser(null);
        setWorkspacesRaw({});
        setView(v => (v === "landing" || v === "story" || v === "privacy" || v === "appPrivacy" || v === "terms" ? v : "auth"));
        return;
      }
    });

    if (isRecoveryFlow) {
      setView("recover");
      return () => subscription?.unsubscribe();
    }

    (async () => {
      const session = await getCurrentSession();
      if (session?.user) {
        const email = session.user.email || "";
        const isBootstrapSuperAdmin = SUPER_ADMIN_BOOTSTRAP_EMAILS.includes(email.toLowerCase());
        const u = {
          email,
          name: email.split("@")[0],
          plan: isBootstrapSuperAdmin ? "superadmin" : "trial",
          token: session.access_token,
        };
        setUser(u);
        await loadWorkspaces(u);
        // Keep the user on a public legal page if that's where they landed — don't
        // yank them into the dashboard just because a session exists.
        setView(v => (v === "privacy" || v === "appPrivacy" || v === "terms") ? v : "admin");
        // Authoritative super-admin lookup against the DB. See checkSuperAdminFromDB.
        const isDbSuperAdmin = await checkSuperAdminFromDB(u.token);
        if (isDbSuperAdmin && u.plan !== "superadmin") {
          setUser(prev => prev ? { ...prev, plan: "superadmin" } : prev);
        } else if (!isDbSuperAdmin && u.plan === "superadmin") {
          setUser(prev => prev ? { ...prev, plan: "trial" } : prev);
        }
        // NOTE: teamContext is already set by loadWorkspaces(u) above, which
        // resolves the ACTIVE account's per-membership role (operator vs
        // brand_owner). Do NOT re-fetch the legacy /api/team/me here — it is not
        // account-scoped and would clobber the active-account role with the
        // user's default/own-account role (the operator-sees-config bug).
      } else {
        // No session — make sure we never stay stuck on the loading screen
        // (e.g. a stray ?subscription=success with no auth).
        setView(v => (v === "loading" ? "landing" : v));
      }
    })();

    return () => subscription?.unsubscribe();
  }, []);

  // Periodic team-context re-validation (multi-account aware).
  // Runs every 30 seconds while signed in. Detects server-side changes to the
  // user's memberships (e.g. a brand owner revoked or modified their access)
  // and reacts within ~30s without a manual refresh. Cases handled:
  //   - Revoked from the ACTIVE account but others remain → auto-switch to a
  //     remaining account.
  //   - Revoked from ALL accounts → sign out (nothing left to show).
  //   - A NON-active account changed/removed → just refresh the switcher list,
  //     don't disturb the current view.
  //   - Active account's role/access changed (still a member) → reload current.
  useEffect(() => {
    if (!user?.token) return;
    let stopped = false;
    let consecutiveEmpties = 0; // require 2 consecutive confirmed-empty results before logout
    const interval = setInterval(async () => {
      // Skip while backgrounded — an idle open tab shouldn't poll the backend
      // (this path hits requireAuth → getUser + a Supabase client per call).
      if (stopped || document.hidden) return;
      try {
        const accounts = await fetchMyAccounts(user.token);
        if (stopped) return;

        // null = the request FAILED (transient auth blip, network error, 401/403
        // during token refresh). This is NOT "user has no accounts". Do nothing,
        // keep the session, try again next poll. NEVER log out on a failed request.
        if (accounts === null) {
          consecutiveEmpties = 0;
          return;
        }

        // A genuine, SUCCESSFUL response with zero accounts → revoked from
        // everything. Require TWO consecutive confirmations to avoid logging out
        // on a one-off odd response. (Super admins keep admin-panel access, so
        // never auto-logout them.)
        if (accounts.length === 0) {
          if (user?.plan === "superadmin") { consecutiveEmpties = 0; return; }
          consecutiveEmpties += 1;
          if (consecutiveEmpties >= 2) {
            console.log("[team-poll] no memberships remain (confirmed) — signing out");
            await handleLogout();
          }
          return;
        }

        // Got a real, non-empty account list → reset the empty counter.
        consecutiveEmpties = 0;

        // Always keep the switcher list fresh.
        setMyAccounts(accounts);

        const activeStillExists = accounts.some(a => a.account_id === activeAccountId);

        if (!activeStillExists) {
          // Revoked from the account currently being viewed → auto-switch to a
          // remaining account (first in the list).
          const next = accounts[0].account_id;
          console.log("[team-poll] active account revoked — switching to", next);
          setActiveAccountId(next);
          await loadWorkspaces(user, next);
          return;
        }

        // Still in the active account — check if role/access there changed,
        // or whether it pays for the paid-only features (lib/teamContext.js).
        const activeMembership = accounts.find(a => a.account_id === activeAccountId);
        if (activeMembership) {
          const changed = membershipChanged(activeMembership, teamContext);
          if (changed) {
            console.log("[team-poll] active account access changed — reloading");
            await loadWorkspaces(user, activeAccountId);
          }
        }
      } catch (e) { /* swallow — keeps polling alive */ }
    }, 60000);
    return () => { stopped = true; clearInterval(interval); };
  }, [user?.token, user?.plan, activeAccountId, teamContext?.role, JSON.stringify(teamContext?.location_access), teamContext?.paid_access === true]); // eslint-disable-line

  // Load workspaces from Supabase when user logs in
  async function loadWorkspaces(u, accountIdOverride = null) {
    setWsLoading(true);
    const isDemoAccount = u.email === "test@netrev.ai";
    // Demo account (test@netrev.ai) shows seed data; everyone else (including super admins) loads real data.
    // Super admins use RLS bypass server-side to load their own workspaces — they have to create them like anyone else.
    if (isDemoAccount) {
      setWorkspacesRaw(WORKSPACE_SEED);
      setWsId(Object.keys(WORKSPACE_SEED)[0]);
      setWsLoading(false);
      setWsLoadedOnce(true);
      return;
    }
    // Load from Supabase
    const session = await getCurrentSession();
    if (session?.user?.id) {
      // Multi-account: load every account this user belongs to (for the switcher).
      // fetchMyAccounts returns null on failure — coerce to [] here so the UI
      // never crashes; a failed fetch on login just means no switcher this load.
      const accounts = (await fetchMyAccounts(u.token)) || [];
      setMyAccounts(accounts);

      // Decide which account is active: explicit override > current activeAccountId
      // (if still valid) > first account in the list > legacy single-context.
      let chosenId = accountIdOverride;
      if (!chosenId && activeAccountId && accounts.some(a => a.account_id === activeAccountId)) {
        chosenId = activeAccountId;
      }
      // Honour the persisted account from the previous visit. Read directly from
      // localStorage (not the activeAccountId closure, which can be stale on a
      // fresh-load restore) so a refresh keeps you on the account you were in —
      // operator or brand. Only if you still belong to it.
      if (!chosenId) {
        try {
          const saved = localStorage.getItem("netrev_active_account");
          if (saved && accounts.some(a => a.account_id === saved)) chosenId = saved;
        } catch(_){}
      }
      if (!chosenId && accounts.length > 0) {
        chosenId = accounts[0].account_id;
      }

      // Resolve the membership for the chosen account (gives role + location_access
      // specific to THAT account — critical for operators with different access
      // per brand).
      const activeMembership = accounts.find(a => a.account_id === chosenId) || null;

      // Build the team context for the active account. Fall back to the legacy
      // single-context fetch if the accounts list came back empty (e.g. a
      // pre-accounts legacy user).
      // membershipContext also copies paid_access (read as `=== true`, so a
      // row without it gives false, as before).
      let ctx;
      if (activeMembership) {
        ctx = membershipContext(activeMembership);
      } else {
        ctx = await fetchTeamContext(u.token);
      }
      if (ctx) setTeamContext(ctx);
      if (chosenId) setActiveAccountId(chosenId);

      let dbWorkspaces = await loadWorkspacesFromDB(session.user.id, ctx?.account_id);

      // App-layer operator filter. For users with role=operator, we trim the
      // visible workspaces to their location_access list, even if RLS would
      // allow more. This is important when an operator is ALSO a super admin
      // — the super_admin clause in RLS bypasses the location_access check,
      // so we enforce it here too. Defense in depth; matches what the
      // operator was explicitly granted.
      if (ctx?.role === "operator" && Array.isArray(ctx.location_access) && dbWorkspaces) {
        const allowed = new Set(ctx.location_access);
        dbWorkspaces = Object.fromEntries(
          Object.entries(dbWorkspaces).filter(([id]) => allowed.has(id))
        );
      }

      if (dbWorkspaces && Object.keys(dbWorkspaces).length > 0) {
        setWorkspacesRaw(dbWorkspaces);
        setWsId(Object.keys(dbWorkspaces)[0]);
      } else {
        setWorkspacesRaw({});
        setWsId(null);
      }
    }
    setWsLoading(false);
    setWsLoadedOnce(true);
  }

  // Switch the active account (from the header switcher). Persist the chosen
  // account, then do a CLEAN reload so EVERY account-scoped context — workspaces,
  // the per-account role/access (teamContext), billing state, and any cached
  // insights — re-resolves from scratch for the new account. The previous
  // in-place switch reloaded workspaces but left some context stale, so the view
  // needed a manual refresh to settle (the bug reported). A reload is reliable and
  // the current tab is preserved via the URL. On reload, loadWorkspaces restores
  // this account from localStorage (validated against membership).
  async function switchAccount(accountId) {
    if (!user || accountId === activeAccountId) return;
    try { localStorage.setItem("netrev_active_account", accountId); } catch(_){}
    if (typeof window !== "undefined") window.location.reload();
  }

  function setWorkspaces(fn) {
    setWorkspacesRaw(prev => {
      const next = typeof fn === "function" ? fn(prev) : fn;
      // Handle workspace deletions
      if (user && user.email !== "test@netrev.ai") {
        Object.keys(prev).forEach(id => {
          if (!next[id]) deleteWorkspaceFromDB(id);
        });
      }
      return next;
    });
  }

  // Fetch the user's team role + workspace access from the backend. Used after
  // login and on session restore. Never throws — returns null on any error so
  // a flaky backend can't break the auth flow.
  async function fetchTeamContext(token) {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/team/me`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const d = await res.json();
      return {
        role:            d.role || null,
        location_access: d.location_access || null,
        account_id:      d.account_id || null,
        account_name:    d.account_name || null,
        // Entitlement for the account this user works in — see paidAccess in
        // AdminView. Absent on an old backend → undefined → falls back to the
        // owned-account check, i.e. exactly today's behaviour.
        paid_access:     d.paid_access === true,
      };
    } catch (_) { return null; }
  }

  // Fetch ALL accounts this user belongs to (for the account switcher).
  // Returns null on ANY failure (network error, 401/403, bad JSON) so callers
  // can distinguish "request failed" from "succeeded with zero accounts".
  // CRITICAL: a failed request must NEVER be treated as "user has no
  // memberships" — that caused false-positive logouts on transient auth blips.
  async function fetchMyAccounts(token) {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/team/my-accounts`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) return null;            // failure → null (NOT empty array)
      const d = await res.json();
      return Array.isArray(d.accounts) ? d.accounts : null;
    } catch (_) { return null; }            // failure → null (NOT empty array)
  }

  async function handleAuth(u) {
    setUser(u);
    await loadWorkspaces(u);
    setView("admin");
    // Restore the admin tab the user was on before an involuntary session loss.
    // (Skip if an invite is pending — that flow owns the URL below.) The admin
    // tab-sync effect maps the path back to the active tab.
    try {
      const hasPendingInvite = !!sessionStorage.getItem("__netrev_pending_invite_token");
      const returnTo = sessionStorage.getItem(STASH_KEY);
      sessionStorage.removeItem(STASH_KEY);
      if (returnTo && !hasPendingInvite && PATH_TO_NAV[returnTo]) routerNav(returnTo);
    } catch (_) {}
    // Authoritative super-admin check: the DB super_admins table is the source of truth.
    const isDbSuperAdmin = await checkSuperAdminFromDB(u.token);
    if (isDbSuperAdmin && u.plan !== "superadmin") {
      setUser(prev => prev ? { ...prev, plan: "superadmin" } : prev);
    } else if (!isDbSuperAdmin && u.plan === "superadmin") {
      setUser(prev => prev ? { ...prev, plan: "trial" } : prev);
    }
    // NOTE: teamContext is already set by loadWorkspaces(u) above (account-scoped
    // role). Do NOT overwrite it with the non-account-scoped /api/team/me here —
    // that clobbers an operator's role with their default/own-account role.

    // If the user arrived via an invite link and just signed in, redirect
    // them back to the accept-invite page (URL param preserved) so they see
    // the invite preview and click Accept themselves. This avoids the
    // "I clicked the link and it joined me without asking" surprise.
    let pendingToken = null;
    try { pendingToken = sessionStorage.getItem("__netrev_pending_invite_token"); } catch(_){}
    if (pendingToken) {
      try { sessionStorage.removeItem("__netrev_pending_invite_token"); } catch(_){}
      try { sessionStorage.removeItem("__netrev_invite_email_hint"); } catch(_){}
      // Put the token back in the URL so the accept page renders on next nav.
      try {
        window.history.replaceState({}, "", `/?accept_invite=${encodeURIComponent(pendingToken)}`);
        // Force a re-render so App re-reads the URL.
        setView("admin");
      } catch (_) {}
    }
  }

  function handleLogout() {
    // Mark this as a deliberate sign-out so the onAuthStateChange SIGNED_OUT
    // handler doesn't treat it as an involuntary expiry (which would stash a
    // return-path and bounce to the auth screen instead of the landing page).
    deliberateLogoutRef.current = true;
    try { sessionStorage.removeItem(STASH_KEY); } catch (_) {}
    supabase.auth.signOut();
    setUser(null);
    setWorkspacesRaw({});
    setView("landing");
  }

  const MOB_TABS = [
    // Overview ("dash") hidden for now (owner request) to match the desktop nav.
    // The MobDash component is kept; it's just no longer a tab.
    {id:"items",   icon:"🏷️", label:"Items"},
    {id:"log",     icon:"📋", label:"Log"},
    {id:"account", icon:"💳", label:"Account"},
  ];

  // If the URL has ?accept_invite=TOKEN, hand off to the dedicated landing page
  // — it handles both signed-in (auto-accept) and signed-out (sign-in prompt) states.
  // Computed once per render; the page itself manages reload/redirect after accept.
  const acceptInviteToken = (() => {
    if (typeof window === "undefined") return null;
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get("accept_invite");
    } catch (_) { return null; }
  })();

  if (acceptInviteToken) {
    return (
      <AcceptInvitePage
        token={acceptInviteToken}
        user={user}
        teamContext={teamContext}
        onAuthRedirect={(email) => {
          // Stash the token so AuthScreen can auto-accept after sign-in.
          try { sessionStorage.setItem("__netrev_pending_invite_token", acceptInviteToken); } catch(_){}
          if (email) try { sessionStorage.setItem("__netrev_invite_email_hint", email); } catch(_){}
          // Clear the URL param so the next render shows AuthScreen instead of
          // re-rendering AcceptInvitePage. The token lives in sessionStorage now.
          try { window.history.replaceState({}, "", "/"); } catch(_){}
          setView("auth");
        }}
      />
    );
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg2,fontFamily:"'Inter','Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",color:C.text,WebkitFontSmoothing:"antialiased"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;font-family:'Inter','Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
        input,select,button,textarea{font-family:inherit;color:inherit;}
        input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
        input[type=number]{-moz-appearance:textfield;}
        input:focus,select:focus,textarea:focus{outline:none;border-color:${C.blue} !important;box-shadow:0 0 0 3px ${C.blue}1a !important;}
        button{transition:all .15s cubic-bezier(0.4, 0, 0.2, 1);}
        button:not(:disabled):active{transform:translateY(1px);}
        ::-webkit-scrollbar{width:8px;height:8px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:8px;border:2px solid ${C.bg2};}
        ::-webkit-scrollbar-thumb:hover{background:${C.muted2};}
        tr:hover td{background:${C.blue5} !important;}
        .num{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-feature-settings:"tnum";}
        @keyframes fadeInUp{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes shimmer{0%{background-position:-200% 0;}100%{background-position:200% 0;}}
        .card-enter{animation:fadeInUp .4s cubic-bezier(0.4, 0, 0.2, 1) both;}
        /* ── Responsive utilities (portal shell + admin pages) ─────────────
           The app styles inline, so every media override needs !important.
           Nothing below applies at ≥1024px — desktop stays pixel-identical.
           .nv-table-scroll: wrapper so wide tables scroll inside their own
           container instead of forcing horizontal page scroll. */
        @keyframes nvFadeIn{from{opacity:0;}to{opacity:1;}}
        @media (max-width:1023.98px){
          /* .nv-table-scroll only bites below 1024 so desktop stays pixel-
             identical (review: an ungated rule made one wrapper a scroll
             container at desktop widths). */
          .nv-table-scroll{overflow-x:auto !important;-webkit-overflow-scrolling:touch;max-width:100%;}
          /* Welcome steps: repeat(3,1fr) crushes beside the 232px sidebar */
          .nv-welcome-grid{grid-template-columns:repeat(auto-fit,minmax(200px,1fr)) !important;}
          /* 768-1023 tablet band: the 232px rail still renders, so wide KPI
             grids need gentler column counts than desktop. */
          .nv-grid-md-3{grid-template-columns:repeat(3,1fr) !important;}
          .nv-grid-md-2{grid-template-columns:repeat(2,1fr) !important;}
          .nv-grid-md-1{grid-template-columns:1fr !important;}
        }
        @media (max-width:767.98px){
          /* Top bar: show hamburger, drop branding text + user name + plan
             pill (avatar chip stays), tighten paddings so the right cluster
             (billing + sign out) fits a 390px viewport. */
          .nv-menu-btn{display:flex !important;}
          .nv-topbar{padding:0 10px !important;}
          .nv-topbar-title{display:none !important;}
          .nv-user-name{display:none !important;}
          /* Plan pill STAYS visible (billing-state cue); the topbar Manage
             billing button hides instead — billing remains reachable via the
             drawer -> Subscriptions page. */
          .nv-billing-btn{display:none !important;}
          .nv-user-chip{padding:6px !important;}
          .nv-topbar-right{gap:6px !important;}
          .nv-topbar-right > button{padding:7px 9px !important;font-size:12px !important;}
          /* Workspace banner: tighter padding; ≥40px touch targets for the
             channel pills + toolbar buttons (incl. the RangeFilter trigger). */
          .nv-ws-banner{padding:10px 14px !important;}
          .nv-chpills button{min-height:40px !important;padding:8px 14px !important;}
          .nv-toolbar button{min-height:40px !important;}
          .nv-cancel-banner{flex-wrap:wrap !important;}
          /* Page helpers: grid collapses + wrapping rows for admin pages. */
          .nv-page{padding:16px 12px !important;}
          .nv-grid-1-sm{grid-template-columns:1fr !important;}
          .nv-grid-2-sm{grid-template-columns:1fr 1fr !important;}
          .nv-flex-wrap-sm{flex-wrap:wrap !important;}
          /* Subscriptions workspace row: name keeps the first line, so the
             price block + action button wrap onto a second line at 390px. */
          .nv-ws-name{min-width:60% !important;}
          /* Date-filter popover: anchor to the inline-START edge on phones —
             the toolbar is start-aligned there, so an end-anchored 236px
             popover would extend past the viewport's start edge. */
          .nv-range-pop{inset-inline-end:auto !important;inset-inline-start:0 !important;}
        }
      `}</style>

      {/* Top nav — hidden on landing, auth and Our story (which have their own nav) */}
      {view !== "landing" && view !== "auth" && view !== "story" && (
        <div className="nv-topbar" style={{background:"rgba(255,255,255,.85)",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",borderBottom:`1px solid ${C.border}`,padding:"0 32px",display:"flex",alignItems:"center",justifyContent:"space-between",height:64,position:"sticky",top:0,zIndex:100}}>
          {/* Logo */}
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            {/* Mobile-only hamburger (<768px, portal shell only) — opens the
                sidebar drawer rendered by AdminView. display:none inline;
                the .nv-menu-btn media rule shows it below the breakpoint. */}
            {view === "admin" && user && (
              <button
                className="nv-menu-btn"
                onClick={() => setMobileNavOpen(o => !o)}
                style={{display:"none",alignItems:"center",justifyContent:"center",width:40,height:40,padding:0,border:`1px solid ${C.border}`,borderRadius:9,background:"#fff",cursor:"pointer"}}
                aria-expanded={mobileNavOpen}
                aria-label={tr(mobileNavOpen ? "topbar.closeMenu" : "topbar.openMenu")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </button>
            )}
            <img src="/logo.svg" alt="Netrev.ai" width={36} height={36} style={{borderRadius:10,display:"block",boxShadow:C.sBlue}}/>
            <div className="nv-topbar-title">
              <div style={{fontSize:15,fontWeight:700,color:C.text,letterSpacing:"-.4px",lineHeight:1.1}}>Netrev.ai</div>
              <div style={{fontSize:10,color:C.muted,letterSpacing:.4,fontWeight:500,textTransform:"uppercase",marginTop:1}}>{tr("topbar.adminPortal")}</div>
            </div>
          </div>

          {/* Right — user info + sign out */}
          {user&&(
            <div className="nv-topbar-right" style={{display:"flex",alignItems:"center",gap:10}}>
              {/* Connect delivery platforms (test feature) — sits next to the
                  language toggle. Gated on the INGESTION_ENABLED master flag +
                  brand-owner role; opens the Connect hub as a modal. */}
              {ingestionOn && teamContext?.role==="brand_owner" && (
                <button onClick={()=>setConnectOpen(true)} title="Connect delivery platforms to auto-fill your Daily Log (test feature)"
                  style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.sub,cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><path d="M8 12h8"/></svg>
                  Connect
                  <span style={{fontSize:9,fontWeight:700,letterSpacing:.4,textTransform:"uppercase",color:C.muted,border:`1px solid ${C.border}`,borderRadius:4,padding:"1px 4px"}}>Test</span>
                </button>
              )}
              {connectOpen && typeof document !== "undefined" && createPortal(
                <div onClick={()=>setConnectOpen(false)} style={{position:"fixed",inset:0,background:"rgba(15,23,42,.45)",zIndex:100000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",overflowY:"auto"}}>
                  <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:880,width:"100%",boxShadow:"0 24px 70px rgba(0,0,0,.32)",position:"relative"}}>
                    <button onClick={()=>setConnectOpen(false)} aria-label="Close" style={{position:"absolute",top:12,right:14,width:32,height:32,border:"none",background:"transparent",cursor:"pointer",fontSize:22,lineHeight:1,color:C.muted,zIndex:1,fontFamily:"inherit"}}>×</button>
                    <ConnectPlatforms wsId={wsId}/>
                  </div>
                </div>,
                document.body
              )}
              {/* Language toggle — reuses the Sign-out button's exact style tokens
                  (border 1px solid C.border, background #fff, color C.sub). */}
              <button onClick={()=>setLang(lang==="ar"?"en":"ar")}
                style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.sub,cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600}}>
                {lang==="ar"?"EN":"عربي"}
              </button>
              <div className="nv-user-chip" style={{display:"flex",alignItems:"center",gap:10,padding:"6px 14px 6px 6px",borderRadius:24,background:C.bg3,border:`1px solid ${C.border}`}}>
                <div style={{width:28,height:28,borderRadius:14,background:`linear-gradient(135deg,${C.blue} 0%,${C.blueDark} 100%)`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:C.s1}}>
                  <span style={{color:"#fff",fontSize:12,fontWeight:700}}>{(user.name||user.email||"U")[0].toUpperCase()}</span>
                </div>
                <span className="nv-user-name" style={{fontSize:13,color:C.text,fontWeight:600}}>{user.name||user.email?.split("@")[0]}</span>
                {(() => {
                  // Dynamic plan pill — reads from live account, not stale user.plan
                  if (user?.plan === "superadmin") {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:"#7c3aed20",color:"#7c3aed",fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{tr("app.root.superAdmin")}</span>;
                  }
                  if (isOverrideActive(account)) {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:C.green10,color:C.greenDark,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{tr("app.root.free")}</span>;
                  }
                  if (account?.subscription_status === "active") {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:C.green10,color:C.greenDark,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{account?.subscription_plan === "annual" ? tr("app.root.annual") : tr("app.root.monthly")}</span>;
                  }
                  if (account?.subscription_status === "trialing") {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:C.green10,color:C.greenDark,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{tr("app.root.trial")}</span>;
                  }
                  if (account?.subscription_status === "past_due") {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:"#fef2f2",color:"#991b1b",fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{tr("app.root.pastDue")}</span>;
                  }
                  if (account?.subscription_status === "canceled") {
                    return <span className="nv-plan-pill" style={{fontSize:10,padding:"3px 8px",borderRadius:12,background:"#f1f5f9",color:"#64748b",fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>{tr("app.root.canceled")}</span>;
                  }
                  return null;
                })()}
              </div>
              {/* Manage Billing — visible only if user has a Stripe customer record */}
              {account?.stripe_customer_id && user?.plan !== "superadmin" && (
                <span className="nv-billing-btn" style={{display:"contents"}}><ManageBillingButton size="sm"/></span>
              )}
              <button onClick={handleLogout}
                style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",color:C.sub,cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:500}}>
                {tr("btn.signOut")}
              </button>
            </div>
          )}
        </div>
      )}

      {view==="loading" && (
        <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:C.bg2,gap:20}}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{animation:"nrspin 1s linear infinite"}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:6}}>{tr("status.settingUpAccount")}</div>
            <div style={{fontSize:13,color:C.muted}}>{tr("status.settingUpSub")}</div>
          </div>
          <style>{`@keyframes nrspin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {view==="landing" && <Landing onStart={(mode)=>{setAuthInitialMode(mode==="signup"?"signup":"login");setView("auth");}} onStory={()=>{window.scrollTo(0,0);setView("story");}} onLegal={(v)=>{window.scrollTo(0,0);setView(v);}}/>}
      {view==="story"   && <OurStory onBack={()=>{window.scrollTo(0,0);setView("landing");}} onStart={(mode)=>{setAuthInitialMode(mode==="signup"?"signup":"login");setView("auth");}} onLegal={(v)=>{window.scrollTo(0,0);setView(v);}}/>}
      {(view==="privacy"||view==="appPrivacy"||view==="terms") && <LegalPage which={view} onBack={()=>{window.scrollTo(0,0);setView("landing");}} onNav={(v)=>{window.scrollTo(0,0);setView(v);}}/>}
      {view==="auth"    && <AuthScreen onAuth={handleAuth} onBackToHome={()=>setView("landing")} initialMode={authInitialMode}/>}
      {view==="recover" && <RecoverPassword onComplete={()=>{try{sessionStorage.removeItem("__netrev_recovery");}catch(_){}window.history.replaceState({},"","/");setView("auth");}}/>}

      {view==="app" && (
        <div style={{display:"flex",justifyContent:"center",padding:"24px 16px"}}>
          <div style={{width:390,background:"#fff",borderRadius:44,border:`1.5px solid ${C.border}`,overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,.14),0 4px 16px rgba(0,0,0,.08)",position:"relative"}}>
            <div style={{background:C.blue,padding:"15px 26px 8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{color:"rgba(255,255,255,.9)",fontSize:12,fontWeight:500}}>9:41</span>
              <div style={{width:86,height:22,background:"rgba(0,0,0,.25)",borderRadius:11}}/>
              <span style={{color:"rgba(255,255,255,.9)",fontSize:11}}>100%</span>
            </div>
            <div style={{background:C.blue,padding:"4px 18px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
              <div>
                <div style={{color:"rgba(255,255,255,.65)",fontSize:9,fontWeight:600,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>{tr("topbar.currentWorkspace")}</div>
                <div style={{color:"#fff",fontSize:15,fontWeight:600,letterSpacing:"-.2px"}}>{ws.name}</div>
              </div>
              <WsBadge ws={ws} onClick={()=>setShowPicker(true)}/>
            </div>
            <div style={{display:"flex",background:"#fff",borderBottom:`1px solid ${C.border}`,boxShadow:C.s1}}>
              {MOB_TABS.map(t => (
                <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"9px 0",border:"none",background:"transparent",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,borderBottom:`2.5px solid ${tab===t.id?C.blue:"transparent"}`,fontFamily:"inherit"}}>
                  <span style={{fontSize:15}}>{t.icon}</span>
                  <span style={{fontSize:9,fontWeight:500,color:tab===t.id?C.blue:C.muted}}>{tr("mobnav."+t.id)}</span>
                </button>
              ))}
            </div>
            <div style={{overflowY:"auto",maxHeight:540,padding:14,background:C.bg2}}>
              {tab==="dash"    && <MobDash ws={ws} cfg={cfg}/>}
              {tab==="items"   && <MobItems ws={ws} setWs={setWs} tiers={tiers} cfg={cfg}/>}
              {tab==="log"     && <MobLog ws={ws} cfg={cfg}/>}
              {tab==="account" && <MobAccount workspaces={workspaces} setWorkspaces={setWorkspaces}/>}
            </div>
            <div style={{background:"#fff",padding:"9px 0 20px",display:"flex",justifyContent:"center",borderTop:`1px solid ${C.border2}`}}>
              <div style={{width:110,height:4,background:C.border,borderRadius:2}}/>
            </div>
            {showPicker && <WsPicker workspaces={workspaces} current={wsId} onSelect={id=>{setWsId(id);setShowPicker(false);}}/>}
          </div>
        </div>
      )}

      {view==="admin" && (
        wsLoading ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(100vh - 60px)",flexDirection:"column",gap:12}}>
            <div style={{width:36,height:36,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.blue}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
            <p style={{fontSize:13,color:C.muted}}>{tr("status.loadingWorkspaces")}</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : postStripeSettling ? (
          // Returning from Stripe checkout — hold on a loading screen until the
          // account refresh + workspace creation/restore finishes, so the
          // "subscribe" paywall never flashes before the paid workspace appears.
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(100vh - 60px)",flexDirection:"column",gap:12}}>
            <div style={{width:36,height:36,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.blue}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
            <p style={{fontSize:13,color:C.muted}}>{tr("status.settingUpAccount")}</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : (accountStatus === "trial_expired" || accountStatus === "override_expired") && user?.plan !== "superadmin" ? (
          // Trial-end / free-access-end selective subscription screen.
          // Wait for workspaces to populate before showing the paywall, otherwise the user
          // sees an empty "Choose your workspaces" list with nothing to pick.
          // Override accounts may legitimately own ZERO workspaces (comped but never
          // created one) — for them, wait only until the FIRST load completes
          // (wsLoadedOnce), then an empty list is genuinely empty and the
          // paywall offers the plain-checkout path instead of spinning forever.
          Object.keys(workspaces || {}).length === 0
            && (accountStatus === "trial_expired" || !wsLoadedOnce) ? (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"calc(100vh - 60px)",flexDirection:"column",gap:12}}>
              <div style={{width:36,height:36,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.blue}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <p style={{fontSize:13,color:C.muted}}>{tr("status.loadingWorkspaces")}</p>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : (
            <TrialEndPaywall
            variant={accountStatus === "override_expired" ? "override" : "trial"}
            account={account}
            user={user}
            workspaces={workspaces}
            onSignOut={handleLogout}
            onCancelEverything={async () => {
              // Confirmation already handled by ConfirmDialog inside TrialEndPaywall
              try {
                // Best-effort: archive all the user's workspaces locally; the cleanup_expired_archives job handles the rest
                const ids = Object.keys(workspaces || {});
                if (ids.length > 0) {
                  await archiveWorkspaces(ids);
                }
              } catch (e) { console.warn("Cancel cleanup error:", e); }
              handleLogout();
            }}
          />
          )
        ) : (accountStatus === "past_due" || accountStatus === "archived" || accountStatus === "canceled_expired") && user?.plan !== "superadmin" ? (
          // Other paywall states (failed payment, archived, retention expired)
          <PaywallScreen
            account={account}
            status={accountStatus}
            user={user}
            onUpgrade={() => setUpgradeOpen(true)}
            onSignOut={handleLogout}
          />
        ) : (
          <>
            {/* Trial countdown banner — shown during active trial */}
            <TrialBanner
              account={account}
              onUpgrade={() => setUpgradeOpen(true)}
            />
            <AdminView cfg={cfg} setCfg={setCfg} tiers={tiers} setTiers={setTiers}
              workspaces={workspaces} setWorkspaces={setWorkspaces}
              user={user} onLogout={handleLogout}
              currency={currency} setCurrency={setCurrency}
              account={account} accountStatus={accountStatus}
              onUpgrade={() => setUpgradeOpen(true)}
              refreshAccount={refreshAccount}
              refreshWorkspaces={refreshWorkspaces}
              teamContext={teamContext}
              myAccounts={myAccounts}
              activeAccountId={activeAccountId}
              switchAccount={switchAccount}
              mobileNavOpen={mobileNavOpen}
              setMobileNavOpen={setMobileNavOpen}/>
          </>
        )
      )}

      {/* Upgrade modal — opens from banner, paywall, or settings */}
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        account={account}
        siteCount={Object.keys(workspaces).length || 1}
        refreshAccount={refreshAccount}
        refreshWorkspaces={refreshWorkspaces}
      />
    </div>
  );
}
